import { useState, useRef, useEffect } from "react";

const AGENT_NAMES   = ["Sarah Mitchell", "Alex Chen", "Jordan Davis"];
const FREE_LIMIT    = 10; // guest (not logged in) limit — logged-in free tier is 20 (server-side)
const SIGNUP_BONUS  = 5;

const uid  = () => crypto.randomUUID ? crypto.randomUUID().slice(0,8) : Math.random().toString(36).slice(2,9);
const now  = () => new Date().toLocaleDateString("en-US", { month:"short", day:"numeric", year:"numeric" });

// ─── API helper — all calls go to Vercel serverless functions ────────────────
const apiHeaders = () => ({
  "Content-Type": "application/json",
  ...(localStorage.getItem("lw_sid") ? { Authorization: `Bearer ${localStorage.getItem("lw_sid")}` } : {}),
});
const safeJson = async (r) => { try { return await r.json(); } catch { return { error: `Server error (${r.status})` }; } };
const api = {
  signup:        (name,email,password) => fetch("/api/auth/signup",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({name,email,password})}).then(safeJson),
  login:         (email,password)      => fetch("/api/auth/login",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({email,password})}).then(safeJson),
  logout:        ()                    => fetch("/api/auth/logout",{method:"POST",headers:apiHeaders()}),
  getUser:       ()                    => fetch("/api/auth/me",{headers:apiHeaders()}).then(r=>r.ok?r.json():null),
  forgotPassword:(email)               => fetch("/api/auth/forgot",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({email})}).then(safeJson),
  resetPassword: (token,password)      => fetch("/api/auth/reset",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({token,password})}).then(safeJson),
  getLeads:()                    => fetch("/api/leads",{headers:apiHeaders()}).then(r=>r.json()),
  addLeads:(leads)               => fetch("/api/leads",{method:"POST",headers:apiHeaders(),body:JSON.stringify({leads})}).then(r=>r.json()),
  updateLead:(id,field,value)    => fetch(`/api/leads/${id}`,{method:"PATCH",headers:apiHeaders(),body:JSON.stringify({field,value})}),
  deleteLead:(id)                => fetch(`/api/leads/${id}`,{method:"DELETE",headers:apiHeaders()}),
  bulkUpdateLeads:(ids,field,value)=> fetch("/api/leads",{method:"PATCH",headers:apiHeaders(),body:JSON.stringify({ids,field,value})}).then(r=>r.json()),
  bulkDeleteLeads:async (ids) => {
    const CHUNK = 100;
    for (let i = 0; i < ids.length; i += CHUNK) {
      const chunk = ids.slice(i, i + CHUNK);
      const result = await fetch("/api/leads", { method:"DELETE", headers:apiHeaders(), body:JSON.stringify({ ids: chunk }) }).then(r=>r.json());
      if (result?.error) throw new Error(result.error);
    }
    return { ok: true };
  },
  reEnrichLead:(id)              => fetch(`/api/leads/${id}`,{method:"POST",headers:apiHeaders()}).then(r=>r.json()),
  // Admin
  adminListClients:()            => fetch("/api/admin/list-clients",{headers:apiHeaders()}).then(r=>r.json()),
  adminCreateClient:(data)       => fetch("/api/admin/create-client",{method:"POST",headers:apiHeaders(),body:JSON.stringify(data)}).then(r=>r.json()),
  adminSetPlan:(clientId,plan)   => fetch("/api/admin/set-plan",{method:"POST",headers:apiHeaders(),body:JSON.stringify({clientId,plan})}).then(r=>r.json()),
  adminClientLeads:(clientId)    => fetch(`/api/admin/client-leads?clientId=${clientId}`,{headers:apiHeaders()}).then(r=>r.json()),
  adminResetPassword:(clientId)  => fetch("/api/admin/reset-client-password",{method:"POST",headers:apiHeaders(),body:JSON.stringify({clientId})}).then(r=>r.json()),
  adminGenerateCheckout:(clientId)=> fetch("/api/admin/generate-checkout",{method:"POST",headers:apiHeaders(),body:JSON.stringify({clientId})}).then(r=>r.json()),
  checkUsage:()                  => fetch("/api/usage/check",{headers:apiHeaders()}).then(r=>r.json()),
  recordUsage:(count)            => fetch("/api/usage/record",{method:"POST",headers:apiHeaders(),body:JSON.stringify({count})}),
  claimEmailBonus:(bonusEmail)   => fetch("/api/usage/email-bonus",{method:"POST",headers:apiHeaders(),body:JSON.stringify({bonusEmail})}),
  upgrade: (plan)                => fetch("/api/usage/upgrade",{method:"POST",headers:apiHeaders(),body:JSON.stringify({plan})}).then(safeJson),
  buyPack: (pack)                => fetch("/api/usage/buy-pack",{method:"POST",headers:apiHeaders(),body:JSON.stringify({pack})}).then(safeJson),
  billingPortal: ()              => fetch("/api/usage/portal",{method:"POST",headers:apiHeaders()}).then(safeJson),
  deleteAccount: ()              => fetch("/api/usage/delete-account",{method:"POST",headers:apiHeaders(),body:JSON.stringify({confirm:"DELETE MY ACCOUNT"})}).then(r=>r.json()),
  enrichSearch:(product,target,opts)=> fetch("/api/enrich/search",{method:"POST",headers:apiHeaders(),body:JSON.stringify({product,target,...(opts||{})})}).then(r=>r.json()),
  googleAuth:(credential)        => fetch("/api/auth/google",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({credential})}).then(r=>r.json()),
  // Outreach + Brainstorm
  generateEmail:(lead,product,tone) => fetch("/api/ai/outreach",{method:"POST",headers:apiHeaders(),body:JSON.stringify({lead,product,tone})}).then(r=>r.json()),
  brainstorm:(product)           => fetch("/api/ai/brainstorm",{method:"POST",headers:apiHeaders(),body:JSON.stringify({product})}).then(r=>r.json()),
  getIntentSignals:(company,domain)=> fetch("/api/ai/intent",{method:"POST",headers:apiHeaders(),body:JSON.stringify({company,domain})}).then(r=>r.json()),
  // Saved searches
  listSavedSearches:()           => fetch("/api/saved-searches/list",{headers:apiHeaders()}).then(r=>r.json()),
  saveSearch:(data)              => fetch("/api/saved-searches/save",{method:"POST",headers:apiHeaders(),body:JSON.stringify(data)}).then(r=>r.json()),
  deleteSavedSearch:(id)         => fetch("/api/saved-searches/delete",{method:"POST",headers:apiHeaders(),body:JSON.stringify({id})}).then(r=>r.json()),
  // Schedule API
  getSchedules:()                => fetch("/api/schedule/list",{headers:apiHeaders()}).then(r=>r.json()),
  createSchedule:(data)          => fetch("/api/schedule/create",{method:"POST",headers:apiHeaders(),body:JSON.stringify(data)}).then(r=>r.json()),
  toggleSchedule:(id)            => fetch("/api/schedule/toggle",{method:"POST",headers:apiHeaders(),body:JSON.stringify({id})}).then(r=>r.json()),
  deleteSchedule:(id)            => fetch("/api/schedule/delete",{method:"DELETE",headers:apiHeaders(),body:JSON.stringify({id})}).then(r=>r.json()),
  runSchedule:(id)               => fetch("/api/schedule/run",{method:"POST",headers:apiHeaders(),body:JSON.stringify({id})}).then(r=>r.json()),
  // API keys
  listApiKeys:()                 => fetch("/api/v1/keys/list",{headers:apiHeaders()}).then(r=>r.json()),
  createApiKey:(label)           => fetch("/api/v1/keys/create",{method:"POST",headers:apiHeaders(),body:JSON.stringify({label})}).then(r=>r.json()),
  revokeApiKey:(id)              => fetch("/api/v1/keys/revoke",{method:"POST",headers:apiHeaders(),body:JSON.stringify({id})}).then(r=>r.json()),
  // Webhooks
  listWebhooks:()                => fetch("/api/v1/webhooks.list",{headers:apiHeaders()}).then(r=>r.json()),
  createWebhook:(url,events)     => fetch("/api/v1/webhooks.create",{method:"POST",headers:apiHeaders(),body:JSON.stringify({url,events})}).then(r=>r.json()),
  deleteWebhook:(id)             => fetch("/api/v1/webhooks.delete",{method:"POST",headers:apiHeaders(),body:JSON.stringify({id})}).then(r=>r.json()),
  toggleWebhook:(id)             => fetch("/api/v1/webhooks.toggle",{method:"POST",headers:apiHeaders(),body:JSON.stringify({id})}).then(r=>r.json()),
  testWebhook:(id)               => fetch("/api/v1/webhooks.test",{method:"POST",headers:apiHeaders(),body:JSON.stringify({id})}).then(r=>r.json()),
  // Profile / settings
  updateProfile: (name)                   => fetch("/api/auth/update-profile",{method:"POST",headers:apiHeaders(),body:JSON.stringify({name})}).then(safeJson),
  changePassword: (currentPassword,newPassword) => fetch("/api/auth/change-password",{method:"POST",headers:apiHeaders(),body:JSON.stringify({currentPassword,newPassword})}).then(safeJson),
  listTeam:    ()        => fetch("/api/auth/list-team",{headers:apiHeaders()}).then(safeJson),
  inviteTeam:  (email)   => fetch("/api/auth/invite-team",{method:"POST",headers:apiHeaders(),body:JSON.stringify({email})}).then(safeJson),
  removeTeam:  (id)      => fetch("/api/auth/remove-team",{method:"POST",headers:apiHeaders(),body:JSON.stringify({id})}).then(safeJson),
};

const callClaude = async (messages, system, sessionId) => {
  try {
    const res = await fetch("/api/claude/chat", {
      method: "POST",
      headers: apiHeaders(),
      body: JSON.stringify({ messages, system, sessionId }),
    });
    const data = await res.json();
    if (data.error) console.error("Chat API error:", data.error, data.debug || "");
    return data.text || "";
  } catch (e) {
    console.error("Chat fetch error:", e);
    return "";
  }
};

const SOURCE_STYLE = { Inbound:"bg-teal-100 text-teal-700 border-teal-200", Outbound:"bg-emerald-100 text-emerald-700 border-emerald-200", Imported:"bg-violet-100 text-violet-700 border-violet-200" };
const STATUS_STYLE = {
  Hot:"bg-red-100 text-red-700 border-red-200", Warm:"bg-orange-100 text-orange-700 border-orange-200",
  Cold:"bg-blue-100 text-blue-700 border-blue-200", Qualified:"bg-green-100 text-green-700 border-green-200",
  Contacted:"bg-purple-100 text-purple-700 border-purple-200", New:"bg-gray-100 text-gray-600 border-gray-200",
  "Not a Fit":"bg-gray-100 text-gray-400 border-gray-200", "Closed Won":"bg-teal-100 text-teal-700 border-teal-200",
  "Closed Lost":"bg-red-50 text-red-400 border-red-100",
};
const LEAD_STATUSES = ["New","Contacted","Qualified","Hot","Warm","Cold","Not a Fit","Closed Won","Closed Lost"];
const LEAD_SCORES   = ["Hot","Warm","Cold","Qualified"];
const SORT_OPTIONS  = ["Newest","Oldest","Score","Company","Status"];
const SCORE_ORDER   = { Hot:0, Warm:1, Qualified:2, Cold:3, New:4, Contacted:5, "Not a Fit":6 };

// ════════════════════════════════════════════════════════════════════════════
// AUTH SCREEN
// ════════════════════════════════════════════════════════════════════════════
function AuthScreen({ onAuth, onDismiss, initialMode }) {
  const [mode, setMode]         = useState(initialMode || "login");
  const [name, setName]         = useState("");
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw]     = useState(false);
  const [err, setErr]           = useState("");
  const [loading, setLoading]   = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const googleBtnRef            = useRef(null);

  // Initialize Google Sign-In button
  useEffect(() => {
    if (mode !== "login" && mode !== "signup") return;
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId) return;

    let cancelled = false;
    let intervalId = null;

    const renderGoogleButton = () => {
      if (cancelled || !googleBtnRef.current || !window.google?.accounts?.id) return false;
      googleBtnRef.current.innerHTML = "";
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: async (response) => {
          setErr(""); setLoading(true);
          try {
            const result = await api.googleAuth(response.credential);
            setLoading(false);
            if (result.error) { setErr(result.error); return; }
            onAuth(result.sessionId, result.user, result.isSignup);
          } catch {
            setLoading(false);
            setErr("Google sign-in is unavailable right now. Please try again.");
          }
        },
      });
      window.google.accounts.id.renderButton(googleBtnRef.current, {
        theme: "outline", size: "large", width: 360, text: "continue_with", shape: "pill",
      });
      return true;
    };

    if (!renderGoogleButton()) {
      intervalId = window.setInterval(() => {
        if (renderGoogleButton() && intervalId) {
          window.clearInterval(intervalId);
          intervalId = null;
        }
      }, 250);
    }

    return () => {
      cancelled = true;
      if (intervalId) window.clearInterval(intervalId);
    };
  }, [onAuth, mode]);

  const submit = async () => {
    setErr(""); setLoading(true);
    try {
      const result = mode === "signup"
        ? await api.signup(name.trim(), email.trim().toLowerCase(), password)
        : await api.login(email.trim().toLowerCase(), password);
      setLoading(false);
      if (result.error) { setErr(result.error); return; }
      onAuth(result.sessionId, result.user, mode === "signup");
    } catch (e) {
      setLoading(false);
      setErr("Unable to connect. Please check your internet connection and try again.");
    }
  };

  const submitForgot = async () => {
    setErr(""); setLoading(true);
    const result = await api.forgotPassword(email.trim().toLowerCase());
    setLoading(false);
    if (result.error) { setErr(result.error); return; }
    setForgotSent(true);
  };

  const switchMode = (m) => { setMode(m); setErr(""); setForgotSent(false); };

  // ── Forgot password view ──────────────────────────────────────────────────
  if (mode === "forgot") return (
    <div className="min-h-full w-full bg-[#edf7ef] flex flex-col items-center justify-center p-4 py-8">
      {onDismiss && <button onClick={onDismiss} type="button" aria-label="Close" className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-white/90 text-sage-500 hover:text-sage-800 hover:bg-white shadow-sm border border-sage-200 transition-all z-20"><svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/></svg></button>}
      <div className="flex items-center gap-2 mb-8">
        <img src="/logo.png" alt="Virtuosoh" className="w-9 h-9 rounded-xl shadow-sm object-cover"/>
        <span className="font-display text-2xl font-semibold tracking-tight text-sage-950">Virtuosoh</span>
      </div>
      <div className="bg-white rounded-2xl shadow-xl shadow-sage-900/10 border border-sage-100 w-full max-w-md p-8">
        {forgotSent ? (
          <div className="text-center py-4">
            <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-sage-100 text-sage-700 flex items-center justify-center"><svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.7}><path strokeLinecap="round" strokeLinejoin="round" d="M3 8l9 6 9-6M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg></div>
            <h2 className="font-display text-xl font-semibold text-sage-950 mb-2">Check your inbox</h2>
            <p className="text-sm text-sage-900/60 mb-6">If an account exists for <strong>{email}</strong>, we've sent a password reset link. It expires in 1 hour.</p>
            <button onClick={() => switchMode("login")} className="text-sage-700 hover:underline text-sm font-medium">Back to log in</button>
          </div>
        ) : (
          <>
            <button onClick={() => switchMode("login")} className="flex items-center gap-1.5 text-xs text-sage-500 hover:text-sage-800 mb-5 transition-colors">
              <span>&larr;</span> Back to log in
            </button>
            <h2 className="font-display text-xl font-semibold text-sage-950 mb-1">Forgot your password?</h2>
            <p className="text-sm text-sage-900/60 mb-5">Enter your email and we'll send you a reset link.</p>
            <div className="mb-4">
              <label className="text-xs font-semibold text-sage-700 block mb-1">Email</label>
              <input type="email" autoFocus className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-sage-500 focus:ring-2 focus:ring-sage-200" placeholder="you@company.com" value={email} onChange={e=>setEmail(e.target.value)} onKeyDown={e=>e.key==="Enter"&&submitForgot()}/>
            </div>
            {err && <div className="bg-red-50 border border-red-200 text-red-600 text-xs rounded-lg px-3 py-2 mb-4">{err}</div>}
            <button onClick={submitForgot} disabled={loading || !email.trim()} className="w-full bg-sage-700 hover:bg-sage-800 text-white font-semibold py-3 rounded-xl transition-all disabled:opacity-50 text-sm shadow-md">
              {loading ? "Sending…" : "Send reset link →"}
            </button>
          </>
        )}
      </div>
    </div>
  );

  if (loading) return (
    <div className="min-h-full w-full bg-[#edf7ef] flex flex-col items-center justify-center p-4 py-8">
      <div className="flex flex-col items-center gap-4">
        <div className="w-10 h-10 rounded-full border-2 border-sage-200 border-t-sage-600 animate-spin"/>
        <p className="text-sm text-sage-600 font-medium">Signing you in…</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-full w-full bg-[#edf7ef] flex flex-col items-center justify-center p-4 py-8 relative overflow-hidden">
      <div className="absolute -top-24 right-[-4rem] w-80 h-80 rounded-full bg-sage-200/30 blur-3xl pointer-events-none" />
      {onDismiss && (
        <button onClick={onDismiss} type="button" aria-label="Close" className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-white/90 text-sage-500 hover:text-sage-800 hover:bg-white shadow-sm border border-sage-200 transition-all z-20"><svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/></svg></button>
      )}
      <div className="relative z-10 w-full flex flex-col items-center">
        <div className="inline-flex items-center gap-3 rounded-full border border-white/80 bg-white/80 px-4 py-2 shadow-sm backdrop-blur mb-5">
          <img src="/logo.png" alt="Virtuosoh" className="w-9 h-9 rounded-xl shadow-lg object-cover"/>
          <div className="text-left">
            <div className="font-display text-base font-semibold tracking-tight text-sage-950">Virtuosoh</div>
            <div className="text-[11px] text-sage-600">Simplify your sales funnel</div>
          </div>
        </div>
        <div className="text-center max-w-md mb-8 px-2">
          <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-[-0.02em] text-sage-950 mb-2">{mode==="signup" ? "Start building a better pipeline" : "Welcome back"}</h1>
          <p className="text-sm text-sage-900/70">We do the prospecting and prep &mdash; you focus on closing.</p>
          <div className="mt-4 flex flex-wrap justify-center gap-2 text-xs text-sage-700">
            <span className="rounded-full border border-sage-200 bg-white/80 px-3 py-1">Outbound prospecting</span>
            <span className="rounded-full border border-sage-200 bg-white/80 px-3 py-1">Inbound chat</span>
            <span className="rounded-full border border-sage-200 bg-white/80 px-3 py-1">One lead board</span>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-xl shadow-sage-900/10 border border-sage-100 w-full max-w-md p-8">
        <div className="flex bg-sage-50 rounded-xl p-1 mb-6">
          {["login","signup"].map(m => (
            <button key={m} onClick={()=>switchMode(m)} className={`flex-1 py-2 text-sm font-semibold rounded-lg transition-all capitalize focus:outline-none focus-visible:ring-2 focus-visible:ring-sage-300 ${mode===m?"bg-white text-sage-950 shadow-sm":"text-sage-500 hover:text-sage-800"}`}>{m==="login"?"Log In":"Create Account"}</button>
          ))}
        </div>

        {mode==="signup" && (
          <div className="mb-4">
            <label className="text-xs font-semibold text-sage-700 block mb-1">Full Name</label>
            <input className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-sage-500 focus:ring-2 focus:ring-sage-200" placeholder="Jane Smith" value={name} onChange={e=>setName(e.target.value)}/>
          </div>
        )}
        <div className="mb-4">
          <label className="text-xs font-semibold text-sage-700 block mb-1">Email</label>
          <input type="email" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-sage-500 focus:ring-2 focus:ring-sage-200" placeholder="you@company.com" value={email} onChange={e=>setEmail(e.target.value)} onKeyDown={e=>e.key==="Enter"&&submit()}/>
        </div>
        <div className="mb-1">
          <label className="text-xs font-semibold text-sage-700 block mb-1">Password</label>
          <div className="relative">
            <input type={showPw?"text":"password"} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 pr-10 text-sm outline-none focus:border-sage-500 focus:ring-2 focus:ring-sage-200" placeholder="••••••••" value={password} onChange={e=>setPassword(e.target.value)} onKeyDown={e=>e.key==="Enter"&&submit()}/>
            <button type="button" onClick={()=>setShowPw(p=>!p)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors select-none" tabIndex={-1} aria-label={showPw?"Hide password":"Show password"}>
              {showPw ? (
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                  <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                  <line x1="1" y1="1" x2="23" y2="23"/>
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                  <circle cx="12" cy="12" r="3"/>
                </svg>
              )}
            </button>
          </div>
        </div>

        {mode==="login" && (
          <div className="flex justify-end mb-4">
            <button onClick={()=>switchMode("forgot")} className="text-xs text-sage-500 hover:underline">Forgot password?</button>
          </div>
        )}
        {mode==="signup" && <div className="mb-4"/>}

        {err && <div className="bg-red-50 border border-red-200 text-red-600 text-xs rounded-lg px-3 py-2 mb-4">{err}</div>}

        {mode==="signup" && (
          <div className="bg-sage-50 border border-sage-100 rounded-xl px-4 py-3 mb-5 flex items-start gap-2.5">
            <svg className="w-5 h-5 text-sage-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.7}><path strokeLinecap="round" strokeLinejoin="round" d="M20 12v9H4v-9M2 7h20v5H2zM12 22V7M12 7H7.5a2.5 2.5 0 010-5C11 2 12 7 12 7zM12 7h4.5a2.5 2.5 0 000-5C13 2 12 7 12 7z"/></svg>
            <div>
              <div className="text-xs font-semibold text-sage-800">Create an account and get {SIGNUP_BONUS} bonus contacts</div>
              <div className="text-xs text-sage-600 mt-0.5">Plus your lead board saves automatically across sessions.</div>
            </div>
          </div>
        )}

        <button onClick={submit} disabled={loading} className="w-full bg-sage-700 hover:bg-sage-800 text-white font-semibold py-3 rounded-xl transition-all disabled:opacity-50 text-sm shadow-md">
          {mode==="signup" ? "Create Account & Get 5 Bonus Contacts →" : "Log In →"}
        </button>

        {/* Google Sign-In */}
        <div className="flex items-center gap-3 my-4">
          <div className="flex-1 h-px bg-gray-200"></div>
          <span className="text-xs text-gray-400">or</span>
          <div className="flex-1 h-px bg-gray-200"></div>
        </div>
        <div ref={googleBtnRef} className="flex justify-center"></div>

        <p className="text-center text-[11px] leading-relaxed text-gray-400 mt-4 px-2">
          By continuing, you agree to our{" "}
          <a href="/?legal=terms" target="_blank" rel="noopener noreferrer" className="text-sage-600 hover:underline">Terms of Service</a>{" "}and{" "}
          <a href="/?legal=privacy" target="_blank" rel="noopener noreferrer" className="text-sage-600 hover:underline">Privacy Policy</a>.
        </p>

        <p className="text-center text-xs text-gray-400 mt-4">
          {mode==="login" ? "Don't have an account? " : "Already have an account? "}
          <button onClick={()=>switchMode(mode==="login"?"signup":"login")} className="text-sage-500 hover:underline font-medium">{mode==="login"?"Sign up free":"Log in"}</button>
        </p>
      </div>

      <p className="text-xs text-sage-600/80 mt-6 text-center max-w-sm">
        Inbound, outbound, and every lead &mdash; together on one board.
      </p>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// BONUS TOAST
// ════════════════════════════════════════════════════════════════════════════
function BonusToast({ name, onDone }) {
  useEffect(() => { const t = setTimeout(onDone, 4000); return () => clearTimeout(t); }, [onDone]);
  return (
    <div className="fixed top-4 right-4 z-50 bg-white border border-sage-200 shadow-xl rounded-2xl px-5 py-4 flex items-center gap-3 animate-pulse">
      <span className="text-2xl">🎉</span>
      <div>
        <div className="font-semibold text-sage-950 text-sm">Welcome, {name}!</div>
        <div className="text-xs text-sage-600 mt-0.5">+{SIGNUP_BONUS} bonus contacts added to your account.</div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// EMAIL GATE
// ════════════════════════════════════════════════════════════════════════════
function EmailGate({ userEmail, onUnlocked }) {
  const [email, setEmail]     = useState(userEmail||"");
  const [loading, setLoading] = useState(false);
  const [err, setErr]         = useState("");
  const submit = async () => {
    if (!email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) { setErr("Enter a valid email."); return; }
    setLoading(true);
    await new Promise(r=>setTimeout(r,700));
    setLoading(false); onUnlocked(email);
  };
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center">
        <div className="w-14 h-14 bg-sage-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <svg className="w-7 h-7 text-sage-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/></svg>
        </div>
        <h2 className="font-display text-xl font-semibold text-sage-950 mb-1">You've used all your free contacts</h2>
        <p className="text-gray-500 text-sm mb-6">Confirm your email to unlock <span className="font-semibold text-sage-600">5 more free contacts</span>.</p>
        <input type="email" placeholder="you@company.com" value={email} onChange={e=>{setEmail(e.target.value);setErr("");}} onKeyDown={e=>e.key==="Enter"&&submit()} className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-sage-500 focus:ring-2 focus:ring-sage-200 mb-2"/>
        {err && <p className="text-red-500 text-xs mb-2">{err}</p>}
        <button onClick={submit} disabled={loading} className="w-full bg-sage-700 hover:bg-sage-800 text-white font-semibold py-3 rounded-xl text-sm disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-sage-300">
          {loading?"Unlocking...":"Unlock 5 More Contacts →"}
        </button>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// PAYWALL
// ════════════════════════════════════════════════════════════════════════════
// BUY CONTACTS MODAL
// ════════════════════════════════════════════════════════════════════════════
const CONTACT_PACKS_UI = [
  { key:"small",  label:"Starter Pack", contacts:25,  price:"$4.99",  perContact:"$0.20 each", popular:false },
  { key:"medium", label:"Growth Pack",  contacts:100, price:"$14.99", perContact:"$0.15 each", popular:true  },
  { key:"large",  label:"Pro Pack",     contacts:250, price:"$29.99", perContact:"$0.12 each", popular:false },
];
function BuyContactsModal({ onClose }) {
  const [loading, setLoading] = useState(null);
  const [err, setErr] = useState("");
  const checkout = async (key) => {
    setLoading(key); setErr("");
    try {
      const result = await api.buyPack(key);
      if (result.error) { setErr(result.error); setLoading(null); return; }
      if (result.url) { window.location.href = result.url; }
    } catch {
      setErr("Unable to start checkout. Please try again.");
      setLoading(null);
    }
  };
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-7 relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/></svg>
        </button>
        <div className="text-center mb-6">
          <div className="w-12 h-12 bg-gradient-to-br from-sage-600 to-sage-700 rounded-xl flex items-center justify-center mx-auto mb-3">
            <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4"/></svg>
          </div>
          <h2 className="font-display text-xl font-semibold text-sage-950 mb-1">Buy extra contacts</h2>
          <p className="text-gray-500 text-sm">One-time purchase. Contacts never expire and stack with your plan.</p>
        </div>
        <div className="grid grid-cols-3 gap-3 mb-5">
          {CONTACT_PACKS_UI.map(p => (
            <div key={p.key} className={`border-2 rounded-xl p-4 relative text-center flex flex-col ${p.popular ? "border-sage-500 shadow-md" : "border-gray-200"}`}>
              {p.popular && <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-sage-600 text-white text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap">Best value</span>}
              <div className="font-bold text-gray-900 text-sm mb-2">{p.label}</div>
              <div className="text-3xl font-extrabold text-gray-900 leading-none mb-0.5">{p.contacts}</div>
              <div className="text-xs text-gray-400 mb-3">contacts</div>
              <div className="text-lg font-bold text-sage-600 mb-0.5">{p.price}</div>
              <div className="text-xs text-gray-400 mb-4">{p.perContact}</div>
              <button onClick={() => checkout(p.key)} disabled={!!loading} className={`mt-auto w-full py-2 rounded-lg text-xs font-semibold transition-colors disabled:opacity-50 ${p.popular ? "bg-sage-700 hover:bg-sage-800 text-white" : "bg-gray-100 hover:bg-gray-200 text-gray-800"}`}>
                {loading === p.key ? "Redirecting..." : `Buy ${p.contacts}`}
              </button>
            </div>
          ))}
        </div>
        {err && <p className="text-red-600 text-sm text-center mb-3">{err}</p>}
        <p className="text-xs text-gray-400 text-center">Secured by Stripe · No expiry · Stacks with your monthly plan</p>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
const PLANS = [
  { key:"starter", label:"Starter", price:"$9.99", contacts:"50 enriched contacts/mo", features:["Verified emails & phone numbers","LinkedIn profile links","AI waterfall enrichment","Lead board & tracking","Email support"], pop:false },
  { key:"growth",  label:"Growth",  price:"$29.99", contacts:"500 enriched contacts/mo", features:["Everything in Starter","Google Search enrichment","Public records phone lookup","CSV export","Priority support"], pop:true },
  { key:"scale",   label:"Scale",   price:"$59.99", contacts:"2,000 enriched contacts/mo", features:["Everything in Growth","Team seats (coming soon)","Custom AI prompts","Dedicated onboarding"], pop:false },
];
function Paywall({ userEmail, onPaid, onBuyPacks, onClose }) {
  const [loading, setLoading] = useState(null);
  const [err, setErr] = useState("");
  const checkout = async (key) => {
    setLoading(key); setErr("");
    try {
      const result = await api.upgrade(key);
      if (result.error) { setErr(result.error); setLoading(null); return; }
      if (result.url) { window.location.href = result.url; return; }
      setLoading(null); onPaid();
    } catch (e) {
      setErr("Unable to start checkout. Please try again.");
      setLoading(null);
    }
  };
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-8 relative">
        {onClose && (
          <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/></svg>
          </button>
        )}
        <div className="text-center mb-8">
          <div className="w-14 h-14 bg-gradient-to-br from-sage-600 to-sage-700 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z"/></svg>
          </div>
          <h2 className="font-display text-2xl font-semibold text-sage-950 mb-1">Unlock enriched contact data</h2>
          <p className="text-gray-500 text-sm">Get verified emails, phone numbers, and LinkedIn profiles for every lead.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {PLANS.map(p=>(
            <div key={p.key} className={`border-2 rounded-xl p-5 relative ${p.pop?"border-sage-500 shadow-md":"border-gray-200"}`}>
              {p.pop&&<span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-sage-600 text-white text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap text-center">Most Popular</span>}
              <div className="font-bold text-gray-900 mb-1">{p.label}</div>
              <div className="text-3xl font-extrabold text-gray-900 mb-0.5">{p.price}<span className="text-sm font-normal text-gray-400">/mo</span></div>
              <div className="text-xs text-sage-600 font-semibold mb-3">{p.contacts}</div>
              <ul className="space-y-1.5 mb-5">{p.features.map(f=><li key={f} className="flex items-start gap-1.5 text-xs text-gray-600"><svg className="w-3.5 h-3.5 text-green-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7"/></svg>{f}</li>)}</ul>
              <button onClick={()=>checkout(p.key)} disabled={!!loading} className={`w-full py-2.5 rounded-xl text-sm font-semibold transition-colors disabled:opacity-50 ${p.pop?"bg-sage-700 hover:bg-sage-800 text-white":"bg-gray-100 hover:bg-gray-200 text-gray-800"}`}>
                {loading===p.key?"Redirecting...":"Get "+p.label}
              </button>
            </div>
          ))}
        </div>
        {err && <p className="text-red-600 text-sm text-center mb-3">{err}</p>}
        <div className="flex items-center justify-center gap-6 text-xs text-gray-400 mb-4">
          <span>Secured by Stripe</span><span>Cancel anytime</span><span>7-day money-back</span>
        </div>
        {onBuyPacks && (
          <div className="border-t border-gray-100 pt-4 text-center">
            <p className="text-xs text-gray-400 mb-1">Not ready to subscribe?</p>
            <button onClick={onBuyPacks} className="text-sm text-sage-600 hover:text-sage-700 font-semibold transition-colors">
              Buy a one-time contact pack instead →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// DAILY BRIEFING MODAL
// ════════════════════════════════════════════════════════════════════════════
function DailyBriefing({ leads, user, onClose, onGoToLeads }) {
  const today = new Date().toISOString().split("T")[0];
  const overdue = leads.filter(l => l.followUpDate && l.followUpDate < today);
  const dueToday = leads.filter(l => l.followUpDate === today);
  const hotUncontacted = leads.filter(l => l.score === "Hot" && l.status === "New");
  const recentNew = leads.filter(l => l.status === "New").slice(0, 5);
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  const firstName = user?.name?.split(" ")[0] || "there";

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="bg-gradient-to-br from-sage-600 to-emerald-600 px-6 py-5">
          <div className="text-white/70 text-sm mb-1">{new Date().toLocaleDateString("en-US",{weekday:"long",month:"long",day:"numeric"})}</div>
          <h2 className="font-display text-white font-semibold text-xl">{greeting}, {firstName}</h2>
          <p className="text-white/80 text-sm mt-1">Here's your pipeline at a glance.</p>
        </div>
        <div className="p-5 space-y-3">
          {overdue.length > 0 && (
            <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl p-3.5 cursor-pointer hover:bg-red-100 transition-colors" onClick={()=>{onGoToLeads();onClose();}}>
              <svg className="w-5 h-5 text-red-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/></svg>
              <div><div className="font-semibold text-red-800 text-sm">{overdue.length} overdue follow-up{overdue.length>1?"s":""}</div><div className="text-xs text-red-600">{overdue.map(l=>l.name).join(", ")}</div></div>
              <span className="ml-auto text-red-400 text-sm">→</span>
            </div>
          )}
          {dueToday.length > 0 && (
            <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl p-3.5 cursor-pointer hover:bg-amber-100 transition-colors" onClick={()=>{onGoToLeads();onClose();}}>
              <svg className="w-5 h-5 text-amber-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.4-1.4A2 2 0 0118 14.2V11a6 6 0 00-4-5.7V5a2 2 0 10-4 0v.3A6 6 0 006 11v3.2a2 2 0 01-.6 1.4L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"/></svg>
              <div><div className="font-semibold text-amber-800 text-sm">{dueToday.length} follow-up{dueToday.length>1?"s":""} due today</div><div className="text-xs text-amber-600">{dueToday.map(l=>l.name).join(", ")}</div></div>
              <span className="ml-auto text-amber-400 text-sm">→</span>
            </div>
          )}
          {hotUncontacted.length > 0 && (
            <div className="flex items-center gap-3 bg-orange-50 border border-orange-200 rounded-xl p-3.5 cursor-pointer hover:bg-orange-100 transition-colors" onClick={()=>{onGoToLeads();onClose();}}>
              <svg className="w-5 h-5 text-orange-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M12 2c1 3 4 4.5 4 8a4 4 0 01-8 0c0-1 .5-2 1-2.5C8 9 7 11 7 13a5 5 0 0010 0c0-4-3-8-5-11z"/></svg>
              <div><div className="font-semibold text-orange-800 text-sm">{hotUncontacted.length} hot lead{hotUncontacted.length>1?"s":""} not yet contacted</div><div className="text-xs text-orange-600">{hotUncontacted.map(l=>l.name||l.company).join(", ")}</div></div>
              <span className="ml-auto text-orange-400 text-sm">→</span>
            </div>
          )}
          {recentNew.length > 0 && overdue.length === 0 && dueToday.length === 0 && (
            <div className="flex items-center gap-3 bg-sage-50 border border-sage-200 rounded-xl p-3.5">
              <svg className="w-5 h-5 text-sage-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/></svg>
              <div><div className="font-semibold text-sage-800 text-sm">{leads.filter(l=>l.status==="New").length} leads waiting to be contacted</div><div className="text-xs text-sage-600">Start with your hottest prospects first</div></div>
            </div>
          )}
          {overdue.length === 0 && dueToday.length === 0 && hotUncontacted.length === 0 && (
            <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl p-3.5">
              <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
              <div><div className="font-semibold text-green-800 text-sm">You're all caught up!</div><div className="text-xs text-green-600">No follow-ups overdue. Time to find new leads.</div></div>
            </div>
          )}
          <button onClick={()=>{onGoToLeads();onClose();}} className="w-full bg-sage-700 hover:bg-sage-800 text-white font-semibold py-3 rounded-xl text-sm transition-colors mt-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-sage-300">
            Go to my pipeline →
          </button>
          <button onClick={onClose} className="w-full text-gray-400 hover:text-gray-600 text-sm py-1 transition-colors">Dismiss</button>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// BRAINSTORM COMPONENT
// ════════════════════════════════════════════════════════════════════════════
function BrainstormPanel({ user, onStartSearch, onNeedsAuth }) {
  const [product,setProduct]=useState("");
  const [loading,setLoading]=useState(false);
  const [result,setResult]=useState(null);
  const [err,setErr]=useState("");

  const run = async () => {
    if (!user) { onNeedsAuth(); return; }
    if (!product.trim()) return;
    setLoading(true); setResult(null); setErr("");
    try {
      const r = await api.brainstorm(product.trim());
      if (r.error) { setErr(r.error); return; }
      setResult(r);
    } catch {
      setErr("Could not generate recommendations. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const SCORE_COLOR = { high:"bg-green-100 text-green-700 border-green-200", medium:"bg-amber-100 text-amber-700 border-amber-200", low:"bg-gray-100 text-gray-500 border-gray-200" };

  return (
    <div className="bg-gradient-to-br from-sage-50 to-emerald-50 border border-sage-200 rounded-2xl p-5">
      <div className="mb-1">
        <span className="font-bold text-gray-900 text-sm">Not sure who to target?</span>
      </div>
      <p className="text-xs text-gray-500 mb-3">Describe what you sell — AI will recommend your best-fit buyer segments, titles, and outreach angles.</p>
      <div className="flex gap-2">
        <textarea value={product} onChange={e=>setProduct(e.target.value)} rows={2} placeholder="e.g. A project management tool that helps engineering teams ship faster..." className="flex-1 border border-sage-200 bg-white rounded-xl px-3 py-2.5 text-sm outline-none focus:border-sage-500 focus:ring-2 focus:ring-sage-200 resize-none placeholder:text-gray-300"/>
        <button onClick={run} disabled={loading||!product.trim()} className="bg-sage-700 hover:bg-sage-800 text-white font-semibold px-4 rounded-xl text-sm transition-colors disabled:opacity-40 flex-shrink-0">
          {loading?<span className="animate-spin inline-block">↻</span>:"→"}
        </button>
      </div>
      {err&&<p className="text-red-500 text-xs mt-2">{err}</p>}
      {result && (
        <div className="mt-4 space-y-3">
          <p className="text-xs text-gray-600 italic">{result.summary}</p>
          {result.segments?.map((seg,i)=>(
            <div key={i} className="bg-white border border-gray-200 rounded-xl p-4 space-y-2.5">
              <div className="flex items-center justify-between gap-2">
                <span className="font-semibold text-gray-900 text-sm">{seg.segment}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full border font-semibold ${SCORE_COLOR[seg.score]||SCORE_COLOR.medium}`}>{seg.score} fit</span>
              </div>
              <p className="text-xs text-gray-600">{seg.why}</p>
              <div className="flex flex-wrap gap-1">
                {seg.titles?.map(t=><span key={t} className="text-xs bg-sage-50 text-sage-700 border border-sage-200 px-2 py-0.5 rounded-full">{t}</span>)}
              </div>
              <div className="flex flex-wrap gap-1">
                {seg.industries?.map(ind=><span key={ind} className="text-xs bg-gray-100 text-gray-600 border border-gray-200 px-2 py-0.5 rounded-full">{ind}</span>)}
              </div>
              <div className="bg-blue-50 border border-blue-100 rounded-lg p-2.5 text-xs text-blue-700"><strong>Angle:</strong> {seg.angle}</div>
              <button onClick={()=>onStartSearch(seg, product)} className="w-full text-xs bg-sage-700 hover:bg-sage-800 text-white font-semibold py-2 rounded-lg transition-colors">
                Find {seg.segment} leads →
              </button>
            </div>
          ))}
          {result.quickWins?.length > 0 && (
            <div className="bg-white border border-amber-200 rounded-xl p-4">
              <div className="text-xs font-semibold text-amber-800 mb-2 flex items-center gap-1.5"><svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z"/></svg>Quick wins</div>
              <ul className="space-y-1">{result.quickWins.map((tip,i)=><li key={i} className="text-xs text-gray-600 flex gap-2"><span className="text-amber-400 flex-shrink-0">→</span>{tip}</li>)}</ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// LEAD DRAWER
// ════════════════════════════════════════════════════════════════════════════
function LeadDrawer({ lead, onClose, onUpdate, onReEnrich, productContext }) {
  const [ef,setEf]=useState(null); const [ev,setEv]=useState("");
  const [emailDraft,setEmailDraft]=useState(null); const [genLoading,setGenLoading]=useState(false);
  const [genTone,setGenTone]=useState("professional"); const [copied,setCopied]=useState(false);
  const [signals,setSignals]=useState(null); const [sigLoading,setSigLoading]=useState(false);
  const [reEnriching,setReEnriching]=useState(false); const [reEnrichDone,setReEnrichDone]=useState(false);
  const edit=(f,v)=>{setEf(f);setEv(v||"");};
  const save=()=>{onUpdate(lead.id,ef,ev);setEf(null);};

  const handleReEnrich = async () => {
    setReEnriching(true); setReEnrichDone(false);
    const result = await api.reEnrichLead(lead.id);
    setReEnriching(false);
    if (result.ok) { setReEnrichDone(true); if (onReEnrich) onReEnrich(lead.id, result); }
  };

  const generateEmail = async () => {
    setGenLoading(true); setEmailDraft(null);
    const r = await api.generateEmail(lead, productContext, genTone);
    setGenLoading(false);
    if (r.subject) setEmailDraft(r);
  };

  const copyEmail = () => {
    if (!emailDraft) return;
    navigator.clipboard.writeText(`Subject: ${emailDraft.subject}\n\n${emailDraft.body}`);
    setCopied(true); setTimeout(()=>setCopied(false), 2000);
  };

  const today = new Date().toISOString().split("T")[0];
  const isOverdue = lead.followUpDate && lead.followUpDate < today;
  const isDueToday = lead.followUpDate === today;
  const Field=({label,field,mono=false,wide=false,conf=null})=>(
    <div className={wide?"col-span-2":""}>
      <div className="text-xs text-gray-400 mb-0.5">{label}</div>
      {ef===field?<input autoFocus className="w-full border border-gray-300 rounded px-2 py-1 text-sm" value={ev} onChange={e=>setEv(e.target.value)} onBlur={save} onKeyDown={e=>e.key==="Enter"&&save()}/>
        :<div onClick={()=>edit(field,lead[field])} className={`text-sm cursor-pointer hover:text-gray-900 min-h-[20px] flex items-center gap-1.5 ${mono?"font-mono":""} ${lead[field]?"text-gray-800":"text-gray-300"}`}>
          <span>{lead[field]||"Click to add"}</span>
          {conf&&lead[field]&&<ConfBadge level={conf}/>}
        </div>}
    </div>
  );
  return (
    <div className="fixed inset-0 bg-black/30 z-50 flex justify-end" onClick={onClose}>
      <div className="w-full max-w-md bg-white h-full overflow-y-auto shadow-2xl" onClick={e=>e.stopPropagation()}>
        <div className="px-5 py-4 flex items-start justify-between bg-white border-b border-gray-200">
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <span className="text-gray-900 font-bold text-xl">{lead.name||"Unknown"}</span>
              <span className={`text-xs px-2 py-0.5 rounded-full border font-semibold ${lead.source==="Inbound"?"bg-teal-50 text-teal-700 border-teal-200":"bg-emerald-50 text-emerald-700 border-emerald-200"}`}>{lead.source}</span>
            </div>
            <div className="text-gray-500 text-sm">{lead.title}{lead.company&&` · ${lead.company}`}</div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-xl mt-1">✕</button>
        </div>
        <div className="p-5 space-y-5">
          <div className="flex gap-3 flex-wrap">
            <div><div className="text-xs text-gray-400 mb-1">Score</div>
              {ef==="score"?<select autoFocus className="border border-gray-300 rounded text-sm px-2 py-1" value={ev} onChange={e=>setEv(e.target.value)} onBlur={save}>{LEAD_SCORES.map(s=><option key={s}>{s}</option>)}</select>
                :<span onClick={()=>edit("score",lead.score)} title="Click to change" className={`cursor-pointer inline-block text-xs border px-3 py-1 rounded-full font-semibold ${STATUS_STYLE[lead.score]||STATUS_STYLE.Cold}`}>{lead.score||"—"}</span>}
            </div>
            <div><div className="text-xs text-gray-400 mb-1">Status</div>
              {ef==="status"?<select autoFocus className="border border-gray-300 rounded text-sm px-2 py-1" value={ev} onChange={e=>setEv(e.target.value)} onBlur={save}>{LEAD_STATUSES.map(s=><option key={s}>{s}</option>)}</select>
                :<span onClick={()=>edit("status",lead.status)} className={`cursor-pointer inline-block text-xs border px-3 py-1 rounded-full font-semibold ${STATUS_STYLE[lead.status]||STATUS_STYLE.New}`}>{lead.status||"New"}</span>}
            </div>
            <div className="ml-auto text-right"><div className="text-xs text-gray-400 mb-1">Added</div><div className="text-xs text-gray-600">{lead.date}</div></div>
          </div>

          {/* Follow-up date */}
          <div className={`rounded-xl p-3 border flex items-center justify-between gap-3 ${isOverdue?"bg-red-50 border-red-200":isDueToday?"bg-amber-50 border-amber-200":"bg-gray-50 border-gray-200"}`}>
            <div>
              <div className="text-xs font-semibold text-gray-700 flex items-center gap-1.5">
                Follow-up reminder
                {isOverdue&&<span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full font-semibold">Overdue</span>}
                {isDueToday&&<span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-semibold">Due today</span>}
              </div>
              <div className="text-xs text-gray-500 mt-0.5">{lead.followUpDate ? new Date(lead.followUpDate+"T12:00:00").toLocaleDateString("en-US",{weekday:"short",month:"short",day:"numeric"}) : "No reminder set"}</div>
            </div>
            <div className="flex items-center gap-2">
              {ef==="followUpDate"
                ? <input autoFocus type="date" className="border border-gray-300 rounded px-2 py-1 text-sm" value={ev} onChange={e=>setEv(e.target.value)} onBlur={save} onKeyDown={e=>e.key==="Enter"&&save()}/>
                : <button onClick={()=>edit("followUpDate", lead.followUpDate||"")} className="text-xs bg-white border border-gray-200 hover:border-gray-400 text-gray-600 px-3 py-1.5 rounded-lg transition-colors">{lead.followUpDate?"Change":"Set date"}</button>}
              {lead.followUpDate&&ef!=="followUpDate"&&<button onClick={()=>{onUpdate(lead.id,"followUpDate","");}} className="text-xs text-gray-400 hover:text-red-500 transition-colors">✕</button>}
            </div>
          </div>

          {/* Re-enrich banner: show when contact data is missing */}
          {(!lead.email || !lead.phone || !lead.linkedinUrl) && onReEnrich && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 flex items-center justify-between gap-3">
              <div>
                <div className="text-xs font-semibold text-amber-800">Missing contact data</div>
                <div className="text-xs text-amber-600 mt-0.5">
                  {[!lead.email&&"email", !lead.phone&&"phone", !lead.linkedinUrl&&"LinkedIn"].filter(Boolean).join(", ")} not found
                </div>
              </div>
              <button onClick={handleReEnrich} disabled={reEnriching}
                className="text-xs font-semibold bg-amber-600 hover:bg-amber-700 text-white px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1.5 whitespace-nowrap">
                {reEnriching ? <><span className="animate-spin">↻</span> Searching...</> : reEnrichDone ? "✓ Updated" : "🔍 Re-enrich"}
              </button>
            </div>
          )}

          <div className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest">Contact Info</div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Full Name" field="name"/><Field label="Job Title" field="title"/>
            <Field label="Company" field="company"/><Field label="Company Size" field="companySize"/>
            <Field label="Industry" field="industry"/><Field label="Location" field="location"/>
            <Field label="Email" field="email" mono conf={lead.confidenceEmail}/><Field label="Phone" field="phone" mono conf={lead.confidencePhone}/>
          </div>
          {lead.linkedinUrl&&<div><div className="text-xs text-gray-400 mb-1">LinkedIn</div><a href={lead.linkedinUrl} target="_blank" rel="noreferrer" className="text-sm text-blue-600 hover:underline flex items-center gap-1"><svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>{lead.linkedinUrl.replace("https://linkedin.com/in/","")}</a></div>}
          {(lead.fitReason||lead.scoreReason)&&<div><div className="text-xs text-gray-400 mb-1">Why a fit</div><div className="text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-lg p-3">{lead.fitReason||lead.scoreReason}</div></div>}
          {lead.interest&&<div><div className="text-xs text-gray-400 mb-1">Expressed Interest</div><div className="text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-lg p-3">{lead.interest}</div></div>}

          {/* AI Email Generator */}
          <div className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest">Outreach</div>
          <div className="border border-gray-200 rounded-xl bg-gray-50/50 p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs font-semibold text-gray-700">Write cold email</div>
              <div className="flex gap-1">
                {["professional","friendly","direct"].map(t=>(
                  <button key={t} onClick={()=>setGenTone(t)}
                    className={`text-xs px-2.5 py-1 rounded-lg border capitalize transition-all ${genTone===t?"bg-gray-900 text-white border-gray-900":"bg-white text-gray-500 border-gray-200 hover:border-gray-400"}`}>{t}</button>
                ))}
              </div>
            </div>
            {!emailDraft && (
              <button onClick={generateEmail} disabled={genLoading}
                className="w-full bg-sage-700 hover:bg-sage-800 text-white text-sm font-semibold py-2.5 rounded-xl transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                {genLoading?<><span className="animate-spin">↻</span> Generating...</>:"Generate personalized email"}
              </button>
            )}
            {emailDraft && (
              <div className="space-y-2">
                <div className="bg-white border border-gray-200 rounded-lg p-3">
                  <div className="text-xs text-gray-400 mb-1">Subject</div>
                  <div className="text-sm font-semibold text-gray-800">{emailDraft.subject}</div>
                </div>
                <div className="bg-white border border-gray-200 rounded-lg p-3">
                  <div className="text-xs text-gray-400 mb-1">Body</div>
                  <div className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{emailDraft.body}</div>
                </div>
                {emailDraft.followUp&&<div className="text-xs text-gray-500 italic px-1 flex items-start gap-1.5"><svg className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M9 18h6m-5 3h4M12 3a6 6 0 00-4 10.5c.5.5 1 1.2 1 2.5h6c0-1.3.5-2 1-2.5A6 6 0 0012 3z"/></svg><span>Follow-up: {emailDraft.followUp}</span></div>}
                <div className="flex gap-2 pt-1">
                  <button onClick={copyEmail} className={`flex-1 text-sm font-semibold py-2 rounded-xl border transition-all ${copied?"bg-green-600 text-white border-green-600":"bg-white border-gray-200 text-gray-700 hover:bg-gray-50"}`}>
                    {copied?"✓ Copied!":"Copy email"}
                  </button>
                  <button onClick={generateEmail} disabled={genLoading} className="px-4 py-2 text-sm border border-gray-200 text-gray-500 rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50">↻</button>
                </div>
              </div>
            )}
          </div>

          <div className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest">Notes</div>
          <div>
            {ef==="notes"?<textarea autoFocus rows={3} className="w-full border border-gray-300 rounded px-2 py-1 text-sm" value={ev} onChange={e=>setEv(e.target.value)} onBlur={save}/>
              :<div onClick={()=>edit("notes",lead.notes)} className={`text-sm cursor-pointer rounded-lg border border-dashed border-gray-200 p-2.5 hover:border-gray-300 hover:bg-gray-50 transition-colors min-h-[60px] ${lead.notes?"text-gray-700":"text-gray-300"}`}>{lead.notes||"Add notes..."}</div>}
          </div>
          {/* Intent Signals */}
          {lead.company && (
            <>
            <div className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest">Signals</div>
            <div className="border border-purple-200 rounded-xl bg-purple-50/50 p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs font-semibold text-gray-700 flex items-center gap-1.5">Intent signals — <span className="text-purple-600">{lead.company}</span></div>
                <button onClick={async ()=>{setSigLoading(true);setSignals(null);const r=await api.getIntentSignals(lead.company,lead.domain);setSigLoading(false);setSignals(r.signals||[]);}} disabled={sigLoading}
                  className="text-xs bg-white border border-purple-200 hover:border-purple-400 text-purple-600 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1">
                  {sigLoading?<><span className="animate-spin">↻</span> Scanning...</>:<><svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><circle cx="11" cy="11" r="8"/><path strokeLinecap="round" d="M21 21l-4.35-4.35"/></svg> Scan</>}
                </button>
              </div>
              {signals===null && !sigLoading && <p className="text-xs text-gray-400">Click Scan to find recent hiring, funding, news, and leadership changes.</p>}
              {signals!==null && signals.length===0 && <p className="text-xs text-gray-400">No recent signals found for this company.</p>}
              {signals!==null && signals.length>0 && (
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {signals.map((s,i)=>(
                    <a key={i} href={s.url} target="_blank" rel="noreferrer" className="block bg-white border border-gray-200 rounded-lg p-3 hover:border-purple-300 hover:shadow-sm transition-all group">
                      <div className="flex items-start gap-2">
                        <span className="text-sm flex-shrink-0">{s.icon}</span>
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-semibold text-gray-800 group-hover:text-purple-700 leading-tight mb-0.5">{s.title}</div>
                          {s.snippet && <div className="text-xs text-gray-500 leading-relaxed line-clamp-2">{s.snippet}</div>}
                          <div className="flex items-center gap-2 mt-1.5">
                            <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full border ${s.type==="funding"?"bg-green-50 text-green-600 border-green-200":s.type==="hiring"?"bg-blue-50 text-blue-600 border-blue-200":s.type==="leadership"?"bg-amber-50 text-amber-600 border-amber-200":s.type==="product"?"bg-indigo-50 text-indigo-600 border-indigo-200":"bg-gray-100 text-gray-500 border-gray-200"}`}>{s.type}</span>
                            {s.strength==="high"&&<span className="text-[10px] text-red-500 font-bold">High intent</span>}
                            {s.date&&<span className="text-[10px] text-gray-400">{s.date}</span>}
                          </div>
                        </div>
                      </div>
                    </a>
                  ))}
                </div>
              )}
            </div>
            </>
          )}

        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// CSV IMPORT UTILITIES
// ════════════════════════════════════════════════════════════════════════════
function parseCSV(text) {
  // Auto-detect delimiter: prefer comma, then tab, then semicolon
  const sep = text.indexOf(",") !== -1 ? ","
            : text.indexOf("\t") !== -1 ? "\t"
            : text.indexOf(";") !== -1  ? ";"
            : ",";
  const lines = text.trim().split(/\r?\n|\r/).filter(Boolean);
  if (lines.length < 2) return { headers: [], rows: [] };
  const parseLine = line => {
    const fields = []; let f = ""; let inQ = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (c === '"') { if (inQ && line[i+1] === '"') { f += '"'; i++; } else inQ = !inQ; }
      else if (c === sep && !inQ) { fields.push(f.trim()); f = ""; }
      else f += c;
    }
    fields.push(f.trim()); return fields;
  };
  // Auto-detect header row: skip leading title/metadata rows until we find one
  // whose cells look like column headers (short, mostly letters, no sentence-length text)
  const HEADER_KEYWORDS = /^(name|email|company|title|phone|first|last|location|city|state|linkedin|industry|website|url|contact|organization|account|mobile|role|position|notes|status|score|source|#|no\.?|id)$/i;
  let headerIdx = 0;
  for (let i = 0; i < Math.min(lines.length - 1, 5); i++) {
    const cells = parseLine(lines[i]);
    const matches = cells.filter(c => HEADER_KEYWORDS.test(c.trim().toLowerCase().replace(/[^a-z#.]/g, "")));
    if (matches.length >= 1 && cells.every(c => c.length < 60)) { headerIdx = i; break; }
  }
  const headerLine = parseLine(lines[headerIdx]);
  const dataLines = lines.slice(headerIdx + 1);
  return { headers: headerLine, rows: dataLines.map(parseLine) };
}

const COL_ALIASES = {
  name:        ["name","full name","full name ","contact","contact name","person","display name","full contact name"],
  firstName:   ["first name","firstname","given name","fname","first"],
  lastName:    ["last name","lastname","surname","family name","lname","last"],
  email:       ["email","email address","work email","business email","e mail","primary email","corporate email","company name for emails"],
  company:     ["company","company name","organization","employer","business","account","account name","firm","company  name"],
  title:       ["title","job title","position","role","occupation","designation","job role","seniority"],
  phone:       ["phone","phone number","mobile","cell","telephone","tel","mobile phone","work phone","direct phone","direct phone number","work direct phone","mobile phone number","corporate phone","other phone","phone  number"],
  location:    ["location","city","state","address","region","geo","locality","country","city  state  country","state region","city state"],
  linkedinUrl: ["linkedin","linkedin url","linkedin profile","linkedin profile url","linkedin  url"],
  website:     ["website","website url","website  url","url","domain","web","site","company website","company url"],
  industry:    ["industry","sector","vertical","market","industries","category","niche","type","business type"],
  notes:       ["notes","note","comments","description","remarks"],
  source:      ["source","lead source"],
  score:       ["score","lead score","rating","priority"],
  status:      ["status","stage","lead status"],
};

const normStr = s => s.toLowerCase().replace(/[^a-z]/g, " ").replace(/\s+/g, " ").trim();

function buildColMap(headers) {
  const map = {};
  headers.forEach((h, i) => {
    const n = normStr(h);
    for (const [field, aliases] of Object.entries(COL_ALIASES)) {
      if (!(field in map) && aliases.some(a => normStr(a) === n)) map[field] = i;
    }
  });
  return map;
}

function rowToLead(row, colMap) {
  const g = f => (colMap[f] != null ? (row[colMap[f]] || "") : "").trim();
  const firstName = g("firstName");
  const lastName  = g("lastName");
  // Only use the "name" column if it maps to a different column than firstName/lastName
  // (prevents "First Name" matching both aliases and short-circuiting the concat)
  const nameCol = colMap.name;
  const dedicatedName = (nameCol != null && nameCol !== colMap.firstName && nameCol !== colMap.lastName) ? g("name") : "";
  const name = dedicatedName || [firstName, lastName].filter(Boolean).join(" ") || g("name");
  const website = g("website");
  const baseNotes = g("notes");
  const notes = [baseNotes, website ? `Website: ${website}` : ""].filter(Boolean).join("\n");
  return {
    name, email: g("email"), company: g("company"), title: g("title"),
    phone: g("phone"), location: g("location"), linkedinUrl: g("linkedinUrl"),
    industry: g("industry"), notes,
    source: g("source") || "Imported",
    score: g("score") || null,
    status: g("status") || "New",
  };
}

// ════════════════════════════════════════════════════════════════════════════
// IMPORT MODAL
// ════════════════════════════════════════════════════════════════════════════
// Match an incoming lead against an existing lead. Returns the existing lead or null.
function findDuplicate(incoming, existingLeads) {
  const normEmail = e => (e || "").trim().toLowerCase();
  const normName  = s => (s || "").trim().toLowerCase();
  // Email match (strongest signal)
  if (incoming.email && normEmail(incoming.email) !== "not found") {
    const match = existingLeads.find(l => l.email && normEmail(l.email) === normEmail(incoming.email));
    if (match) return match;
  }
  // Company + name match (fallback)
  if (incoming.company && incoming.name) {
    const match = existingLeads.find(l =>
      l.company && l.name &&
      normName(l.company) === normName(incoming.company) &&
      normName(l.name) === normName(incoming.name)
    );
    if (match) return match;
  }
  return null;
}

function ImportModal({ onClose, onDone, userInfo, existingLeads = [] }) {
  const isPaid = userInfo?.stage === "paid" || userInfo?.stage === "admin";
  const remaining = typeof userInfo?.remaining === "number" ? userInfo.remaining : 0;
  const [tab, setTab]       = useState("csv");
  const [headers, setHeaders] = useState([]);
  const [colMap, setColMap] = useState({});
  const [rows, setRows]     = useState([]);
  const [sel, setSel]       = useState(new Set());
  const [enrichAfter, setEnrichAfter] = useState(false);
  const [enrichConfirming, setEnrichConfirming] = useState(false);
  const [dupeAction, setDupeAction] = useState("skip"); // "skip" | "update"
  const [status, setStatus] = useState(null); // null | {phase:"importing"|"updating"|"enriching", done:N, total:N}
  const [drag, setDrag]     = useState(false);
  const [error, setError]   = useState(null);
  const [manual, setManual] = useState({ name:"",email:"",company:"",title:"",phone:"",location:"",notes:"",source:"Outbound" });
  const fileRef = useRef();

  const parsedLeads = rows.map(r => rowToLead(r, colMap));
  // Annotate each lead with its matching existing lead (if any)
  const leadsWithDupeInfo = parsedLeads.map(l => ({ lead: l, existing: findDuplicate(l, existingLeads) }));
  const selectedLeads = [...sel].sort((a,b)=>a-b).map(i => leadsWithDupeInfo[i]);

  const loadFile = file => {
    if (!file) return;
    setError(null);
    const reader = new FileReader();
    reader.onload = e => {
      const { headers: h, rows: r } = parseCSV(e.target.result);
      if (!h.length) { setError("Couldn't parse this file — make sure it's a valid CSV."); return; }
      setHeaders(h); setColMap(buildColMap(h)); setRows(r);
      setSel(new Set(r.map((_, i) => i)));
    };
    reader.readAsText(file);
  };

  const toggleRow = i => setSel(prev => { const n = new Set(prev); n.has(i) ? n.delete(i) : n.add(i); return n; });
  const toggleAll = () => setSel(prev => prev.size === rows.length ? new Set() : new Set(rows.map((_, i) => i)));

  const handleImport = async () => {
    const rawToImport = tab === "csv" ? selectedLeads : [{ lead: manual, existing: null }];
    if (!rawToImport.length || (tab === "manual" && !manual.name && !manual.email)) {
      setError("Add at least a name or email before importing."); return;
    }
    // Warn if CSV data appears blank
    if (tab === "csv" && rawToImport.every(({ lead: l }) => !l.name && !l.email && !l.company)) {
      setError("All selected rows appear empty — your CSV column headers may not match expected names (Name, Email, Company, etc.)."); return;
    }
    setError(null);

    // Split into new leads and duplicates
    const newLeads  = rawToImport.filter(({ existing }) => !existing).map(({ lead }) => lead);
    const dupeLeads = rawToImport.filter(({ existing }) => !!existing);

    // Free-tier enrichment confirmation
    const allLeadCount = newLeads.length + (dupeAction === "update" ? dupeLeads.length : 0);
    const canEnrich = enrichAfter && (isPaid || remaining > 0);
    const enrichCount = isPaid ? allLeadCount : Math.min(allLeadCount, remaining);
    if (!isPaid && enrichAfter && remaining > 0 && !enrichConfirming) {
      setEnrichConfirming(true);
      return;
    }
    setEnrichConfirming(false);

    const savedIds = [];

    // ── 1. Insert new leads ──────────────────────────────────────────────────
    if (newLeads.length) {
      const withIds = newLeads.map(l => ({ ...l, id: crypto.randomUUID(), date: new Date().toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"}) }));
      const BATCH = 25;
      setStatus({ phase: "importing", done: 0, total: withIds.length });
      try {
        for (let i = 0; i < withIds.length; i += BATCH) {
          const result = await api.addLeads(withIds.slice(i, i + BATCH));
          if (result.error) throw new Error(result.error);
          const batchIds = result.ids?.length ? result.ids : withIds.slice(i, i + BATCH).map(l => l.id);
          savedIds.push(...batchIds);
          setStatus({ phase: "importing", done: Math.min(i + BATCH, withIds.length), total: withIds.length });
        }
      } catch (e) {
        setError(e.message || "Import failed. Please try again."); setStatus(null); return;
      }
    }

    // ── 2. Update duplicate leads (if user chose "update") ──────────────────
    if (dupeAction === "update" && dupeLeads.length) {
      const UPDATE_FIELDS = ["name","title","company","email","phone","location","linkedinUrl","industry","notes"];
      setStatus({ phase: "updating", done: 0, total: dupeLeads.length });
      let done = 0;
      for (const { lead, existing } of dupeLeads) {
        for (const field of UPDATE_FIELDS) {
          const val = lead[field];
          if (val && val !== "Not found" && val !== existing[field]) {
            try { await api.updateLead(existing.id, field, val); } catch {}
          }
        }
        savedIds.push(existing.id);
        done++;
        setStatus({ phase: "updating", done, total: dupeLeads.length });
      }
    }

    // ── 3. Enrich ────────────────────────────────────────────────────────────
    if (canEnrich && savedIds.length) {
      const idsToEnrich = savedIds.slice(0, enrichCount);
      const CONCURRENCY = 3;
      let done = 0;
      setStatus({ phase: "enriching", done: 0, total: idsToEnrich.length });
      for (let i = 0; i < idsToEnrich.length; i += CONCURRENCY) {
        await Promise.all(idsToEnrich.slice(i, i + CONCURRENCY).map(async id => {
          try { await api.reEnrichLead(id); } catch {}
          done++;
          setStatus({ phase: "enriching", done, total: idsToEnrich.length });
        }));
      }
    }

    await onDone();
    onClose();
  };

  const busy = !!status;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={e => { if (!busy && e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="font-display text-lg font-semibold text-sage-950">Import Leads</h2>
            <p className="text-xs text-gray-400 mt-0.5">Upload a CSV or add a lead manually</p>
          </div>
          {!busy && <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-xl leading-none">✕</button>}
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 px-6 flex-shrink-0">
          {[{id:"csv",label:"Upload CSV"},{id:"manual",label:"Add Manually"}].map(t => (
            <button key={t.id} onClick={()=>!busy&&setTab(t.id)}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors -mb-px ${tab===t.id?"border-sage-500 text-sage-700":"border-transparent text-gray-500 hover:text-gray-700"}`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {tab === "csv" && (
            <>
              {!rows.length ? (
                <div
                  onDragOver={e=>{e.preventDefault();setDrag(true);}}
                  onDragLeave={()=>setDrag(false)}
                  onDrop={e=>{e.preventDefault();setDrag(false);loadFile(e.dataTransfer.files[0]);}}
                  onClick={()=>fileRef.current?.click()}
                  className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-colors ${drag?"border-sage-400 bg-sage-50":"border-gray-300 hover:border-sage-300 hover:bg-gray-50"}`}>
                  <div className="text-3xl mb-3">📂</div>
                  <div className="text-sm font-semibold text-gray-700 mb-1">Drop a CSV file here</div>
                  <div className="text-xs text-gray-400 mb-4">or click to browse</div>
                  <div className="text-xs text-gray-400">Supported columns: name, email, company, title, phone, location, LinkedIn URL, and more</div>
                  <input ref={fileRef} type="file" accept=".csv,.tsv,.txt" className="hidden" onChange={e=>loadFile(e.target.files[0])}/>
                </div>
              ) : (
                <>
                  {/* Summary row */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <input type="checkbox" checked={sel.size===rows.length&&rows.length>0} onChange={toggleAll} className="rounded border-gray-300 accent-sage-600 cursor-pointer"/>
                      <span className="text-sm text-gray-700 font-medium">{sel.size} of {rows.length} rows selected</span>
                      {leadsWithDupeInfo.filter(({existing})=>existing).length > 0 && (
                        <span className="text-xs bg-amber-100 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full font-medium">
                          {leadsWithDupeInfo.filter(({existing})=>existing).length} duplicate{leadsWithDupeInfo.filter(({existing})=>existing).length!==1?"s":""} found
                        </span>
                      )}
                    </div>
                    <button onClick={()=>{setRows([]);setHeaders([]);setSel(new Set());}} className="text-xs text-gray-400 hover:text-red-500 transition-colors">✕ Remove file</button>
                  </div>
                  {/* Duplicate action picker */}
                  {leadsWithDupeInfo.some(({existing})=>existing) && (
                    <div className="mb-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
                      <p className="text-xs font-semibold text-amber-800 mb-2">How should duplicates be handled?</p>
                      <div className="flex gap-4">
                        {[{val:"skip",label:"Skip duplicates",desc:"Only import new contacts"},{val:"update",label:"Update existing",desc:"Overwrite with new CSV data"}].map(opt=>(
                          <label key={opt.val} className="flex items-start gap-2 cursor-pointer">
                            <input type="radio" name="dupeAction" value={opt.val} checked={dupeAction===opt.val} onChange={()=>setDupeAction(opt.val)} className="mt-0.5 accent-sage-600"/>
                            <div>
                              <div className="text-xs font-medium text-gray-800">{opt.label}</div>
                              <div className="text-xs text-gray-400">{opt.desc}</div>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Column mapping preview */}
                  {Object.keys(colMap).length === 0 ? (
                    <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3 flex items-start gap-1.5">
                      <svg className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/></svg>
                      <span>No columns matched. Check that your CSV has standard headers (Name, Email, Company, Title, etc.).</span>
                    </div>
                  ) : (
                    <div className="text-xs text-gray-400 mb-3 flex flex-wrap gap-2">
                      {Object.entries(colMap).map(([field, col]) => (
                        <span key={field} className="bg-sage-50 border border-sage-200 text-sage-700 px-2 py-0.5 rounded-full">
                          {headers[col]} → {field}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="border border-gray-200 rounded-xl overflow-hidden">
                    <div className="overflow-x-auto max-h-64">
                      <table className="w-full text-xs">
                        <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
                          <tr>
                            <th className="w-8 px-3 py-2"/>
                            <th className="w-5 px-3 py-2"/>
                            {["Name","Email","Company","Title","Phone"].map(h => (
                              <th key={h} className="px-3 py-2 text-left text-gray-500 font-medium whitespace-nowrap">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {leadsWithDupeInfo.map(({ lead: l, existing }, i) => (
                            <tr key={i} onClick={()=>toggleRow(i)} className={`cursor-pointer border-b border-gray-100 last:border-0 transition-colors ${sel.has(i)?"bg-white hover:bg-gray-50":"bg-gray-50/50 opacity-50 hover:opacity-70"}`}>
                              <td className="px-3 py-2"><input type="checkbox" checked={sel.has(i)} onChange={()=>toggleRow(i)} onClick={e=>e.stopPropagation()} className="rounded border-gray-300 accent-sage-600 cursor-pointer"/></td>
                              <td className="px-3 py-2">
                                {existing && <span title="Duplicate — already in your leads" className="inline-block w-4 h-4 rounded-full bg-amber-100 border border-amber-300 text-amber-700 text-[9px] font-bold flex items-center justify-center leading-none">↺</span>}
                              </td>
                              <td className="px-3 py-2 font-medium text-gray-800 max-w-[120px] truncate">{l.name||<span className="text-gray-300">—</span>}</td>
                              <td className="px-3 py-2 font-mono text-gray-600 max-w-[160px] truncate">{l.email||<span className="text-gray-300">—</span>}</td>
                              <td className="px-3 py-2 text-gray-700 max-w-[120px] truncate">{l.company||<span className="text-gray-300">—</span>}</td>
                              <td className="px-3 py-2 text-gray-600 max-w-[120px] truncate">{l.title||<span className="text-gray-300">—</span>}</td>
                              <td className="px-3 py-2 font-mono text-gray-600 whitespace-nowrap">{l.phone||<span className="text-gray-300">—</span>}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              )}
            </>
          )}

          {tab === "manual" && (
            <div className="grid grid-cols-2 gap-4">
              {[
                {key:"name",label:"Full Name",placeholder:"Jane Smith"},
                {key:"email",label:"Email",placeholder:"jane@company.com"},
                {key:"company",label:"Company",placeholder:"Acme Corp"},
                {key:"title",label:"Job Title",placeholder:"VP of Sales"},
                {key:"phone",label:"Phone",placeholder:"(555) 123-4567"},
                {key:"location",label:"Location",placeholder:"Chicago, IL"},
              ].map(({key,label,placeholder}) => (
                <div key={key}>
                  <label className="text-xs text-gray-500 font-medium mb-1 block">{label}</label>
                  <input value={manual[key]} onChange={e=>setManual(p=>({...p,[key]:e.target.value}))} placeholder={placeholder}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-sage-300"/>
                </div>
              ))}
              <div className="col-span-2">
                <label className="text-xs text-gray-500 font-medium mb-1 block">Notes</label>
                <textarea value={manual.notes} onChange={e=>setManual(p=>({...p,notes:e.target.value}))} placeholder="Any context about this lead..."
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-sage-300 resize-none" rows={2}/>
              </div>
              <div>
                <label className="text-xs text-gray-500 font-medium mb-1 block">Source</label>
                <select value={manual.source} onChange={e=>setManual(p=>({...p,source:e.target.value}))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-sage-300">
                  <option value="Outbound">Outbound</option>
                  <option value="Inbound">Inbound</option>
                </select>
              </div>
            </div>
          )}

          {error && <div className="mt-4 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex-shrink-0 bg-white">
          {/* Free-tier enrichment confirmation */}
          {enrichConfirming && !busy && (
            <div className="mb-3 bg-violet-50 border border-violet-200 rounded-xl px-4 py-3 text-sm">
              <p className="text-violet-800 font-medium mb-0.5">Use enrichment credits?</p>
              <p className="text-violet-600 text-xs">This will use <strong>{Math.min(sel.size || 1, remaining)}</strong> of your <strong>{remaining}</strong> remaining credit{remaining !== 1 ? "s" : ""} to fill in emails, phones &amp; LinkedIn.</p>
              <div className="flex gap-2 mt-2.5">
                <button onClick={()=>{ setEnrichAfter(false); setEnrichConfirming(false); }} className="text-xs bg-white border border-violet-300 text-violet-700 hover:bg-violet-50 px-3 py-1.5 rounded-lg font-medium transition-colors">Import without enriching</button>
                <button onClick={handleImport} className="text-xs bg-violet-600 hover:bg-violet-700 text-white px-3 py-1.5 rounded-lg font-semibold transition-colors">Confirm &amp; enrich</button>
              </div>
            </div>
          )}
          <div className="flex items-center gap-4">
            {(isPaid || remaining > 0) && (
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input type="checkbox" checked={enrichAfter} onChange={e=>{ setEnrichAfter(e.target.checked); setEnrichConfirming(false); }} disabled={busy} className="rounded border-gray-300 accent-sage-600 cursor-pointer"/>
                <span className="text-sm text-gray-700">Enrich after import</span>
                {!isPaid && <span className="text-xs text-violet-600 font-medium">{remaining} credit{remaining !== 1 ? "s" : ""} left</span>}
                {isPaid && <span className="text-xs text-gray-400">(fills emails, phones, LinkedIn)</span>}
              </label>
            )}
            <div className="ml-auto flex items-center gap-3">
              {!busy && <button onClick={onClose} className="text-sm text-gray-500 hover:text-gray-700 transition-colors">Cancel</button>}
              {!enrichConfirming && (
                <button onClick={handleImport} disabled={busy || (tab==="csv" && !sel.size && !rows.length)}
                  className="bg-sage-700 hover:bg-sage-800 disabled:opacity-50 text-white text-sm font-semibold px-5 py-2.5 rounded-xl transition-colors min-w-[130px] text-center">
                  {busy ? (
                    status.phase === "importing" ? `Importing ${status.done}/${status.total}…`
                    : status.phase === "updating" ? `Updating ${status.done}/${status.total}…`
                    : `Enriching ${status.done}/${status.total}…`
                  ) : tab === "csv"
                    ? sel.size > 0 ? `Import ${sel.size} lead${sel.size>1?"s":""}` : "Import"
                    : "Add Lead"
                  }
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// LEAD BOARD
// ════════════════════════════════════════════════════════════════════════════
// Confidence badge for enriched contact fields
function ConfBadge({ level }) {
  if (!level) return null;
  const styles = {
    inbound:  "bg-teal-100 text-teal-700 border-teal-300",
    verified: "bg-green-100 text-green-700 border-green-200",
    pattern:  "bg-yellow-100 text-yellow-700 border-yellow-200",
    guessed:  "bg-gray-100 text-gray-500 border-gray-200",
  };
  const labels = { inbound: "★", verified: "✓", pattern: "~", guessed: "?" };
  const titles = { inbound: "Self-reported by visitor — high confidence", verified: "Verified from source", pattern: "Email pattern match", guessed: "AI estimated" };
  return (
    <span title={titles[level]||level} className={`inline-flex items-center justify-center w-4 h-4 rounded-full text-[9px] font-bold border flex-shrink-0 ${styles[level]||styles.guessed}`}>
      {labels[level]||"?"}
    </span>
  );
}

function LeadBoard({ leads, onUpdate, userInfo, onUpgrade, onShowPaywall, onBulkUpdate, onDelete, onReEnrich, onDone, onOpenProspector }) {
  const isPaid = userInfo?.stage === "paid" || userInfo?.stage === "admin";
  const [search,setSearch]=useState(""); const [src,setSrc]=useState("All");
  const [score,setScore]=useState("All"); const [sortBy,setSortBy]=useState("Newest");
  const [hasEmail,setHasEmail]=useState(false); const [hasPhone,setHasPhone]=useState(false);
  const [selected,setSelected]=useState(null); const [view,setView]=useState("table");
  const [checkedIds,setCheckedIds]=useState(new Set());
  const [bulkLoading,setBulkLoading]=useState(false);
  const [bulkEnrichProgress,setBulkEnrichProgress]=useState(null); // {done,total}
  const [showImport,setShowImport]=useState(false);

  const toggleCheck = (id,e) => { e.stopPropagation(); setCheckedIds(prev=>{const n=new Set(prev);n.has(id)?n.delete(id):n.add(id);return n;}); };
  const toggleAll = () => setCheckedIds(prev=>prev.size===filtered.length&&filtered.length>0?new Set():new Set(filtered.map(l=>l.id)));
  const clearSelection = () => setCheckedIds(new Set());

  const bulkUpdate = async (field, value) => {
    if (!value || !checkedIds.size || !onBulkUpdate) return;
    setBulkLoading(true);
    await onBulkUpdate([...checkedIds], field, value);
    setBulkLoading(false); clearSelection();
  };
  const bulkDelete = async () => {
    if (!checkedIds.size || !onDelete) return;
    if (!window.confirm(`Delete ${checkedIds.size} lead${checkedIds.size>1?"s":""}? This cannot be undone.`)) return;
    setBulkLoading(true);
    try {
      await onDelete([...checkedIds]);
      clearSelection();
    } catch (e) {
      alert(e?.message || "Failed to delete leads. Please try again.");
    } finally {
      setBulkLoading(false);
    }
  };

  const exportCSV = () => {
    const toExport = leads.filter(l => checkedIds.has(l.id));
    const COLS = [
      { key: "name",        label: "Name" },
      { key: "title",       label: "Title" },
      { key: "company",     label: "Company" },
      { key: "industry",    label: "Industry" },
      { key: "email",       label: "Email" },
      { key: "phone",       label: "Phone" },
      { key: "location",    label: "Location" },
      { key: "linkedinUrl", label: "LinkedIn URL" },
      { key: "score",       label: "Score" },
      { key: "status",      label: "Status" },
      { key: "source",      label: "Source" },
      { key: "notes",       label: "Notes" },
      { key: "date",        label: "Date Added" },
    ];
    const escape = v => {
      if (v == null || v === "") return "";
      const s = String(v);
      return s.includes(",") || s.includes('"') || s.includes("\n") ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = [
      COLS.map(c => c.label).join(","),
      ...toExport.map(l => COLS.map(c => escape(l[c.key])).join(","))
    ];
    const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `virtuosoh-export-${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };
  const bulkEnrich = async () => {
    if (!checkedIds.size || !isPaid) return;
    const ids = [...checkedIds];
    setBulkLoading(true);
    setBulkEnrichProgress({ done: 0, total: ids.length });
    const CONCURRENCY = 3;
    let done = 0;
    for (let i = 0; i < ids.length; i += CONCURRENCY) {
      await Promise.all(ids.slice(i, i + CONCURRENCY).map(async id => {
        try {
          const data = await api.reEnrichLead(id);
          if (data.ok && onReEnrich) onReEnrich(id, data);
        } catch {}
        done++;
        setBulkEnrichProgress({ done, total: ids.length });
      }));
    }
    setBulkLoading(false); setBulkEnrichProgress(null); clearSelection();
  };

  const inC=leads.filter(l=>l.source==="Inbound").length;
  const outC=leads.filter(l=>l.source==="Outbound").length;
  const hotC=leads.filter(l=>l.score==="Hot").length;

  const filtered=leads.filter(l=>{
    const ms=src==="All"||l.source===src;
    const msc=score==="All"||l.score===score||l.status===score;
    const me=!hasEmail||(l.email&&l.email.trim()&&l.email.toLowerCase()!=="not found");
    const mp=!hasPhone||(l.phone&&l.phone.trim()&&l.phone.toLowerCase()!=="not found");
    const q=search.toLowerCase();
    return ms&&msc&&me&&mp&&(!q||[l.name,l.company,l.title,l.email,l.industry].some(f=>f?.toLowerCase().includes(q)));
  }).sort((a,b)=>{
    if(sortBy==="Score") return (SCORE_ORDER[a.score]??9)-(SCORE_ORDER[b.score]??9);
    if(sortBy==="Company") return (a.company||"").localeCompare(b.company||"");
    if(sortBy==="Status") return (a.status||"").localeCompare(b.status||"");
    return 0;
  });

  const KANBAN_COLS=["New","Contacted","Qualified","Closed Won"];

  if(!leads.length) return (
    <div className="flex flex-col items-center justify-center h-full p-6 bg-gray-50">
      {showImport&&<ImportModal onClose={()=>setShowImport(false)} onDone={async()=>{ if(onDone) await onDone(); }} userInfo={userInfo} existingLeads={leads}/>}
      <div className="w-full max-w-2xl rounded-2xl border border-sage-100 bg-white shadow-sm p-8 text-center">
        <div className="mx-auto w-16 h-16 rounded-2xl bg-sage-50 border border-sage-100 flex items-center justify-center mb-5 shadow-sm">
          <svg className="w-8 h-8 text-sage-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/></svg>
        </div>
        <span className="inline-flex items-center rounded-full bg-sage-50 text-sage-700 border border-sage-100 px-3 py-1 text-xs font-semibold mb-3">Pipeline starts here</span>
        <h3 className="font-display text-sage-950 font-semibold text-2xl tracking-tight mb-2">No leads yet</h3>
        <p className="text-gray-500 text-sm max-w-lg mx-auto mb-6">Run your first search in Prospector, import an existing list, or let the inbound chat widget capture conversations for you.</p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-6">
          <button onClick={()=>onOpenProspector&&onOpenProspector()} className="bg-sage-700 hover:bg-sage-800 text-white text-sm font-semibold px-5 py-2.5 rounded-xl transition-colors flex items-center gap-2 w-full sm:w-auto justify-center">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v12m6-6H6"/></svg>
            Open Prospector
          </button>
          <button onClick={()=>setShowImport(true)} className="bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 text-sm font-semibold px-5 py-2.5 rounded-xl transition-colors flex items-center gap-2 w-full sm:w-auto justify-center">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10"/></svg>
            Import existing leads
          </button>
        </div>
        <div className="grid sm:grid-cols-3 gap-3 text-left">
          {[
            ["Prospector", "Describe who you want to reach and let AI build the list."],
            ["Import", "Bring over a CSV when you already have a starting pipeline."],
            ["Inbound", "Capture and qualify site visitors with the chat widget."],
          ].map(([title, copy]) => (
            <div key={title} className="rounded-2xl border border-gray-100 bg-gray-50 px-4 py-3">
              <div className="text-sm font-semibold text-gray-800 mb-1">{title}</div>
              <div className="text-xs text-gray-500 leading-relaxed">{copy}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col h-full bg-gray-50">
      {showImport&&<ImportModal onClose={()=>setShowImport(false)} onDone={async()=>{ if(onDone) await onDone(); }} userInfo={userInfo} existingLeads={leads}/>}
      {selected&&<LeadDrawer lead={selected} onClose={()=>setSelected(null)}
        onUpdate={(id,f,v)=>{onUpdate(id,f,v);setSelected(l=>({...l,[f]:v}));}}
        onReEnrich={isPaid ? (id,data)=>{if(onReEnrich)onReEnrich(id,data);setSelected(l=>({...l,...data}));} : null}
      />}

      {/* ── Bulk action floating bar ── */}
      {checkedIds.size>0&&(
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-gray-900 text-white rounded-2xl shadow-2xl px-5 py-3 flex items-center gap-3 flex-wrap">
          <span className="text-sm font-bold whitespace-nowrap">{checkedIds.size} selected</span>
          <div className="w-px h-5 bg-white/20 flex-shrink-0"/>
          {isPaid&&(
            <button onClick={bulkEnrich} disabled={bulkLoading}
              className="bg-sage-500 hover:bg-sage-600 disabled:opacity-50 text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors whitespace-nowrap flex items-center gap-1.5">
              {bulkEnrichProgress ? `Enriching ${bulkEnrichProgress.done}/${bulkEnrichProgress.total}…` : `✦ Enrich ${checkedIds.size}`}
            </button>
          )}
          <button onClick={exportCSV} disabled={bulkLoading}
            className="bg-white/10 hover:bg-white/20 disabled:opacity-50 text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors whitespace-nowrap flex items-center gap-1.5 border border-white/20">
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/></svg>
            Export CSV
          </button>
          <div className="w-px h-5 bg-white/20 flex-shrink-0"/>
          <select defaultValue="" disabled={bulkLoading}
            onChange={e=>{if(e.target.value){bulkUpdate("status",e.target.value);e.target.value="";}}}
            className="bg-white/10 text-white text-xs rounded-lg px-2.5 py-1.5 outline-none border border-white/20 cursor-pointer">
            <option value="" disabled>Set status…</option>
            {["New","Contacted","Qualified","Closed Won","Closed Lost"].map(s=><option key={s} value={s}>{s}</option>)}
          </select>
          <select defaultValue="" disabled={bulkLoading}
            onChange={e=>{if(e.target.value){bulkUpdate("score",e.target.value);e.target.value="";}}}
            className="bg-white/10 text-white text-xs rounded-lg px-2.5 py-1.5 outline-none border border-white/20 cursor-pointer">
            <option value="" disabled>Set score…</option>
            {["Hot","Warm","Cold"].map(s=><option key={s} value={s}>{s}</option>)}
          </select>
          <input type="date" disabled={bulkLoading} title="Set follow-up date for all selected"
            onChange={e=>{if(e.target.value)bulkUpdate("followUpDate",e.target.value);}}
            className="bg-white/10 text-white text-xs rounded-lg px-2.5 py-1.5 outline-none border border-white/20 cursor-pointer [color-scheme:dark]"/>
          <div className="w-px h-5 bg-white/20 flex-shrink-0"/>
          <button onClick={bulkDelete} disabled={bulkLoading}
            className="bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors whitespace-nowrap">
            {bulkLoading?"…":`Delete ${checkedIds.size}`}
          </button>
          <button onClick={clearSelection} aria-label="Clear selection" className="text-white/50 hover:text-white transition-colors ml-1"><svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/></svg></button>
        </div>
      )}
      {!isPaid && leads.some(l=>l._redacted || (l.source==="Outbound" && !l.email)) && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-2.5 flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs text-amber-800">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/></svg>
            <span><strong>Contact data is locked.</strong> Upgrade to reveal emails, phones, and LinkedIn for all leads.</span>
          </div>
          <button onClick={()=>onShowPaywall&&onShowPaywall()} className="bg-sage-700 hover:bg-sage-800 text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors whitespace-nowrap">Upgrade →</button>
        </div>
      )}
      <div className="px-4 md:px-6 pt-5 pb-4 bg-gradient-to-b from-sage-50/90 to-white border-b border-gray-100">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <span className="inline-flex items-center rounded-full border border-sage-100 bg-white text-sage-700 px-3 py-1 text-xs font-semibold mb-3">Pipeline overview</span>
            <h1 className="font-display text-2xl font-semibold tracking-tight text-sage-950">Leads</h1>
            <p className="text-sm text-gray-500 mt-1">Keep every conversation, enrichment result, and next step organized in one place.</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="rounded-2xl border border-gray-200 bg-white px-4 py-3 text-right min-w-[150px] shadow-sm">
              <div className="text-xs uppercase tracking-wide text-gray-400">Ready for outreach</div>
              <div className="text-lg font-bold text-gray-900">{filtered.length}</div>
            </div>
            <button onClick={()=>onOpenProspector&&onOpenProspector()} className="bg-sage-700 hover:bg-sage-800 text-white text-sm font-semibold px-4 py-2.5 rounded-xl transition-colors whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-sage-300">
              Find more leads
            </button>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-gray-200 border-b border-gray-200">
        {[{label:"Total",val:leads.length,color:"text-gray-900"},{label:"Inbound",val:inC,color:"text-teal-700"},{label:"Outbound",val:outC,color:"text-emerald-700"},{label:"Hot",val:hotC,color:"text-red-600"}].map(s=>(
          <div key={s.label} className="bg-white px-4 py-3 text-center"><div className={`text-xl font-bold ${s.color}`}>{s.val}</div><div className="text-xs text-gray-400">{s.label}</div></div>
        ))}
      </div>
      {/* ── Toolbar row 1: search + actions (always visible) ── */}
      <div className="bg-white border-b border-gray-100 px-4 py-2 flex items-center gap-2">
        <input className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm outline-none focus:border-sage-500 focus:ring-2 focus:ring-sage-200 flex-1 min-w-0 max-w-xs" placeholder="Search leads..." value={search} onChange={e=>setSearch(e.target.value)}/>
        <div className="ml-auto flex items-center gap-2 flex-shrink-0">
          <button onClick={()=>setShowImport(true)} className="text-xs bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-gray-300 px-3 py-1.5 rounded-lg font-semibold transition-colors whitespace-nowrap flex items-center gap-1.5">
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10"/></svg>
            Import
          </button>
          <select value={sortBy} onChange={e=>setSortBy(e.target.value)} className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 text-gray-600 outline-none">{SORT_OPTIONS.map(o=><option key={o}>{o}</option>)}</select>
          <div className="flex border border-gray-200 rounded-lg overflow-hidden">
            <button onClick={()=>setView("table")} className={`px-2.5 py-1.5 text-xs transition-colors ${view==="table"?"bg-sage-700 text-white":"bg-white text-gray-500"}`}><svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 6h18M3 14h18M3 18h18"/></svg></button>
            <button onClick={()=>setView("kanban")} className={`px-2.5 py-1.5 text-xs transition-colors ${view==="kanban"?"bg-sage-700 text-white":"bg-white text-gray-500"}`}><svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2"/></svg></button>
          </div>
        </div>
      </div>
      {/* ── Toolbar row 2: filters (collapsible on mobile) ── */}
      <div className="bg-white border-b border-gray-200 px-4 py-2 flex flex-wrap gap-1.5 items-center">
        <div className="flex gap-1">
          {["All","Inbound","Outbound","Imported"].map(s=>(
            <button key={s} onClick={()=>setSrc(s)} className={`text-xs px-3 py-1.5 rounded-full border font-medium transition-colors ${src===s?s==="Inbound"?"bg-teal-600 text-white border-teal-600":s==="Outbound"?"bg-emerald-600 text-white border-emerald-600":s==="Imported"?"bg-violet-600 text-white border-violet-600":"bg-gray-800 text-white border-gray-800":"bg-white text-gray-600 border-gray-200 hover:border-gray-400"}`}>{s}</button>
          ))}
        </div>
        <div className="w-px h-4 bg-gray-200 flex-shrink-0"/>
        <div className="flex gap-1 flex-wrap">
          {["All","Hot","Warm","Cold","Qualified","Contacted"].map(f=>(
            <button key={f} onClick={()=>setScore(f)} className={`text-xs px-3 py-1.5 rounded-full border font-medium transition-colors ${score===f?"bg-sage-700 text-white border-sage-700":"bg-white text-gray-600 border-gray-200 hover:border-gray-400"}`}>{f}</button>
          ))}
        </div>
        <div className="w-px h-4 bg-gray-200 flex-shrink-0"/>
        <button onClick={()=>setHasEmail(v=>!v)} className={`text-xs px-3 py-1.5 rounded-full border font-medium transition-colors ${hasEmail?"bg-sage-700 text-white border-sage-700":"bg-white text-gray-600 border-gray-200 hover:border-gray-400"}`}>Has email</button>
        <button onClick={()=>setHasPhone(v=>!v)} className={`text-xs px-3 py-1.5 rounded-full border font-medium transition-colors ${hasPhone?"bg-sage-700 text-white border-sage-700":"bg-white text-gray-600 border-gray-200 hover:border-gray-400"}`}>Has phone</button>
        {(search||src!=="All"||score!=="All"||hasEmail||hasPhone)&&(
          <button onClick={()=>{setSearch("");setSrc("All");setScore("All");setHasEmail(false);setHasPhone(false);}} className="text-xs text-gray-400 hover:text-gray-700 border border-gray-200 hover:border-gray-400 px-2.5 py-1.5 rounded-lg transition-colors whitespace-nowrap ml-auto">✕ Clear</button>
        )}
      </div>
      {view==="table"&&<div className="flex-1 overflow-auto">
        <table className="w-full text-left">
          <thead className="sticky top-0 bg-gray-50 border-b border-gray-200 z-10">
            <tr className="text-xs text-gray-500 font-medium uppercase tracking-wider">
              <th className="px-4 py-2.5 w-8">
                <input type="checkbox" title="Select all" checked={checkedIds.size===filtered.length&&filtered.length>0} onChange={toggleAll} className="rounded border-gray-300 cursor-pointer accent-sage-600"/>
              </th>
              {["Name","Title","Company","Score","Status","Email","Phone","Actions"].map(h=><th key={h} className="px-4 py-2.5 whitespace-nowrap">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {filtered.map(l=>(
              <tr key={l.id} onClick={()=>setSelected(l)} className={`border-b border-gray-100 hover:bg-gray-50 cursor-pointer text-sm transition-colors group ${checkedIds.has(l.id)?"bg-gray-100/60":""}`}>
                <td className="px-4 py-3 w-8" onClick={e=>e.stopPropagation()}>
                  <input type="checkbox" checked={checkedIds.has(l.id)} onChange={e=>toggleCheck(l.id,e)} className="rounded border-gray-300 cursor-pointer accent-sage-600"/>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2.5">
                    <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center text-xs font-bold text-gray-500 flex-shrink-0 group-hover:bg-gray-200 group-hover:text-gray-700">{l.name?.[0]?.toUpperCase()||"?"}</div>
                    <div>
                      <div className="font-semibold text-gray-900 text-xs flex items-center gap-1.5">
                        {l.name||"—"}
                        {(()=>{const td=new Date().toISOString().split("T")[0];return l.followUpDate&&l.followUpDate<td?<span className="text-[9px] bg-red-100 text-red-600 border border-red-200 px-1.5 py-0.5 rounded-full font-bold">Overdue</span>:l.followUpDate===td?<span className="text-[9px] bg-amber-100 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded-full font-bold">Follow up</span>:null;})()}
                      </div>
                      <div className="text-xs text-gray-400">{l.source} · {l.date}</div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-gray-600 text-xs max-w-[140px] truncate">{l.title||"—"}</td>
                <td className="px-4 py-3">
                  <div className="text-xs font-medium text-gray-800">{l.company||"—"}</div>
                  {l.companySize && <div className="text-xs text-gray-400">{l.companySize}</div>}
                </td>
                <td className="px-4 py-3"><span className={`inline-block text-xs border px-2 py-0.5 rounded-full font-semibold ${STATUS_STYLE[l.score]||STATUS_STYLE.Cold}`}>{l.score||"—"}</span></td>
                <td className="px-4 py-3"><span className={`inline-block text-xs border px-2 py-0.5 rounded-full font-medium ${STATUS_STYLE[l.status]||STATUS_STYLE.New}`}>{l.status||"New"}</span></td>
                <td className="px-4 py-3 text-xs">
                  {l.email ? (
                    <span className="flex items-center gap-1 font-mono text-gray-600">
                      <span className="truncate max-w-[130px]">{l.email}</span>
                      <ConfBadge level={l.confidenceEmail}/>
                      <button onClick={e=>{e.stopPropagation();navigator.clipboard.writeText(l.email);}} className="opacity-0 group-hover:opacity-100 ml-1 text-gray-400 hover:text-gray-700 transition-all" title="Copy email">
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/></svg>
                      </button>
                    </span>
                  ) : l._redacted || (!isPaid && !l.email && l.source==="Outbound") ? (
                    <span className="text-gray-300 text-xs flex items-center gap-1 select-none" style={{filter:"blur(3px)",WebkitUserSelect:"none"}}>name@company.com</span>
                  ) : <span className="text-gray-300">—</span>}
                </td>
                <td className="px-4 py-3 text-xs font-mono text-gray-500 whitespace-nowrap">
                  {l.phone ? (
                    <span className="flex items-center gap-1">{l.phone}<ConfBadge level={l.confidencePhone}/></span>
                  ) : l._redacted || (!isPaid && !l.phone && l.source==="Outbound") ? (
                    <span className="text-gray-300 text-xs select-none" style={{filter:"blur(3px)",WebkitUserSelect:"none"}}>(555) 123-4567</span>
                  ) : <span className="text-gray-300">—</span>}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    {l.linkedinUrl && <a href={l.linkedinUrl} target="_blank" rel="noreferrer" onClick={e=>e.stopPropagation()} className="w-6 h-6 rounded bg-blue-50 text-blue-600 flex items-center justify-center text-xs font-bold hover:bg-blue-100 transition-colors" title="Open LinkedIn">in</a>}
                    {l.email && <a href={`mailto:${l.email}`} onClick={e=>e.stopPropagation()} className="w-6 h-6 rounded bg-gray-100 text-gray-500 flex items-center justify-center hover:bg-gray-200 transition-colors" title="Send email"><svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg></a>}
                    {!l.email && !l.linkedinUrl && (l._redacted || (!isPaid && l.source==="Outbound")) && (
                      <button type="button" className="text-xs text-sage-600 font-medium flex items-center gap-1 cursor-pointer hover:underline" onClick={e=>{e.stopPropagation();onShowPaywall&&onShowPaywall();}}>
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/></svg>
                        Upgrade
                      </button>
                    )}
                    <button onClick={e=>{e.stopPropagation();setSelected(l);}} className="w-6 h-6 rounded bg-gray-100 text-gray-500 flex items-center justify-center hover:bg-gray-200 transition-colors" title="View details"><svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/></svg></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!filtered.length&&<div className="text-center py-16 text-gray-400 text-sm">No leads match your filters.</div>}
      </div>}
      {view==="kanban"&&<div className="flex-1 overflow-x-auto p-4"><div className="flex gap-4 h-full min-w-max">
        {KANBAN_COLS.map(col=>{
          const cl=filtered.filter(l=>(l.status||"New")===col);
          return <div key={col} className="w-64 flex flex-col bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-3 py-2.5 border-b border-gray-100 flex items-center justify-between"><span className="text-xs font-semibold text-gray-700">{col}</span><span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full font-medium">{cl.length}</span></div>
            <div className="flex-1 overflow-y-auto p-2 space-y-2">
              {!cl.length&&<div className="text-xs text-gray-300 text-center py-6">No leads</div>}
              {cl.map(l=><div key={l.id} onClick={()=>setSelected(l)} className="bg-gray-50 border border-gray-200 rounded-lg p-3 cursor-pointer hover:border-gray-400 hover:shadow-sm transition-all">
                <div className="flex items-start justify-between gap-1 mb-1.5"><div className="font-semibold text-gray-900 text-xs leading-tight">{l.name||"Unknown"}</div><span className={`text-xs border px-1.5 py-0.5 rounded-full font-semibold flex-shrink-0 ${SOURCE_STYLE[l.source]||""}`}>{l.source==="Inbound"?"In":l.source==="Imported"?"Imp":"Out"}</span></div>
                <div className="text-xs text-gray-500 truncate">{l.title}</div>
                <div className="text-xs text-gray-700 font-medium truncate">{l.company}</div>
                {l.score&&<span className={`mt-1.5 inline-block text-xs border px-2 py-0.5 rounded-full font-medium ${STATUS_STYLE[l.score]||STATUS_STYLE.Cold}`}>{l.score}</span>}
              </div>)}
            </div>
          </div>;
        })}
      </div></div>}
      <div className="px-4 py-2 border-t border-gray-200 bg-white text-xs text-gray-400 flex items-center relative">
        <span>{filtered.length} of {leads.length} leads</span>
        <span className="flex gap-3 absolute left-1/2 -translate-x-1/2"><span><span className="inline-block w-2 h-2 rounded-full bg-teal-500 mr-1"></span>Inbound: {inC}</span><span><span className="inline-block w-2 h-2 rounded-full bg-emerald-500 mr-1"></span>Outbound: {outC}</span></span>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// INBOUND CHAT
// ════════════════════════════════════════════════════════════════════════════
// ════════════════════════════════════════════════════════════════════════════
// INBOUND CHAT BUBBLE  (floating, always visible, random agent name)
// ════════════════════════════════════════════════════════════════════════════
function InboundChatBubble({ addLead, companyContext }) {
  const [agentName] = useState(() => AGENT_NAMES[Math.floor(Math.random() * AGENT_NAMES.length)]);
  const [chatSessionId] = useState(() => uid() + "-" + uid());
  const [open, setOpen]           = useState(false);
  const [qualified, setQualified] = useState(false);
  const [input, setInput]         = useState("");
  const [loading, setLoading]     = useState(false);
  const firstName = agentName.split(" ")[0];
  const initials  = agentName.split(" ").map(n=>n[0]).join("");
  const [msgs, setMsgs] = useState(() => [{role:"assistant", text:`Hey there! 👋 I'm ${firstName}, happy to help. What brings you here today?`}]);
  const bottomRef = useRef();

  useEffect(()=>{ bottomRef.current?.scrollIntoView({behavior:"smooth"}); },[msgs, open]);

  const SYSTEM = `You are ${agentName}, a real human sales rep at Virtuosoh — never reveal you're an AI. Tone: warm, natural, casual, 2-3 sentences max, no bullet points.

ABOUT VIRTUOSOH:
Virtuosoh is an AI-powered B2B lead generation and enrichment platform. It helps sales and marketing teams find, enrich, and manage high-quality leads automatically.

KEY FEATURES:
- AI Prospector: Describe your product and ideal customer, and our AI agents build an ICP (Ideal Customer Profile), identify real matching companies, then enrich each lead with verified emails, phone numbers, and LinkedIn profiles
- Multi-Source Enrichment: We pull data from Google/LinkedIn search, company websites (Firecrawl-powered scraping), public records (TruePeopleSearch), and email pattern validation with MX verification
- Inbound Chat Widget: This chat you're using right now — it captures and qualifies inbound visitors as leads automatically
- Lead Management Board: Organize leads by status (New, Contacted, Qualified, Hot, Warm, Cold) with full contact details and confidence scores
- Data Confidence Scoring: Every contact field shows verified (✓), pattern-matched (~), or guessed (?) confidence level

PRICING TIERS:
- Free: 15 contacts/month, basic lead lists only (no enriched contact data — emails, phones, LinkedIn are locked)
- Starter ($9.99/mo): 50 enriched contacts/month with full emails, phones, LinkedIn URLs
- Growth ($29.99/mo): 500 enriched contacts/month — our most popular plan
- Scale ($59.99/mo): 2,000 enriched contacts/month for high-volume teams

All paid plans include: verified emails, direct phone numbers, LinkedIn profile URLs, confidence scoring, and priority support. Free users see leads but contact details are blurred until they upgrade.

GOALS: (1) answer questions about Virtuosoh helpfully and accurately, (2) qualify leads by naturally collecting: full name, email, company, company size, title, interest, timeline, budget — ask one at a time, don't be pushy. Once you have name + email + company + interest, append at end: <QUALIFY>{"name":"...","email":"...","company":"...","companySize":"...","title":"...","interest":"...","timeline":"...","budget":"...","score":"Hot|Warm|Cold","scoreReason":"..."}</QUALIFY>.`;

  const send = async () => {
    if (!input.trim()||loading) return;
    const um = {role:"user",text:input};
    const nm = [...msgs, um];
    setMsgs(nm); setInput(""); setLoading(true);
    const reply = await callClaude(nm.map(m=>({role:m.role==="assistant"?"assistant":"user",content:m.text})), SYSTEM, chatSessionId);
    const qm    = reply.match(/<QUALIFY>([\s\S]*?)<\/QUALIFY>/);
    const clean = reply.replace(/<QUALIFY>[\s\S]*?<\/QUALIFY>/,"").trim();
    if (qm&&!qualified) { try { const d=JSON.parse(qm[1]); addLead({id:uid(),source:"Inbound",date:now(),status:"New",phone:"",notes:"",agentName,...d}); setQualified(true); } catch{} }
    setMsgs(p=>[...p,{role:"assistant",text:clean||"Thanks! Our team will be in touch shortly. 😊"}]);
    setLoading(false);
  };

  return (
    <>
      {/* ── Chat window ── */}
      {open && (
        <div className="fixed bottom-20 right-4 z-50 w-80 sm:w-96 bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden" style={{height:"460px"}}>
          <div className="bg-gradient-to-r from-sage-600 to-sage-700 px-4 py-3 flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white font-bold text-xs flex-shrink-0">{initials}</div>
            <div className="flex-1 min-w-0">
              <div className="text-white font-semibold text-sm">{agentName}</div>
              <div className="flex items-center gap-1.5"><div className="w-1.5 h-1.5 rounded-full bg-green-400"></div><span className="text-white/70 text-xs">Online</span></div>
            </div>
            {qualified && <span className="text-xs bg-green-500 text-white px-2 py-0.5 rounded-full flex-shrink-0">✓ Lead Captured</span>}
            <button onClick={()=>setOpen(false)} className="text-white/70 hover:text-white text-lg leading-none ml-1">✕</button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
            {msgs.map((m,i)=>(
              <div key={i} className={`flex ${m.role==="user"?"justify-end":"justify-start"}`}>
                <div className={`max-w-[78%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${m.role==="user"?"bg-sage-600 text-white rounded-br-sm":"bg-white text-gray-800 shadow-sm border border-gray-100 rounded-bl-sm"}`}>{m.text}</div>
              </div>
            ))}
            {loading && <div className="flex justify-start"><div className="bg-white border border-gray-100 shadow-sm px-4 py-3 rounded-2xl rounded-bl-sm"><div className="flex gap-1">{[0,1,2].map(i=><div key={i} className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{animationDelay:`${i*0.15}s`}}></div>)}</div></div></div>}
            <div ref={bottomRef}/>
          </div>
          <div className="p-3 bg-white border-t border-gray-100">
            <div className="flex gap-2">
              <input className="flex-1 border border-gray-200 rounded-full px-4 py-2 text-sm outline-none focus:border-sage-500 focus:ring-2 focus:ring-sage-200" placeholder="Type a message..." value={input} onChange={e=>setInput(e.target.value)} onKeyDown={e=>e.key==="Enter"&&send()}/>
              <button onClick={send} disabled={loading} className="bg-sage-700 hover:bg-sage-800 text-white rounded-full w-9 h-9 flex items-center justify-center disabled:opacity-50 flex-shrink-0"><svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"/></svg></button>
            </div>
          </div>
        </div>
      )}

      {/* ── Bubble button ── */}
      <button onClick={()=>setOpen(o=>!o)} className="fixed bottom-4 right-4 z-50 bg-gradient-to-r from-sage-600 to-sage-700 text-white rounded-full shadow-xl hover:shadow-2xl hover:scale-105 transition-all flex items-center gap-2.5 px-4 py-2.5">
        <div className="relative flex-shrink-0">
          <div className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center text-xs font-bold">{initials}</div>
          {!open && <div className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-green-400 border-2 border-sage-600"></div>}
        </div>
        <div className="text-left">
          <div className="text-xs font-semibold leading-tight">{firstName} is online</div>
          <div className="text-xs text-white/70 leading-tight">{open?"Close chat":"Chat with us ↗"}</div>
        </div>
      </button>
    </>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// OUTBOUND SDR  (two-agent: ICP Builder → Lead Researcher)
// ════════════════════════════════════════════════════════════════════════════
const SENIORITY_OPTIONS = ["Any level", "C-Suite", "VP / Director", "Manager", "Individual Contributor"];
const LEAD_COUNT_OPTIONS = [5, 10, 15, 20];
const COMPANY_SIZE_OPTIONS = ["1–10", "11–50", "51–200", "201–500", "500–1000", "1000+"];

// ════════════════════════════════════════════════════════════════════════════
// SCHEDULE MANAGER — Autopilot prospecting
// ════════════════════════════════════════════════════════════════════════════
const FREQ_LABELS = { daily:"Daily", weekly:"Weekly", biweekly:"Every 2 weeks", monthly:"Monthly" };
const FREQ_DESC   = { daily:"Fresh leads every morning", weekly:"New leads every Monday", biweekly:"Bi-weekly refresh", monthly:"Monthly pipeline top-up" };

function ScheduleManager({ user, userInfo, onAddLeads, onSwitchToLeads, onNeedsAuth }) {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [running, setRunning]     = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm]           = useState({ label:"", product:"", target:"", frequency:"weekly", leadCount:10, industry:"", seniority:[] });
  const [creating, setCreating]   = useState(false);
  const [err, setErr]             = useState("");

  useEffect(() => {
    if (!user) return;
    api.getSchedules().then(r => { setSchedules(r.schedules || []); setLoading(false); });
  }, [user]);

  if (!user) return (
    <div className="flex flex-col items-center justify-center h-full text-center p-8 bg-gray-50">
      <h2 className="font-display text-2xl font-semibold tracking-tight text-sage-950 mb-2">Autopilot prospecting</h2>
      <p className="text-gray-500 text-sm max-w-sm mb-6">Wake up to fresh leads every morning. Set up your ideal customer profile once, and Virtuosoh automatically finds and enriches new contacts on your schedule.</p>
      <button onClick={onNeedsAuth} className="bg-sage-700 hover:bg-sage-800 text-white font-semibold px-6 py-3 rounded-xl text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-sage-300">Sign in to get started →</button>
    </div>
  );

  const handleCreate = async () => {
    if (!form.product.trim() || !form.target.trim()) { setErr("Product and target are required."); return; }
    setCreating(true); setErr("");
    const res = await api.createSchedule({
      ...form,
      seniority: form.seniority.length > 0 ? form.seniority.join(", ") : null,
    });
    setCreating(false);
    if (res.error) { setErr(res.error); return; }
    setSchedules(s => [res.schedule, ...s]);
    setShowCreate(false);
    setForm({ label:"", product:"", target:"", frequency:"weekly", leadCount:10, industry:"", seniority:[] });
  };

  const handleRun = async (id) => {
    setRunning(id);
    const res = await api.runSchedule(id);
    setRunning(null);
    if (res.error) { alert(res.error); return; }
    // Refresh schedule list
    api.getSchedules().then(r => setSchedules(r.schedules || []));
    if (res.leads?.length > 0) {
      const mapped = res.leads.map(l => ({
        id: uid(), source:"Outbound", date: now(), notes:"", status:"New",
        ...l,
        confidenceEmail: l.confidence?.email||null,
        confidencePhone: l.confidence?.phone||null,
        confidenceLinkedin: l.confidence?.linkedin||null,
        dataSources: l.sources||[],
      }));
      onAddLeads(mapped);
      onSwitchToLeads();
    }
  };

  const handleToggle = async (id) => {
    await api.toggleSchedule(id);
    api.getSchedules().then(r => setSchedules(r.schedules || []));
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this scheduled search?")) return;
    await api.deleteSchedule(id);
    setSchedules(s => s.filter(x => x.id !== id));
  };

  const SENIORITY_OPTS = ["C-Suite","VP/Director","Manager","Team Lead","Individual Contributor"];

  return (
    <div className="flex flex-col h-full bg-gray-50">
      <div className="flex-1 overflow-auto p-6 max-w-3xl mx-auto w-full">

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-display text-2xl font-semibold tracking-tight text-sage-950">Autopilot</h1>
            <p className="text-sm text-gray-500 mt-0.5">Wake up to fresh leads every morning — zero manual effort.</p>
          </div>
          {!showCreate && (
            <button onClick={() => setShowCreate(true)} className="bg-sage-700 hover:bg-sage-800 text-white font-semibold px-4 py-2.5 rounded-xl text-sm transition-colors flex items-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-sage-300">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 5v14m7-7H5"/></svg> New schedule
            </button>
          )}
        </div>

        {/* How it works banner — shown when no schedules */}
        {!loading && schedules.length === 0 && !showCreate && (
          <div className="bg-gradient-to-br from-sage-50 to-emerald-50 border border-sage-200 rounded-2xl p-6 mb-6 text-center">
            <h3 className="font-display text-lg font-semibold text-sage-950 mb-1">Start your day with a full pipeline</h3>
            <p className="text-sm text-gray-500 mb-4 max-w-sm mx-auto">Tell Virtuosoh who you're after. Every morning (or week), it automatically finds fresh contacts and adds them to your board — ready to outreach.</p>
            <div className="flex justify-center gap-6 text-xs text-gray-500 mb-5">
              {[["1","Set it once"],["2","Runs automatically"],["3","Leads waiting for you"]].map(([num,label])=>(
                <div key={label} className="flex flex-col items-center gap-1"><span className="w-5 h-5 rounded-full bg-sage-100 text-sage-700 flex items-center justify-center text-[10px] font-bold">{num}</span><span>{label}</span></div>
              ))}
            </div>
            <button onClick={() => setShowCreate(true)} className="bg-sage-700 hover:bg-sage-800 text-white font-semibold px-6 py-3 rounded-xl text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-sage-300">
              Set up your first schedule →
            </button>
          </div>
        )}

        {/* Create form */}
        {showCreate && (
          <div className="bg-white rounded-2xl border border-sage-200 shadow-sm p-6 mb-6">
            <h2 className="font-display text-lg font-semibold text-sage-950 mb-4">New scheduled search</h2>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-semibold text-sage-700 block mb-1.5">Label <span className="text-gray-400 font-normal">(optional)</span></label>
                <input value={form.label} onChange={e=>setForm(f=>({...f,label:e.target.value}))} placeholder="e.g. VP Sales at SaaS startups" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-sage-500 focus:ring-2 focus:ring-sage-200"/>
              </div>
              <div>
                <label className="text-xs font-semibold text-sage-700 block mb-1.5">What are you selling? <span className="text-red-400">*</span></label>
                <input value={form.product} onChange={e=>setForm(f=>({...f,product:e.target.value}))} placeholder="AI-powered sales automation for B2B SaaS" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-sage-500 focus:ring-2 focus:ring-sage-200"/>
              </div>
              <div>
                <label className="text-xs font-semibold text-sage-700 block mb-1.5">Who do you want to reach? <span className="text-red-400">*</span></label>
                <textarea value={form.target} onChange={e=>setForm(f=>({...f,target:e.target.value}))} rows={2} placeholder="VP of Sales at Series A–C SaaS startups, 50–500 employees, US-based" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-sage-500 focus:ring-2 focus:ring-sage-200 resize-none"/>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-sage-700 block mb-1.5">How often?</label>
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(FREQ_LABELS).map(([key,label])=>(
                      <button key={key} onClick={()=>setForm(f=>({...f,frequency:key}))}
                        className={`py-2 px-3 rounded-xl text-xs font-semibold border transition-all text-left ${form.frequency===key?"bg-sage-600 text-white border-sage-600":"bg-gray-50 text-gray-600 border-gray-200 hover:border-sage-300"}`}>
                        <div>{label}</div>
                        <div className={`text-[10px] mt-0.5 ${form.frequency===key?"text-white/70":"text-gray-400"}`}>{FREQ_DESC[key]}</div>
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold text-sage-700 block mb-1.5">Leads per run</label>
                  <div className="flex flex-wrap gap-2">
                    {[5,10,15,20].map(n=>(
                      <button key={n} onClick={()=>setForm(f=>({...f,leadCount:n}))}
                        className={`flex-1 py-2 rounded-xl text-sm font-semibold border transition-all ${form.leadCount===n?"bg-sage-600 text-white border-sage-600":"bg-gray-50 text-gray-500 border-gray-200 hover:border-sage-300"}`}>{n}</button>
                    ))}
                  </div>
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-sage-700 block mb-1.5">Seniority <span className="text-gray-400 font-normal">(pick any)</span></label>
                <div className="flex flex-wrap gap-2">
                  {SENIORITY_OPTS.map(s=>{
                    const active = form.seniority.includes(s);
                    return <button key={s} onClick={()=>setForm(f=>({...f,seniority:active?f.seniority.filter(x=>x!==s):[...f.seniority,s]}))}
                      className={`px-3 py-1.5 rounded-xl text-xs font-semibold border transition-all ${active?"bg-sage-600 text-white border-sage-600":"bg-gray-50 text-gray-500 border-gray-200 hover:border-sage-300"}`}>
                      {active&&"✓ "}{s}
                    </button>;
                  })}
                </div>
              </div>
              {err && <p className="text-red-500 text-xs">{err}</p>}
              <div className="flex gap-3 pt-1">
                <button onClick={handleCreate} disabled={creating} className="flex-1 bg-sage-700 hover:bg-sage-800 text-white font-semibold py-3 rounded-xl text-sm transition-colors disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-sage-300">
                  {creating ? "Saving..." : "Save schedule →"}
                </button>
                <button onClick={()=>{setShowCreate(false);setErr("");}} className="px-4 py-3 border border-gray-200 text-gray-600 rounded-xl text-sm hover:bg-gray-50 transition-colors">Cancel</button>
              </div>
            </div>
          </div>
        )}

        {/* Schedule list */}
        {loading ? (
          <div className="text-center py-12 text-gray-400 text-sm">Loading schedules...</div>
        ) : (
          <div className="space-y-3">
            {schedules.map(s => (
              <div key={s.id} className={`bg-white rounded-2xl border shadow-sm p-5 transition-all ${s.active?"border-gray-200":"border-dashed border-gray-200 opacity-60"}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${s.active?"bg-green-500":"bg-gray-300"}`}></span>
                      <span className="font-semibold text-gray-900 text-sm truncate">{s.label || s.product.slice(0,50)}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-semibold border ${s.active?"bg-green-50 text-green-700 border-green-200":"bg-gray-100 text-gray-500 border-gray-200"}`}>{s.active?"Active":"Paused"}</span>
                    </div>
                    <p className="text-xs text-gray-500 truncate mb-2">{s.target}</p>
                    <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                      <span className="flex items-center gap-1"><svg className="w-3.5 h-3.5 text-sage-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h5M20 20v-5h-5M5 9a7 7 0 0111-3m3 9a7 7 0 01-11 3"/></svg>{FREQ_LABELS[s.frequency]}</span>
                      <span className="flex items-center gap-1"><svg className="w-3.5 h-3.5 text-sage-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-1a4 4 0 00-3-3.87M9 20H4v-1a4 4 0 013-3.87m6-1.13a4 4 0 10-4-4 4 4 0 004 4z"/></svg>{s.lead_count} leads/run</span>
                      <span className="flex items-center gap-1"><svg className="w-3.5 h-3.5 text-sage-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>{s.runs_completed||0} runs · {s.leads_found||0} leads found</span>
                      {s.next_run_at && s.active && <span className="flex items-center gap-1"><svg className="w-3.5 h-3.5 text-sage-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>Next: {new Date(s.next_run_at).toLocaleDateString("en-US",{weekday:"short",month:"short",day:"numeric"})}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button onClick={()=>handleRun(s.id)} disabled={running===s.id} title="Run now"
                      className="bg-sage-50 hover:bg-sage-100 text-sage-700 border border-sage-200 text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1">
                      {running===s.id?<svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h5M20 20v-5h-5M5 9a7 7 0 0111-3m3 9a7 7 0 01-11 3"/></svg>:<svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>} {running===s.id?"Running...":"Run now"}
                    </button>
                    <button onClick={()=>handleToggle(s.id)} title={s.active?"Pause":"Resume"}
                      className="text-gray-500 hover:text-gray-800 border border-gray-200 text-xs px-3 py-1.5 rounded-lg hover:bg-gray-50 transition-colors inline-flex items-center gap-1.5">
                      {s.active?<><svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M6 5h4v14H6zM14 5h4v14h-4z"/></svg>Pause</>:<><svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>Resume</>}
                    </button>
                    <button onClick={()=>handleDelete(s.id)} title="Delete" aria-label="Delete schedule" className="text-gray-400 hover:text-red-500 transition-colors px-2"><svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/></svg></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// OUTBOUND SDR
// ════════════════════════════════════════════════════════════════════════════
function OutboundSDR({ addLeads, addLead, user, userInfo, guestUsed, onGuestUse, onGuestHitLimit, onUsageChange, onSwitchToLeads, onSchedule, onPhaseChange, onShowBuyPacks }) {
  const [phase, setPhase]         = useState("input");
  const setPhaseAndNotify = (p) => { setPhase(p); onPhaseChange?.(p); };
  const [product, setProduct]     = useState("");
  const [target, setTarget]       = useState("");
  const [leadCount, setLeadCount]       = useState(10);
  const [industry, setIndustry]         = useState("");
  const [seniority, setSeniority]       = useState([]);
  const [companySizes, setCompanySizes] = useState([]);
  const [showRefine, setShowRefine] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [icp, setIcp]             = useState(null);
  const [foundLeads, setFoundLeads] = useState([]);
  const [gate, setGate]           = useState(null);
  const [error, setError]         = useState(null);
  const leadsRef                  = useRef([]);

  // Saved searches
  const [savedSearches, setSavedSearches] = useState([]);
  const [showSaved, setShowSaved]         = useState(false);

  // Load saved searches from DB (if logged in) or localStorage (guest fallback)
  useEffect(() => {
    if (user) {
      api.listSavedSearches().then(r => {
        if (r.searches) setSavedSearches(r.searches);
      }).catch(() => {});
    } else {
      try {
        const saved = JSON.parse(localStorage.getItem("lw_saved_searches") || "[]");
        setSavedSearches(saved);
      } catch {}
    }
  }, [user]);

  // Restore last search on mount
  useEffect(() => {
    try {
      const last = JSON.parse(localStorage.getItem("lw_last_search") || "null");
      if (last) {
        if (last.product) setProduct(last.product);
        if (last.target)  setTarget(last.target);
        if (last.leadCount) setLeadCount(last.leadCount);
        if (last.industry)  setIndustry(last.industry);
        if (last.seniority?.length) setSeniority(last.seniority);
        if (last.companySizes?.length) setCompanySizes(last.companySizes);
      }
    } catch {}
  }, []);

  const currentSearch = () => ({ product, target, leadCount, industry, seniority, companySizes });

  const saveSearch = async (name) => {
    const entry = { name, ...currentSearch() };
    if (user) {
      const r = await api.saveSearch(entry).catch(() => null);
      if (r?.search) {
        setSavedSearches(prev => [r.search, ...prev.filter(s => s.name !== name)]);
      }
    } else {
      const local = { ...entry, id: name, savedAt: Date.now() };
      const updated = [local, ...savedSearches.filter(s => s.name !== name)].slice(0, 10);
      setSavedSearches(updated);
      localStorage.setItem("lw_saved_searches", JSON.stringify(updated));
    }
  };

  const loadSearch = (s) => {
    setProduct(s.product || "");
    setTarget(s.target || "");
    setLeadCount(s.lead_count || s.leadCount || 10);
    setIndustry(s.industry || "");
    setSeniority(s.seniority || []);
    setCompanySizes(s.companySizes || s.company_sizes || []);
    setShowSaved(false);
  };

  const deleteSavedSearch = async (id, name) => {
    if (user) {
      await api.deleteSavedSearch(id).catch(() => {});
      setSavedSearches(prev => prev.filter(s => s.id !== id));
    } else {
      const updated = savedSearches.filter(s => s.name !== name);
      setSavedSearches(updated);
      localStorage.setItem("lw_saved_searches", JSON.stringify(updated));
    }
  };

  const promptSave = () => {
    if (!product.trim() || !target.trim()) return;
    const name = window.prompt("Name this search:", product.slice(0, 40));
    if (name?.trim()) saveSearch(name.trim());
  };

  // Close saved dropdown on outside click
  useEffect(() => {
    if (!showSaved) return;
    const handler = () => setShowSaved(false);
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showSaved]);

  const start = async (overrides = {}) => {
    const productVal = (overrides.product || product).trim();
    const targetVal  = (overrides.target  || target).trim();
    const industryVal = overrides.industry !== undefined ? overrides.industry : industry;
    if (!user) { onGuestHitLimit(); return; }
    if (!productVal || !targetVal) { setShowValidation(true); return; }

    // If coming from brainstorm, sync visible form fields
    if (overrides.product) setProduct(overrides.product);
    if (overrides.target)  setTarget(overrides.target);
    if (overrides.industry !== undefined) setIndustry(overrides.industry);

    const lim = await api.checkUsage();
    if (!lim.allowed) { setGate(lim.stage); return; }

    setPhaseAndNotify("targeting"); setFoundLeads([]); leadsRef.current = []; setIcp(null); setError(null);

    // Auto-save last search for next visit
    try { localStorage.setItem("lw_last_search", JSON.stringify(currentSearch())); } catch {}

    try {
      await new Promise(r => setTimeout(r, 1200));
      setPhaseAndNotify("enriching");
      const opts = {
        leadCount,
        ...(industryVal.trim() ? { industry: industryVal.trim() } : {}),
        ...(seniority.length > 0 ? { seniority: seniority.join(", ") } : {}),
        ...(companySizes.length > 0 ? { companySize: companySizes.join(", ") } : {}),
      };
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 58000);
      const result = await fetch("/api/enrich/search", {
        method: "POST",
        headers: apiHeaders(),
        body: JSON.stringify({ product: productVal, target: targetVal, ...opts }),
        signal: controller.signal,
      }).then(r => r.json()).catch(e => ({ error: e.name === "AbortError" ? "Search timed out — try fewer leads or a broader target." : "Search failed. Please try again." }));
      clearTimeout(timeout);

      if (result.error) { setError(result.error); setPhase("done"); return; }

      if (result.icp) setIcp(result.icp);

      if (result.leads && result.leads.length > 0) {
        for (const lead of result.leads) {
          const chk = await api.checkUsage();
          if (!chk.allowed) { setGate(chk.stage); break; }

          const full = {
            id: uid(), source: "Outbound", date: now(), notes: "", status: "New",
            ...lead,
            confidenceEmail: lead.confidence?.email || null,
            confidencePhone: lead.confidence?.phone || null,
            confidenceLinkedin: lead.confidence?.linkedin || null,
            dataSources: lead.sources || [],
          };
          await new Promise(r => setTimeout(r, 480));
          await api.addLeads([full]);
          leadsRef.current = [...leadsRef.current, full];
          setFoundLeads([...leadsRef.current]);
        }
        onUsageChange();
      }
    } catch(e) { console.error(e); setError("Something went wrong. Please try again."); }

    setPhaseAndNotify("done");
  };

  const reset = () => { setPhaseAndNotify("input"); setProduct(""); setTarget(""); setIcp(null); setFoundLeads([]); leadsRef.current = []; setError(null); setShowRefine(false); setCompanySizes([]); setShowValidation(false); };

  const u   = userInfo || {};
  const contactsLeft = user ? (u.remaining != null ? u.remaining : "...") : Math.max(0,FREE_LIMIT-guestUsed);

  return (
    <div className="flex flex-col h-full bg-gray-50">
      {gate==="email_gate" && user && <EmailGate userEmail={user.email} onUnlocked={async e=>{await api.claimEmailBonus(e);setGate(null);onUsageChange();}}/>}
      {gate==="paywall"    && user && <Paywall userEmail={user.email} onPaid={()=>{setGate(null);onUsageChange();}} onBuyPacks={onShowBuyPacks ? ()=>{setGate(null);onShowBuyPacks();} : undefined} onClose={()=>setGate(null)}/>}

      <div className="flex-1 overflow-auto p-6">

        {/* ── Input phase — conversational copilot feel ── */}
        {phase==="input" && (
          <div className="max-w-xl mx-auto pt-6">

            {/* Greeting header */}
            <div className="mb-6">
              <div className="flex items-center justify-between gap-2 mb-1">
                  <h1 className="font-display text-2xl font-semibold tracking-tight text-sage-950">Let's find your next customers</h1>
                  {savedSearches.length > 0 && (
                    <div className="relative">
                      <button
                        onClick={() => setShowSaved(p => !p)}
                        className="text-xs text-gray-600 border border-gray-200 bg-white hover:bg-gray-50 px-3 py-1.5 rounded-lg flex items-center gap-1.5 whitespace-nowrap"
                      >
                        Saved ({savedSearches.length})
                      </button>
                      {showSaved && (
                        <div className="absolute right-0 top-8 w-72 bg-white border border-gray-200 rounded-xl shadow-lg z-20 overflow-hidden">
                          <div className="px-3 py-2 border-b border-gray-100 text-xs font-semibold text-gray-500 uppercase tracking-wide">Saved searches</div>
                          {savedSearches.map((s, i) => (
                            <div key={i} className="flex items-center gap-2 px-3 py-2.5 hover:bg-gray-50 group">
                              <button onClick={() => loadSearch(s)} className="flex-1 text-left">
                                <div className="text-sm font-medium text-gray-800 truncate">{s.name}</div>
                                <div className="text-xs text-gray-400 truncate">{s.target?.slice(0, 50)}{s.target?.length > 50 ? "…" : ""}</div>
                              </button>
                              <button onClick={() => deleteSavedSearch(s.id, s.name)} aria-label="Delete saved search" className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 p-1 transition-opacity"><svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/></svg></button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <p className="text-gray-400 text-sm mt-0.5">Tell me what you're selling and who you want to reach — I'll find real contacts and their info, automatically.</p>
            </div>

            <div className="space-y-3">
              {/* Q1 */}
              <div className={`bg-white rounded-2xl border shadow-sm p-5 transition-all focus-within:ring-2 focus-within:ring-sage-200 focus-within:border-sage-400 ${showValidation && !product.trim() ? "border-red-300 ring-2 ring-red-100" : "border-gray-200"}`}>
                <label className="block mb-2"><span className="text-[10px] font-medium text-gray-400 mr-2">Step 1</span><span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">What are you selling?</span></label>
                <input
                  value={product} onChange={e=>{ setProduct(e.target.value); setShowValidation(false); }}
                  onKeyDown={e=>e.key==="Enter"&&document.getElementById("lw-target-input")?.focus()}
                  placeholder="e.g. AI scheduling software for dental practices"
                  className="w-full text-sm text-gray-900 outline-none placeholder:text-gray-300 bg-transparent"
                />
                {showValidation && !product.trim() && <p className="text-xs text-red-500 mt-1.5">Required</p>}
              </div>

              {/* Q2 */}
              <div className={`bg-white rounded-2xl border shadow-sm p-5 transition-all focus-within:ring-2 focus-within:ring-sage-200 focus-within:border-sage-400 ${showValidation && !target.trim() ? "border-red-300 ring-2 ring-red-100" : "border-gray-200"}`}>
                <label className="block mb-2"><span className="text-[10px] font-medium text-gray-400 mr-2">Step 2</span><span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Who do you want to reach?</span></label>
                <textarea
                  id="lw-target-input"
                  value={target} onChange={e=>{ setTarget(e.target.value); setShowValidation(false); }}
                  rows={2}
                  placeholder="e.g. Practice managers at dental offices with 2+ locations, US-based"
                  className="w-full text-sm text-gray-900 outline-none placeholder:text-gray-300 resize-none bg-transparent"
                />
                {showValidation && !target.trim() && <p className="text-xs text-red-500 mt-1.5">Required</p>}
              </div>

              {/* Optional refinement — collapsed by default */}
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                <button
                  onClick={()=>setShowRefine(p=>!p)}
                  className="w-full flex items-center justify-between px-5 py-3.5 text-left hover:bg-gray-50 transition-colors"
                >
                  <span className="text-sm text-gray-500 flex items-center gap-2">
                    <svg className="w-4 h-4 text-sage-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.7}><path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
                    <span>Refine your search <span className="text-gray-400 text-xs">(optional)</span></span>
                  </span>
                  <svg className={`w-4 h-4 text-gray-400 transition-transform ${showRefine?"rotate-180":""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7"/></svg>
                </button>

                {showRefine && (
                  <div className="px-5 pb-5 space-y-5 border-t border-gray-100 pt-4">

                    {/* Lead count */}
                    <div>
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-2.5">How many leads do you need?</label>
                      <div className="flex gap-2">
                        {LEAD_COUNT_OPTIONS.map(n=>(
                          <button key={n} onClick={()=>setLeadCount(n)}
                            className={`flex-1 py-2 rounded-xl text-sm font-semibold border transition-all ${leadCount===n?"bg-gray-900 text-white border-gray-900 shadow-sm":"bg-gray-50 text-gray-500 border-gray-200 hover:border-gray-400 hover:text-gray-700"}`}>
                            {n}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Industry */}
                    <div>
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-2">Target industry?</label>
                      <input
                        value={industry} onChange={e=>setIndustry(e.target.value)}
                        placeholder="e.g. Healthcare, SaaS, Real Estate, Finance..."
                        className="w-full text-sm text-gray-900 bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 outline-none focus:border-sage-500 focus:ring-2 focus:ring-sage-200 placeholder:text-gray-300"
                      />
                    </div>

                    {/* Seniority */}
                    <div>
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1.5">Seniority level? <span className="text-gray-400 font-normal normal-case">(pick any)</span></label>
                      <div className="flex flex-wrap gap-2">
                        {SENIORITY_OPTIONS.filter(s=>s!=="Any level").map(s=>{
                          const active = seniority.includes(s);
                          return (
                            <button key={s}
                              onClick={()=>setSeniority(prev=>active?prev.filter(x=>x!==s):[...prev,s])}
                              className={`px-3.5 py-1.5 rounded-xl text-xs font-semibold border transition-all ${active?"bg-gray-900 text-white border-gray-900 shadow-sm":"bg-gray-50 text-gray-500 border-gray-200 hover:border-gray-400 hover:text-gray-700"}`}>
                              {active && <span className="mr-1">✓</span>}{s}
                            </button>
                          );
                        })}
                      </div>
                      {seniority.length>0&&<button onClick={()=>setSeniority([])} className="text-xs text-gray-400 hover:text-gray-600 mt-2">Clear selection</button>}
                    </div>

                    {/* Company size */}
                    <div>
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1.5">Company size? <span className="text-gray-400 font-normal normal-case">(pick any)</span></label>
                      <div className="flex flex-wrap gap-2">
                        {COMPANY_SIZE_OPTIONS.map(s => {
                          const active = companySizes.includes(s);
                          return (
                            <button key={s}
                              onClick={()=>setCompanySizes(prev=>active?prev.filter(x=>x!==s):[...prev,s])}
                              className={`px-3.5 py-1.5 rounded-xl text-xs font-semibold border transition-all ${active?"bg-gray-900 text-white border-gray-900 shadow-sm":"bg-gray-50 text-gray-500 border-gray-200 hover:border-gray-400 hover:text-gray-700"}`}>
                              {active && <span className="mr-1">✓</span>}{s}
                            </button>
                          );
                        })}
                      </div>
                      {companySizes.length>0&&<button onClick={()=>setCompanySizes([])} className="text-xs text-gray-400 hover:text-gray-600 mt-2">Clear selection</button>}
                    </div>
                  </div>
                )}
              </div>

              {/* CTA */}
              <div className="flex gap-2">
                <button
                  onTouchEnd={e=>{ e.preventDefault(); start(); }}
                  onClick={() => start()}
                  className={`flex-1 bg-sage-700 hover:bg-sage-800 text-white font-semibold py-3.5 rounded-xl transition-all text-sm shadow-sm flex items-center justify-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-sage-300 ${(!product.trim()||!target.trim())?"opacity-40":""}`}
                >
                  <span>Find {leadCount} leads</span>
                  <span>→</span>
                </button>
                {user && product.trim() && target.trim() && (
                  <>
                    <button
                      onClick={promptSave}
                      title="Save this search"
                      className="bg-white border border-gray-200 hover:border-sage-300 text-gray-500 hover:text-sage-600 font-semibold py-3.5 px-4 rounded-2xl transition-all text-sm"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z"/></svg>
                    </button>
                    <button
                      onClick={onSchedule}
                      title="Schedule this search to run automatically"
                      className="bg-white border border-gray-200 hover:border-gray-400 text-gray-600 hover:bg-gray-50 font-semibold py-3.5 px-4 rounded-2xl transition-all text-sm flex items-center gap-1.5 whitespace-nowrap"
                    >
                      <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><circle cx="12" cy="12" r="9"/><path strokeLinecap="round" strokeLinejoin="round" d="M12 7v5l3 3"/></svg>
                      <span className="hidden sm:inline">Schedule</span>
                    </button>
                  </>
                )}
              </div>

              {/* Social proof / how it works */}
              <div className="flex items-center justify-center gap-5 pt-1 text-xs text-gray-400">
                <span>ICP in seconds</span>
                <span>·</span>
                <span>Real companies</span>
                <span>·</span>
                <span>Verified contacts</span>
              </div>

              {/* Brainstorm panel */}
              <BrainstormPanel
                user={user}
                onNeedsAuth={onGuestHitLimit}
                onStartSearch={(seg, brainstormProduct) => {
                  const newTarget = `${seg.titles?.slice(0,2).join(" or ")} at ${seg.industries?.slice(0,2).join(" or ")} companies`;
                  const newProduct = product.trim() || brainstormProduct || seg.segment;
                  setSeniority([]);
                  start({ product: newProduct, target: newTarget, industry: seg.industries?.[0] || "" });
                }}
              />
            </div>
          </div>
        )}

        {/* ── Agent progress phases ── */}
        {phase!=="input" && (
          <div className="max-w-2xl mx-auto space-y-4">

            {/* Agent 1 card */}
            <div className={`bg-white rounded-2xl border ${phase==="targeting"?"border-sage-300 shadow-lg":"border-gray-200"} p-5 transition-all`}>
              <div className="flex items-center gap-3 mb-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${phase==="targeting"?"bg-sage-100 text-sage-600 animate-pulse":"bg-green-100 text-green-600"}`}>{phase==="targeting"?"1":"✓"}</div>
                <div>
                  <div className="font-semibold text-sm text-gray-900">Agent 1 · ICP Builder</div>
                  <div className="text-xs text-gray-400">{phase==="targeting"?"Analyzing your ideal customer profile...":"ICP defined"}</div>
                </div>
              </div>
              {icp && (
                <div className="grid grid-cols-2 gap-2 mt-3">
                  {[["Industry",icp.industry],["Titles",icp.titles?.slice(0,2).join(", ")],["Company Size",icp.companySizes?.join(", ")],["Geography",icp.geographies?.join(", ")]].map(([l,v])=>v?(
                    <div key={l} className="bg-gray-50 rounded-lg p-2.5"><div className="text-xs text-gray-400">{l}</div><div className="text-xs font-medium text-gray-800 mt-0.5">{v}</div></div>
                  ):null)}
                </div>
              )}
            </div>

            {/* Agent 2 card */}
            {(phase==="enriching"||phase==="done") && (
              <div className={`bg-white rounded-2xl border ${phase==="enriching"?"border-emerald-300 shadow-lg":"border-gray-200"} p-5 transition-all`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${phase==="enriching"?"bg-emerald-100 text-emerald-600 animate-pulse":"bg-green-100 text-green-600"}`}>{phase==="done"?"✓":"2"}</div>
                    <div>
                      <div className="font-semibold text-sm text-gray-900">Agent 2 · Lead Researcher</div>
                      <div className="text-xs text-gray-400">{phase==="enriching"?"Enriching with real company data, websites, and email patterns...":""}</div>
                    </div>
                  </div>
                  {phase==="done" && <span className="text-xs bg-emerald-100 text-emerald-700 px-2.5 py-1 rounded-full font-semibold">{foundLeads.length} leads found</span>}
                </div>

                {/* Live lead feed */}
                <div className="space-y-2">
                  {foundLeads.map(lead=>(
                    <div key={lead.id} className="flex items-center gap-3 bg-gray-50 hover:bg-sage-50 rounded-xl px-3 py-2.5 transition-colors">
                      <div className="w-7 h-7 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">{lead.name?.[0]||"?"}</div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-semibold text-gray-900 truncate">{lead.name}</div>
                        <div className="text-xs text-gray-400 truncate">{lead.title} · {lead.company}</div>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        {lead._redacted ? (
                          <span className="text-xs text-gray-300 flex items-center gap-1"><svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/></svg>Locked</span>
                        ) : (<>
                          {lead.email      && <span className="text-green-500 text-xs" title={lead.email}>✉</span>}
                          {lead.phone      && <span className="text-blue-500  text-xs" title={lead.phone}>📞</span>}
                          {lead.linkedinUrl && <span className="text-sage-500 text-xs font-bold" title="LinkedIn">in</span>}
                        </>)}
                        <span className={`text-xs border px-1.5 py-0.5 rounded-full font-semibold ${STATUS_STYLE[lead.score]||STATUS_STYLE.Cold}`}>{lead.score}</span>
                      </div>
                    </div>
                  ))}
                  {phase==="enriching" && (
                    <div className="flex items-center gap-2 px-3 py-2">
                      <div className="flex gap-1">{[0,1,2].map(i=><div key={i} className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-bounce" style={{animationDelay:`${i*0.15}s`}}></div>)}</div>
                      <span className="text-xs text-gray-400">Searching for leads...</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Error state with retry */}
            {phase==="done" && error && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center text-red-500 flex-shrink-0 text-sm">!</div>
                <div className="flex-1">
                  <div className="text-sm font-medium text-red-800">{error}</div>
                  <p className="text-xs text-red-600 mt-1">This can happen if the AI service is temporarily busy. Give it another shot.</p>
                </div>
                <button onClick={() => start()} className="bg-red-600 hover:bg-red-700 text-white text-xs font-semibold px-4 py-2 rounded-lg transition-colors flex-shrink-0">Retry</button>
              </div>
            )}

            {/* Enrichment upgrade banner for free users */}
            {phase==="done" && !error && foundLeads.some(l=>l._redacted) && (
              <div className="bg-gradient-to-r from-sage-50 to-emerald-50 border border-sage-200 rounded-2xl p-5">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-sage-600 flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/></svg>
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-gray-900 text-sm mb-1">Unlock enriched contact data</div>
                    <p className="text-xs text-gray-500 mb-3">You found <strong>{foundLeads.length} leads</strong> — upgrade to reveal verified emails, phone numbers, and LinkedIn profiles for each one.</p>
                    <div className="flex flex-wrap gap-3 text-xs text-gray-400 mb-3">
                      <span className="flex items-center gap-1">✉ Verified emails</span>
                      <span className="flex items-center gap-1">📞 Phone numbers</span>
                      <span className="flex items-center gap-1 font-bold">in LinkedIn URLs</span>
                    </div>
                    <button onClick={()=>setGate("paywall")} className="bg-sage-700 hover:bg-sage-800 text-white text-sm font-semibold px-5 py-2.5 rounded-xl transition-colors">Upgrade from $9.99/mo →</button>
                  </div>
                </div>
              </div>
            )}

            {phase==="done" && !error && (
              <div className="flex gap-3">
                <button onClick={reset} className="flex-1 bg-sage-700 hover:bg-sage-800 text-white font-semibold py-3 rounded-xl text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-sage-300">Search again</button>
                <button onClick={onSwitchToLeads} className="flex-1 bg-white border border-gray-200 text-gray-700 font-semibold py-3 rounded-xl text-sm hover:border-sage-300 hover:text-sage-600 transition-colors">View leads →</button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// HELP CENTER
// ════════════════════════════════════════════════════════════════════════════
function FAQCategoryIcon({ icon }) {
  const cls = "w-4 h-4";
  if (icon === "rocket") return (
    <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 20V5.5" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 6h9.5c1 0 1.5 1.2.8 1.9L14.8 9.5l1.5 1.6c.7.7.2 1.9-.8 1.9H6" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 20h5" />
    </svg>
  );
  if (icon === "card") return (
    <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
      <rect x="3" y="5" width="18" height="14" rx="2.5" />
      <path strokeLinecap="round" d="M3 10h18" />
      <path strokeLinecap="round" d="M7 15h3" />
    </svg>
  );
  if (icon === "chart") return (
    <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 19h16" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M7 16V9" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 16V5" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M17 16v-3" />
    </svg>
  );
  if (icon === "spark") return (
    <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8L12 3z" />
    </svg>
  );
  if (icon === "code") return (
    <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 8l-4 4 4 4" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M16 8l4 4-4 4" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M13 5l-2 14" />
    </svg>
  );
  return null;
}

const FAQS = [
  {
    category: "Getting Started",
    icon: "rocket",
    items: [
      {
        q: "What is Virtuosoh?",
        a: "Virtuosoh is an AI-powered B2B lead generation tool. You describe your product and ideal customer, and our AI finds real matching contacts — complete with verified emails, phone numbers, and LinkedIn profiles.",
      },
      {
        q: "How do I find my first leads?",
        a: "Go to the Prospector tab, describe what you're selling and who you want to reach, then click 'Find Leads'. The AI will identify matching prospects and enrich their contact info automatically. Most searches take under 30 seconds.",
      },
      {
        q: "Do I need a credit card to sign up?",
        a: "No. The free plan gives you 20 contacts per month with no credit card required. You can upgrade anytime when you're ready.",
      },
    ],
  },
  {
    category: "Plans & Billing",
    icon: "card",
    items: [
      {
        q: "What's included in the free plan?",
        a: "The free plan includes 20 contacts per month, plus a 5-contact signup bonus and a 5-contact bonus for verifying your email. Contact details (email, phone, LinkedIn) are visible on paid plans.",
      },
      {
        q: "What are the paid plans?",
        a: "Starter ($9.99/mo) — 50 enriched contacts. Growth ($29.99/mo) — 500 contacts. Scale ($59.99/mo) — 2,000 contacts. All paid plans include verified emails, phone numbers, LinkedIn URLs, and confidence scoring.",
      },
      {
        q: "How do I cancel my subscription?",
        a: "Click your name in the bottom-left corner and select 'Manage billing'. This opens the Stripe billing portal where you can cancel your subscription. You won't be charged again and you'll keep access until the end of your current billing period.",
      },
      {
        q: "How do I delete my account?",
        a: "Click your name in the bottom-left corner and select 'Delete account'. You'll need to type DELETE MY ACCOUNT to confirm — this permanently removes all your leads, searches, and account data and cannot be undone.",
      },
      {
        q: "When do my contacts reset?",
        a: "Your contact count resets to 0 at the start of each billing cycle. Unused contacts don't roll over.",
      },
    ],
  },
  {
    category: "Lead Data",
    icon: "chart",
    items: [
      {
        q: "How accurate is the contact data?",
        a: "Each contact shows a confidence badge next to their email and phone number — 'Verified' (MX-validated), 'Pattern' (common format match), or 'Guessed' (best estimate). Verified contacts have the highest deliverability.",
      },
      {
        q: "Where does the data come from?",
        a: "We use a multi-source approach: company websites, LinkedIn public data, Google Search, and email pattern validation. This gives better coverage than single-database tools.",
      },
      {
        q: "Can I edit lead information?",
        a: "Yes. Click any field in the lead drawer to edit it. You can also update the lead score (Hot/Warm/Cold/Qualified) and status, add notes, and set follow-up dates.",
      },
    ],
  },
  {
    category: "Features",
    icon: "spark",
    items: [
      {
        q: "What is Autopilot?",
        a: "Autopilot lets you schedule recurring searches so fresh leads arrive automatically — daily, weekly, biweekly, or monthly. Set it up once and come back to new leads every morning. Free accounts can have 3 active schedules; paid accounts up to 20.",
      },
      {
        q: "What are intent signals?",
        a: "Intent signals show recent activity for a company — funding rounds, new hires, leadership changes, product launches, and news. Open a lead's drawer and click 'Scan' to see signals. This helps you reach out at the right moment.",
      },
      {
        q: "How does the AI email generator work?",
        a: "In any lead's drawer, click 'Generate Email' to get a personalized cold email draft. Choose a tone (Professional, Friendly, or Direct) and the AI writes a subject line, body, and follow-up message tailored to that specific lead.",
      },
      {
        q: "What is the Brainstorm panel?",
        a: "Describe your product in the Prospector and scroll down to the Brainstorm panel. The AI will recommend your best ICP segments — with ideal titles, industries, company sizes, pain points, and outreach angles.",
      },
      {
        q: "What are saved searches?",
        a: "After filling in the Prospector, click the 💾 button to save your search with a name. Saved searches sync to your account so they're available on any device. Load them instantly from the 📂 dropdown.",
      },
    ],
  },
  {
    category: "Developer & API",
    icon: "code",
    items: [
      {
        q: "Does Virtuosoh have an API?",
        a: "Yes. Go to the Developer tab to generate an API key. You can list, create, update, and delete leads via REST API using your key in an X-API-Key header.",
      },
      {
        q: "Can I embed the chat widget on my website?",
        a: "Yes. Generate an API key in the Developer tab, then paste the one-line script tag on any website. The chat widget lets your visitors talk to an AI assistant that qualifies them as inbound leads directly into your Virtuosoh board.",
      },
      {
        q: "What are webhooks?",
        a: "Webhooks let you receive real-time notifications when new leads are created. Set up a webhook URL in the Developer tab and we'll POST a signed JSON payload to your server every time a lead comes in.",
      },
    ],
  },
];

function HelpCenter() {
  const [open, setOpen] = useState({});
  const [search, setSearch] = useState("");

  const toggle = (key) => setOpen(prev => ({ ...prev, [key]: !prev[key] }));

  const filtered = FAQS.map(cat => ({
    ...cat,
    items: cat.items.filter(item =>
      !search.trim() ||
      item.q.toLowerCase().includes(search.toLowerCase()) ||
      item.a.toLowerCase().includes(search.toLowerCase())
    ),
  })).filter(cat => cat.items.length > 0);

  return (
    <div className="flex-1 overflow-auto p-6">
      <div className="max-w-2xl mx-auto space-y-8">

        {/* Header */}
        <div>
          <h1 className="font-display text-xl font-semibold text-sage-950">Help Center</h1>
          <p className="text-sm text-gray-500 mt-1">Everything you need to get the most out of Virtuosoh.</p>
        </div>

        {/* Search */}
        <div className="relative">
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"><svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8"><circle cx="11" cy="11" r="6" /><path strokeLinecap="round" d="M20 20l-4.2-4.2" /></svg></span>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search FAQs..."
            className="w-full border border-gray-200 rounded-xl pl-10 pr-4 py-3 text-sm outline-none focus:border-sage-500 focus:ring-2 focus:ring-sage-200 bg-white"
          />
          {search && <button onClick={() => setSearch("")} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs">✕</button>}
        </div>

        {/* FAQ categories */}
        {filtered.length === 0 ? (
          <div className="text-center py-12 text-gray-400 text-sm">No results for "{search}"</div>
        ) : (
          filtered.map(cat => (
            <div key={cat.category} className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-sage-50 text-sage-700 border border-sage-100 flex items-center justify-center flex-shrink-0"><FAQCategoryIcon icon={cat.icon} /></span>
                <h2 className="text-sm font-bold text-gray-800">{cat.category}</h2>
              </div>
              <div className="divide-y divide-gray-100">
                {cat.items.map((item, i) => {
                  const key = `${cat.category}-${i}`;
                  const isOpen = open[key];
                  return (
                    <div key={i}>
                      <button
                        onClick={() => toggle(key)}
                        className="w-full text-left px-6 py-4 flex items-center justify-between gap-4 hover:bg-gray-50 transition-colors"
                      >
                        <span className="text-sm font-medium text-gray-800">{item.q}</span>
                        <svg className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7"/></svg>
                      </button>
                      {isOpen && (
                        <div className="px-6 pb-5">
                          <p className="text-sm text-gray-600 leading-relaxed">{item.a}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}

        {/* Footer */}
        <div className="text-center py-4">
          <p className="text-sm text-gray-400">Still have questions? Visit <a href="https://virtuosoh.com" className="text-sage-600 hover:underline">virtuosoh.com</a></p>
        </div>

      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// ADMIN PANEL
// ════════════════════════════════════════════════════════════════════════════
const ADMIN_PLANS = ["chatbot","free","paid","team"];
const PLAN_BADGE = { chatbot:"bg-teal-100 text-teal-700", free:"bg-gray-100 text-gray-600", paid:"bg-emerald-100 text-emerald-700", team:"bg-blue-100 text-blue-700" };

function AdminPanel({ user }) {
  const [clients, setClients]           = useState([]);
  const [loading, setLoading]           = useState(true);
  const [selected, setSelected]         = useState(null); // client being viewed
  const [clientLeads, setClientLeads]   = useState(null);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [showCreate, setShowCreate]     = useState(false);
  const [form, setForm]                 = useState({ name:"", email:"", plan:"chatbot" });
  const [creating, setCreating]         = useState(false);
  const [createErr, setCreateErr]       = useState("");
  const [createOk, setCreateOk]         = useState(false);
  const [search, setSearch]             = useState("");
  const [copied, setCopied]             = useState(null);

  useEffect(() => {
    api.adminListClients().then(r => { setClients(r.clients || []); setLoading(false); });
  }, []);

  const handleCreate = async () => {
    setCreateErr(""); setCreateOk(false);
    if (!form.name.trim() || !form.email.trim()) { setCreateErr("Name and email are required."); return; }
    setCreating(true);
    const r = await api.adminCreateClient(form);
    setCreating(false);
    if (r.error) { setCreateErr(r.error); return; }
    setCreateOk(true);
    setClients(prev => [r.client, ...prev]);
    setForm({ name:"", email:"", plan:"chatbot" });
    setTimeout(() => { setShowCreate(false); setCreateOk(false); }, 1800);
  };

  const handleSetPlan = async (clientId, plan) => {
    await api.adminSetPlan(clientId, plan);
    setClients(prev => prev.map(c => c.id===clientId ? {...c, plan} : c));
    if (selected?.id === clientId) setSelected(s => ({...s, plan}));
  };

  const handleViewLeads = async (client) => {
    setSelected(client); setClientLeads(null); setLeadsLoading(true);
    const r = await api.adminClientLeads(client.id);
    setClientLeads(r.leads || []); setLeadsLoading(false);
  };

  const handleResetPw = async (clientId) => {
    if (!window.confirm("Send a new temporary password to this client?")) return;
    const r = await api.adminResetPassword(clientId);
    if (r.ok) alert("New temporary password sent to client's email.");
  };

  const handleGenerateCheckout = async (clientId) => {
    const r = await api.adminGenerateCheckout(clientId);
    if (r.url) {
      await navigator.clipboard.writeText(r.url);
      alert("Payment link copied to clipboard! Send it to your client.");
    } else {
      alert(r.error || "Failed to generate payment link.");
    }
  };

  const copyEmbed = (clientId) => {
    const snippet = `<script src="${window.location.origin}/widget.js"\n  data-site-id="${clientId}"\n  data-company="Your Company Name"\n  data-description="Brief description of what you do"\n  data-color="#4f7c5f">\n</script>`;
    navigator.clipboard.writeText(snippet);
    setCopied(clientId); setTimeout(() => setCopied(null), 2000);
  };

  const filtered = clients.filter(c => {
    const q = search.toLowerCase();
    return !q || [c.name,c.email,c.plan].some(f => f?.toLowerCase().includes(q));
  });

  if (loading) return <div className="flex-1 flex items-center justify-center text-sm text-gray-400">Loading clients...</div>;

  // ── Client leads drawer ──
  if (selected) return (
    <div className="flex-1 flex flex-col overflow-hidden bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center gap-4">
        <button onClick={()=>{setSelected(null);setClientLeads(null);}} className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1.5">← Back</button>
        <div className="flex-1">
          <div className="font-bold text-gray-900">{selected.name}</div>
          <div className="text-xs text-gray-400">{selected.email} · <span className={`inline-block px-1.5 py-0.5 rounded-full text-xs font-semibold ${PLAN_BADGE[selected.plan]||PLAN_BADGE.free}`}>{selected.plan}</span></div>
        </div>
        <select value={selected.plan} onChange={e=>handleSetPlan(selected.id,e.target.value)}
          className="text-xs border border-gray-200 rounded-lg px-3 py-1.5 outline-none focus:border-sage-400">
          {ADMIN_PLANS.map(p=><option key={p} value={p}>{p}</option>)}
        </select>
        <button onClick={()=>handleResetPw(selected.id)} className="text-xs border border-gray-200 text-gray-600 px-3 py-1.5 rounded-lg hover:border-sage-300 hover:text-sage-700 transition-colors">Reset password</button>
        <button onClick={()=>handleGenerateCheckout(selected.id)} className="text-xs border border-teal-200 text-teal-700 px-3 py-1.5 rounded-lg hover:bg-teal-50 transition-colors">💳 Payment link</button>
        <button onClick={()=>copyEmbed(selected.id)} className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${copied===selected.id?"bg-green-600 text-white border-green-600":"bg-sage-600 text-white border-sage-600 hover:bg-sage-700"}`}>
          {copied===selected.id?"✓ Copied!":"Copy embed"}
        </button>
      </div>
      <div className="flex-1 overflow-auto p-6">
        {leadsLoading && <div className="text-sm text-gray-400 text-center py-12">Loading leads...</div>}
        {clientLeads !== null && clientLeads.length === 0 && (
          <div className="text-center py-16 text-gray-400">
            <div className="text-3xl mb-3">📭</div>
            <div className="text-sm">No leads yet for this client.</div>
          </div>
        )}
        {clientLeads !== null && clientLeads.length > 0 && (
          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
              <div className="text-sm font-semibold text-gray-700">{clientLeads.length} leads</div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-xs text-gray-400 border-b border-gray-100">{["Name","Company","Email","Source","Score","Status","Date"].map(h=><th key={h} className="text-left px-4 py-2.5 font-medium">{h}</th>)}</tr></thead>
                <tbody>
                  {clientLeads.map(l=>(
                    <tr key={l.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-2.5 font-medium text-gray-900 whitespace-nowrap">{l.name||"—"}</td>
                      <td className="px-4 py-2.5 text-gray-600 whitespace-nowrap">{l.company||"—"}</td>
                      <td className="px-4 py-2.5 text-gray-500 font-mono text-xs">{l.email||"—"}</td>
                      <td className="px-4 py-2.5"><span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${l.source==="Inbound"?"bg-teal-100 text-teal-700":"bg-emerald-100 text-emerald-700"}`}>{l.source}</span></td>
                      <td className="px-4 py-2.5"><span className={`text-xs px-2 py-0.5 rounded-full font-semibold border ${STATUS_STYLE[l.score]||STATUS_STYLE.Cold}`}>{l.score||"—"}</span></td>
                      <td className="px-4 py-2.5 text-gray-500 text-xs">{l.status||"—"}</td>
                      <td className="px-4 py-2.5 text-gray-400 text-xs whitespace-nowrap">{l.date||"—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // ── Main client list ──
  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-xl font-semibold text-sage-950">Admin</h1>
          <p className="text-sm text-gray-400 mt-0.5">{clients.length} client{clients.length!==1?"s":""} · only you can see this</p>
        </div>
        <div className="flex items-center gap-2">
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search clients..." className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm outline-none focus:border-sage-300 w-44"/>
          <button onClick={()=>{setShowCreate(p=>!p);setCreateErr("");setCreateOk(false);}} className="bg-sage-700 hover:bg-sage-800 text-white text-sm font-semibold px-4 py-2 rounded-xl transition-colors">+ New client</button>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-6 space-y-4 max-w-5xl mx-auto w-full">

        {/* Create client form */}
        {showCreate && (
          <div className="bg-white rounded-2xl border border-sage-200 p-5">
            <h2 className="text-sm font-bold text-gray-800 mb-4">Create client account</h2>
            <div className="grid grid-cols-3 gap-3 mb-3">
              <input value={form.name} onChange={e=>setForm(p=>({...p,name:e.target.value}))} placeholder="Full name" className="border border-gray-200 rounded-xl px-4 py-2 text-sm outline-none focus:border-sage-400"/>
              <input value={form.email} onChange={e=>setForm(p=>({...p,email:e.target.value}))} placeholder="Email address" type="email" className="border border-gray-200 rounded-xl px-4 py-2 text-sm outline-none focus:border-sage-400"/>
              <select value={form.plan} onChange={e=>setForm(p=>({...p,plan:e.target.value}))} className="border border-gray-200 rounded-xl px-4 py-2 text-sm outline-none focus:border-sage-400">
                {ADMIN_PLANS.map(p=><option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            {createErr && <p className="text-red-500 text-xs mb-3">{createErr}</p>}
            {createOk  && <p className="text-green-600 text-xs mb-3">✓ Account created — welcome email sent!</p>}
            <div className="flex gap-2">
              <button onClick={handleCreate} disabled={creating} className="bg-sage-600 text-white text-sm font-semibold px-5 py-2 rounded-xl hover:bg-sage-700 disabled:opacity-50 transition-colors">{creating?"Creating...":"Create & send welcome email"}</button>
              <button onClick={()=>setShowCreate(false)} className="text-sm text-gray-500 px-4 py-2 rounded-xl hover:bg-gray-50 transition-colors">Cancel</button>
            </div>
          </div>
        )}

        {/* Stats row */}
        <div className="grid grid-cols-4 gap-3">
          {[
            { label:"Total clients", value: clients.length },
            { label:"Chatbot plan",  value: clients.filter(c=>c.plan==="chatbot").length },
            { label:"Paid plan",     value: clients.filter(c=>c.plan==="paid").length },
            { label:"Total leads",   value: clients.reduce((s,c)=>s+c.leadCount,0) },
          ].map(stat=>(
            <div key={stat.label} className="bg-white rounded-2xl border border-gray-200 p-4 text-center">
              <div className="text-2xl font-bold text-gray-900">{stat.value}</div>
              <div className="text-xs text-gray-400 mt-0.5">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Client table */}
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          {filtered.length === 0 ? (
            <div className="text-center py-12 text-gray-400 text-sm">{search ? "No clients match your search." : "No clients yet."}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-xs text-gray-400 border-b border-gray-100">{["Client","Plan","Leads","Contacts used","Joined","Actions"].map(h=><th key={h} className="text-left px-4 py-3 font-medium">{h}</th>)}</tr></thead>
                <tbody>
                  {filtered.map(c=>(
                    <tr key={c.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors group">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{c.name||"—"}</div>
                        <div className="text-xs text-gray-400">{c.email}</div>
                      </td>
                      <td className="px-4 py-3">
                        <select value={c.plan} onChange={e=>handleSetPlan(c.id,e.target.value)}
                          className={`text-xs font-semibold px-2.5 py-1 rounded-full border-0 outline-none cursor-pointer ${PLAN_BADGE[c.plan]||PLAN_BADGE.free}`}>
                          {ADMIN_PLANS.map(p=><option key={p} value={p}>{p}</option>)}
                        </select>
                      </td>
                      <td className="px-4 py-3 text-gray-700 font-medium">{c.leadCount}</td>
                      <td className="px-4 py-3 text-gray-500">{c.contactsUsed}{c.bonusContacts>0&&<span className="text-xs text-teal-600 ml-1">+{c.bonusContacts}</span>}</td>
                      <td className="px-4 py-3 text-gray-400 text-xs">{c.createdAt ? new Date(c.createdAt).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"}) : "—"}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button onClick={()=>handleViewLeads(c)} className="text-xs text-sage-600 hover:text-sage-800 font-semibold">View leads</button>
                          <span className="text-gray-200">|</span>
                          <button onClick={()=>handleGenerateCheckout(c.id)} className="text-xs text-teal-600 hover:text-teal-800 font-semibold">Payment link</button>
                          <span className="text-gray-200">|</span>
                          <button onClick={()=>copyEmbed(c.id)} className="text-xs text-gray-500 hover:text-gray-700 font-semibold">{copied===c.id?"Copied!":"Copy embed"}</button>
                          <span className="text-gray-200">|</span>
                          <button onClick={()=>handleResetPw(c.id)} className="text-xs text-gray-500 hover:text-red-600 font-semibold">Reset pw</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// DEVELOPER PANEL
// ════════════════════════════════════════════════════════════════════════════
function DeveloperPanel({ user, onNeedsAuth }) {
  const [keys, setKeys] = useState([]);
  const [webhooks, setWebhooks] = useState([]);
  const [newKeyLabel, setNewKeyLabel] = useState("");
  const [newKeyResult, setNewKeyResult] = useState(null);
  const [webhookUrl, setWebhookUrl] = useState("");
  const [webhookSecret, setWebhookSecret] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    if (!user) return;
    Promise.all([api.listApiKeys(), api.listWebhooks()]).then(([k, w]) => {
      setKeys(k.keys || []);
      setWebhooks(w.webhooks || []);
      setLoading(false);
    });
  }, [user]);

  if (!user) return (
    <div className="flex-1 flex items-center justify-center">
      <div className="text-center">
        <div className="text-4xl mb-3">🔌</div>
        <h2 className="text-lg font-bold text-gray-800 mb-2">Developer Tools</h2>
        <p className="text-sm text-gray-500 mb-4">Sign in to access API keys, webhooks, and the embeddable widget.</p>
        <button onClick={onNeedsAuth} className="bg-sage-600 text-white px-6 py-2.5 rounded-xl text-sm font-semibold hover:bg-sage-700 transition-colors">Sign in</button>
      </div>
    </div>
  );

  const createKey = async () => {
    const r = await api.createApiKey(newKeyLabel.trim() || "Default");
    if (r.key) { setNewKeyResult(r.key); setNewKeyLabel(""); const k = await api.listApiKeys(); setKeys(k.keys || []); }
  };

  const revokeKey = async (id) => {
    if (!window.confirm("Revoke this API key? Any integrations using it will stop working.")) return;
    await api.revokeApiKey(id);
    setKeys(prev => prev.filter(k => k.id !== id));
  };

  const createWebhook = async () => {
    if (!webhookUrl.startsWith("https://")) return;
    const r = await api.createWebhook(webhookUrl, ["lead.created"]);
    if (r.webhook) { setWebhookSecret(r.secret); setWebhookUrl(""); const w = await api.listWebhooks(); setWebhooks(w.webhooks || []); }
  };

  const deleteWebhook = async (id) => {
    await api.deleteWebhook(id);
    setWebhooks(prev => prev.filter(w => w.id !== id));
  };

  const toggleWebhook = async (id) => {
    const r = await api.toggleWebhook(id);
    if (r.ok) setWebhooks(prev => prev.map(w => w.id === id ? { ...w, active: r.active } : w));
  };

  const testWebhook = async (id) => {
    const r = await api.testWebhook(id);
    alert(r.ok ? `Test sent! Response: ${r.status}` : `Failed: ${r.error}`);
  };

  const copyText = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  const widgetSnippet = user?.id
    ? `<script src="${window.location.origin}/widget.js"\n  data-site-id="${user.id}"\n  data-company="Your Company Name"\n  data-description="Brief description of what you do"\n  data-color="#4f7c5f">\n</script>`
    : "";

  if (loading) return <div className="flex-1 flex items-center justify-center text-sm text-gray-400">Loading...</div>;

  return (
    <div className="flex-1 overflow-auto p-6">
      <div className="max-w-2xl mx-auto space-y-8">

        {/* Header */}
        <div>
          <h1 className="font-display text-xl font-semibold text-sage-950">Developer Tools</h1>
          <p className="text-sm text-gray-500 mt-1">API keys, webhooks, and embeddable chat widget.</p>
        </div>

        {/* ── API Keys ── */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <h2 className="text-sm font-bold text-gray-800 uppercase tracking-wide mb-4">API Keys</h2>
          <p className="text-xs text-gray-500 mb-4">Use API keys to access Virtuosoh from your own apps. Keys are shown once — store them securely.</p>

          <div className="flex gap-2 mb-4">
            <input value={newKeyLabel} onChange={e => setNewKeyLabel(e.target.value)} placeholder="Key label (e.g. Production)" className="flex-1 border border-gray-200 rounded-xl px-4 py-2 text-sm outline-none focus:border-sage-400" onKeyDown={e=>e.key==="Enter"&&createKey()} />
            <button onClick={createKey} className="bg-sage-600 text-white px-5 py-2 rounded-xl text-sm font-semibold hover:bg-sage-700 transition-colors">Generate</button>
          </div>

          {newKeyResult && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
              <div className="text-xs font-bold text-amber-700 mb-1">Copy your API key now — you won't see it again:</div>
              <div className="flex items-center gap-2">
                <code className="text-xs bg-white border border-amber-200 rounded-lg px-3 py-2 flex-1 font-mono text-gray-800 select-all break-all">{newKeyResult}</code>
                <button onClick={() => copyText(newKeyResult, "newkey")} className="text-xs bg-white border border-amber-200 text-amber-700 px-3 py-2 rounded-lg hover:bg-amber-100 transition-colors whitespace-nowrap">{copied === "newkey" ? "Copied!" : "Copy"}</button>
              </div>
            </div>
          )}

          {keys.length === 0 ? (
            <p className="text-xs text-gray-400">No API keys yet.</p>
          ) : (
            <div className="space-y-2">
              {keys.map(k => (
                <div key={k.id} className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3 group">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-800">{k.label || "Unnamed"}</div>
                    <div className="text-xs text-gray-400 font-mono">{k.key_prefix}•••</div>
                  </div>
                  <div className="text-xs text-gray-400">{k.last_used ? `Used ${new Date(k.last_used).toLocaleDateString()}` : "Never used"}</div>
                  <button onClick={() => revokeKey(k.id)} className="opacity-0 group-hover:opacity-100 text-xs text-red-500 hover:text-red-700 font-semibold transition-opacity">Revoke</button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Webhooks ── */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <h2 className="text-sm font-bold text-gray-800 uppercase tracking-wide mb-4">Webhooks</h2>
          <p className="text-xs text-gray-500 mb-4">Get notified in real-time when new leads are created. We'll POST a JSON payload with an HMAC signature to your URL.</p>

          <div className="flex gap-2 mb-4">
            <input value={webhookUrl} onChange={e => setWebhookUrl(e.target.value)} placeholder="https://your-server.com/webhook" className="flex-1 border border-gray-200 rounded-xl px-4 py-2 text-sm outline-none focus:border-sage-400" onKeyDown={e=>e.key==="Enter"&&createWebhook()} />
            <button onClick={createWebhook} disabled={!webhookUrl.startsWith("https://")} className="bg-sage-600 text-white px-5 py-2 rounded-xl text-sm font-semibold hover:bg-sage-700 transition-colors disabled:opacity-40">Add</button>
          </div>

          {webhookSecret && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
              <div className="text-xs font-bold text-amber-700 mb-1">Webhook signing secret — copy it now:</div>
              <div className="flex items-center gap-2">
                <code className="text-xs bg-white border border-amber-200 rounded-lg px-3 py-2 flex-1 font-mono text-gray-800 select-all break-all">{webhookSecret}</code>
                <button onClick={() => copyText(webhookSecret, "secret")} className="text-xs bg-white border border-amber-200 text-amber-700 px-3 py-2 rounded-lg hover:bg-amber-100 transition-colors whitespace-nowrap">{copied === "secret" ? "Copied!" : "Copy"}</button>
              </div>
            </div>
          )}

          {webhooks.length === 0 ? (
            <p className="text-xs text-gray-400">No webhooks configured.</p>
          ) : (
            <div className="space-y-2">
              {webhooks.map(w => (
                <div key={w.id} className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3 group">
                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${w.active ? "bg-green-500" : "bg-gray-300"}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-mono text-gray-700 truncate">{w.url}</div>
                    <div className="text-xs text-gray-400">{w.events?.join(", ")}</div>
                  </div>
                  <button onClick={() => testWebhook(w.id)} className="text-xs text-sage-600 hover:text-sage-800 font-semibold">Test</button>
                  <button onClick={() => toggleWebhook(w.id)} className="text-xs text-gray-500 hover:text-gray-700 font-semibold">{w.active ? "Pause" : "Resume"}</button>
                  <button onClick={() => deleteWebhook(w.id)} className="opacity-0 group-hover:opacity-100 text-xs text-red-500 hover:text-red-700 font-semibold transition-opacity">Delete</button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Embed Widget ── */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <h2 className="text-sm font-bold text-gray-800 uppercase tracking-wide mb-4">Chat Widget</h2>
          <p className="text-xs text-gray-500 mb-4">Paste this one-liner on any website. Visitors chat with your AI assistant, and qualified leads flow into your board automatically — no API key needed.</p>
          <div className="relative">
            <pre className="bg-gray-900 text-green-400 text-xs rounded-xl p-4 overflow-x-auto font-mono leading-relaxed">{widgetSnippet}</pre>
            <button onClick={() => copyText(widgetSnippet, "widget")} className="absolute top-2 right-2 text-xs bg-gray-700 text-gray-300 px-3 py-1.5 rounded-lg hover:bg-gray-600 transition-colors">{copied === "widget" ? "Copied!" : "Copy"}</button>
          </div>
          <div className="mt-3 text-xs text-gray-400 space-y-1">
            <div><strong>data-site-id</strong> — your account ID (pre-filled above, do not change)</div>
            <div><strong>data-company</strong> — your company name, used by the AI in conversation</div>
            <div><strong>data-description</strong> — one sentence about what you do (improves AI responses)</div>
            <div><strong>data-color</strong> — hex accent color for the widget (optional)</div>
            <div><strong>data-position</strong> — "right" (default) or "left"</div>
          </div>
        </div>

        {/* ── API Reference ── */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <h2 className="text-sm font-bold text-gray-800 uppercase tracking-wide mb-4">API Reference</h2>
          <div className="space-y-3 text-xs">
            <div className="bg-gray-50 rounded-xl p-3">
              <div className="font-semibold text-gray-700 mb-1">List leads</div>
              <code className="text-gray-600">GET /api/v1/leads.list?limit=50&offset=0</code>
            </div>
            <div className="bg-gray-50 rounded-xl p-3">
              <div className="font-semibold text-gray-700 mb-1">Get lead</div>
              <code className="text-gray-600">GET /api/v1/leads.get?id=UUID</code>
            </div>
            <div className="bg-gray-50 rounded-xl p-3">
              <div className="font-semibold text-gray-700 mb-1">Create leads</div>
              <code className="text-gray-600">POST /api/v1/leads.create</code>
              <div className="text-gray-400 mt-1">Body: {"{"} "leads": [{"{"}"name":"...","email":"...","company":"...",...{"}"}] {"}"}</div>
            </div>
            <div className="bg-gray-50 rounded-xl p-3">
              <div className="font-semibold text-gray-700 mb-1">Update lead</div>
              <code className="text-gray-600">PATCH /api/v1/leads.update?id=UUID</code>
              <div className="text-gray-400 mt-1">Body: {"{"} "status":"Contacted", "score":"Hot" {"}"}</div>
            </div>
            <div className="bg-gray-50 rounded-xl p-3">
              <div className="font-semibold text-gray-700 mb-1">Delete lead</div>
              <code className="text-gray-600">DELETE /api/v1/leads.delete?id=UUID</code>
            </div>
            <div className="mt-3 text-gray-400">
              All requests require header: <code className="bg-gray-100 px-1.5 py-0.5 rounded">X-API-Key: lw_live_...</code>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// SETTINGS MODAL
// ════════════════════════════════════════════════════════════════════════════
function SettingsModal({ user, userInfo, onClose, onLogout, onDeleteAccount, onUpdateUser, onShowPaywall, onBillingPortal }) {
  const [section, setSection]       = useState("profile");
  // Profile
  const [name, setName]             = useState(user.name || "");
  const [nameLoading, setNameLoading] = useState(false);
  const [nameMsg, setNameMsg]       = useState(null);
  // Security
  const [currentPw, setCurrentPw]   = useState("");
  const [newPw, setNewPw]           = useState("");
  const [confirmPw, setConfirmPw]   = useState("");
  const [showCurPw, setShowCurPw]   = useState(false);
  const [showNewPw, setShowNewPw]   = useState(false);
  const [pwLoading, setPwLoading]   = useState(false);
  const [pwMsg, setPwMsg]           = useState(null);
  // Team
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamLoading, setTeamLoading] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteLoading, setInviteLoading] = useState(false);
  const [teamMsg, setTeamMsg]       = useState(null);

  const u          = userInfo || {};
  const isPaid     = user.plan === "paid";
  const isTeam     = user.plan === "team"  || user.plan === "admin";
  const isGoogle   = !user.email || user.plan; // refined below via password_hash — we surface this as a server error

  const PLAN_LABEL = { free: "Free", paid: "Pro", team: "Team", admin: "Admin" };
  const PLAN_COLOR = { free: "bg-gray-100 text-gray-600", paid: "bg-emerald-100 text-emerald-700", team: "bg-blue-100 text-blue-700", admin: "bg-amber-100 text-amber-700" };

  useEffect(() => {
    if (section === "team" && isTeam && teamMembers.length === 0 && !teamLoading) {
      setTeamLoading(true);
      api.listTeam().then(r => { setTeamMembers(r.members || []); setTeamLoading(false); });
    }
  }, [section, isTeam]); // eslint-disable-line

  const saveName = async () => {
    if (!name.trim()) return;
    setNameLoading(true); setNameMsg(null);
    const r = await api.updateProfile(name.trim());
    setNameLoading(false);
    if (r.error) { setNameMsg({ ok: false, text: r.error }); return; }
    setNameMsg({ ok: true, text: "Name saved!" });
    onUpdateUser(r.user);
    setTimeout(() => setNameMsg(null), 3000);
  };

  const submitChangePassword = async () => {
    setPwLoading(true); setPwMsg(null);
    const r = await api.changePassword(currentPw, newPw);
    setPwLoading(false);
    if (r.error) { setPwMsg({ ok: false, text: r.error }); return; }
    setPwMsg({ ok: true, text: "Password updated!" });
    setCurrentPw(""); setNewPw(""); setConfirmPw("");
    setTimeout(() => setPwMsg(null), 4000);
  };

  const inviteMember = async () => {
    if (!inviteEmail.trim()) return;
    setInviteLoading(true); setTeamMsg(null);
    const r = await api.inviteTeam(inviteEmail.trim());
    setInviteLoading(false);
    if (r.error) { setTeamMsg({ ok: false, text: r.error }); return; }
    setTeamMembers(p => [...p, r.member]);
    setInviteEmail("");
    setTeamMsg({ ok: true, text: "Member added!" });
    setTimeout(() => setTeamMsg(null), 3000);
  };

  const removeMember = async (id) => {
    await api.removeTeam(id);
    setTeamMembers(p => p.filter(m => m.id !== id));
  };

  const pwStrength = (() => {
    if (!newPw) return null;
    const s = [newPw.length>=8,/[A-Z]/.test(newPw),/[0-9]/.test(newPw),/[^A-Za-z0-9]/.test(newPw)].filter(Boolean).length;
    return [{label:"Too weak",color:"bg-red-400",w:"w-1/4"},{label:"Weak",color:"bg-orange-400",w:"w-2/4"},{label:"Good",color:"bg-yellow-400",w:"w-3/4"},{label:"Strong",color:"bg-green-500",w:"w-full"}][s-1] || {label:"Too weak",color:"bg-red-400",w:"w-1/4"};
  })();
  const canChangePw = currentPw && newPw.length >= 8 && newPw === confirmPw && !pwLoading;

  const EyeBtn = ({ show, onToggle }) => (
    <button type="button" onClick={onToggle} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors" tabIndex={-1}>
      {show
        ? <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
        : <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
      }
    </button>
  );

  const NAVS = [
    { id: "profile",  label: "Profile" },
    { id: "plan",     label: "Plan & Account" },
    ...(isTeam ? [{ id: "team", label: "Team" }] : []),
    { id: "security", label: "Security" },
  ];

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={e => e.target===e.currentTarget&&onClose()}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-sage-500 to-sage-600 flex items-center justify-center text-white text-sm font-bold">
              {user.name?.[0]?.toUpperCase() || "U"}
            </div>
            <div>
              <div className="text-sm font-bold text-gray-900">{user.name}</div>
              <div className="text-xs text-gray-400">{user.email}</div>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors text-lg leading-none">✕</button>
        </div>

        <div className="flex flex-1 min-h-0">
          {/* Sidebar nav */}
          <nav className="w-44 flex-shrink-0 border-r border-gray-100 py-3 px-2 space-y-0.5">
            {NAVS.map(n => (
              <button key={n.id} onClick={() => setSection(n.id)}
                className={`w-full flex items-center px-3 py-2 rounded-lg text-left text-xs font-medium transition-all ${section===n.id ? "bg-sage-50 text-sage-700 font-semibold" : "text-gray-500 hover:bg-gray-50 hover:text-gray-800"}`}>
                {n.label}
              </button>
            ))}
            <div className="pt-3 mt-1 border-t border-gray-100 space-y-0.5">
              <button onClick={() => { onClose(); onLogout(); }} className="w-full flex items-center px-3 py-2 rounded-lg text-left text-xs font-medium text-gray-500 hover:bg-gray-50 hover:text-gray-700 transition-all">
                Sign out
              </button>
              <button onClick={() => { onClose(); onDeleteAccount(); }} className="w-full flex items-center px-3 py-2 rounded-lg text-left text-xs font-medium text-red-400 hover:bg-red-50 hover:text-red-600 transition-all">
                Delete account
              </button>
            </div>
          </nav>

          {/* Content area */}
          <div className="flex-1 overflow-y-auto p-6">

            {/* ── Profile ── */}
            {section === "profile" && (
              <div className="space-y-5">
                <div>
                  <h3 className="text-sm font-bold text-sage-950 mb-0.5">Profile</h3>
                  <p className="text-xs text-gray-400">Your public-facing name on Virtuosoh.</p>
                </div>
                <div>
                  <label className="text-xs font-semibold text-sage-700 block mb-1">Full name</label>
                  <div className="flex gap-2">
                    <input className="flex-1 border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-sage-500 focus:ring-2 focus:ring-sage-200"
                      value={name} onChange={e=>setName(e.target.value)} onKeyDown={e=>e.key==="Enter"&&saveName()} placeholder="Your name"/>
                    <button onClick={saveName} disabled={nameLoading || !name.trim() || name.trim()===user.name}
                      className="px-4 py-2 bg-sage-700 hover:bg-sage-800 text-white text-xs font-semibold rounded-xl transition-all disabled:opacity-40 whitespace-nowrap">
                      {nameLoading ? "Saving…" : "Save"}
                    </button>
                  </div>
                  {nameMsg && <p className={`text-xs mt-1 ${nameMsg.ok ? "text-green-600" : "text-red-500"}`}>{nameMsg.text}</p>}
                </div>
                <div>
                  <label className="text-xs font-semibold text-sage-700 block mb-1">Email address</label>
                  <input readOnly value={user.email} className="w-full border border-gray-100 rounded-xl px-3 py-2 text-sm bg-gray-50 text-gray-500 cursor-not-allowed outline-none"/>
                  <p className="text-xs text-gray-400 mt-1">Email cannot be changed. Contact support if needed.</p>
                </div>
              </div>
            )}

            {/* ── Plan & Account ── */}
            {section === "plan" && (
              <div className="space-y-5">
                <div>
                  <h3 className="text-sm font-bold text-sage-950 mb-0.5">Plan & Account</h3>
                  <p className="text-xs text-gray-400">Your current subscription and usage.</p>
                </div>
                {/* Plan badge */}
                <div className="border border-gray-200 rounded-xl p-4 flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full ${PLAN_COLOR[user.plan] || PLAN_COLOR.free}`}>
                        {PLAN_LABEL[user.plan] || "Free"}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500">
                      {user.plan === "free" && "20 contacts/month · AI outreach · Saved searches"}
                      {user.plan === "paid" && "Unlimited contacts · All features · Priority support"}
                      {user.plan === "team" && "Team access · Unlimited contacts · All features"}
                      {user.plan === "admin" && "Full admin access"}
                    </p>
                  </div>
                  {!isPaid && (
                    <button onClick={() => { onClose(); onShowPaywall(); }} className="px-4 py-2 bg-sage-700 hover:bg-sage-800 text-white text-xs font-semibold rounded-xl transition-all shadow-sm whitespace-nowrap">
                      Upgrade →
                    </button>
                  )}
                </div>
                {/* Usage */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-semibold text-gray-600">Contacts used this month</span>
                    <span className="text-xs font-bold text-gray-900">{u.contactsUsed || 0}{u.monthlyLimit && u.monthlyLimit !== -1 ? ` / ${u.monthlyLimit}` : ""}</span>
                  </div>
                  {u.monthlyLimit && u.monthlyLimit !== -1 && (
                    <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-sage-500 rounded-full transition-all" style={{width:`${Math.min(100,((u.contactsUsed||0)/u.monthlyLimit)*100)}%`}}/>
                    </div>
                  )}
                </div>
                {isPaid && (
                  <button onClick={() => { onClose(); onBillingPortal(); }} className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 hover:bg-gray-50 text-xs font-semibold text-gray-700 rounded-xl transition-colors">
                    Manage billing & invoices
                  </button>
                )}
              </div>
            )}

            {/* ── Team ── */}
            {section === "team" && isTeam && (
              <div className="space-y-5">
                <div>
                  <h3 className="text-sm font-bold text-sage-950 mb-0.5">Team Members</h3>
                  <p className="text-xs text-gray-400">Invite teammates to access your Virtuosoh workspace.</p>
                </div>
                {/* Invite */}
                <div>
                  <label className="text-xs font-semibold text-sage-700 block mb-1">Invite by email</label>
                  <div className="flex gap-2">
                    <input type="email" placeholder="teammate@company.com"
                      className="flex-1 border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-sage-500 focus:ring-2 focus:ring-sage-200"
                      value={inviteEmail} onChange={e=>setInviteEmail(e.target.value)} onKeyDown={e=>e.key==="Enter"&&inviteMember()}/>
                    <button onClick={inviteMember} disabled={inviteLoading || !inviteEmail.trim()}
                      className="px-4 py-2 bg-sage-700 hover:bg-sage-800 text-white text-xs font-semibold rounded-xl transition-all disabled:opacity-40 whitespace-nowrap">
                      {inviteLoading ? "Adding…" : "Add member"}
                    </button>
                  </div>
                  {teamMsg && <p className={`text-xs mt-1 ${teamMsg.ok ? "text-green-600" : "text-red-500"}`}>{teamMsg.text}</p>}
                </div>
                {/* Members list */}
                <div>
                  <div className="text-xs font-semibold text-gray-600 mb-2">{teamMembers.length > 0 ? `${teamMembers.length} member${teamMembers.length>1?"s":""}` : "No members yet"}</div>
                  {teamLoading && <div className="text-xs text-gray-400">Loading…</div>}
                  {teamMembers.map(m => (
                    <div key={m.id} className="flex items-center justify-between py-2.5 border-b border-gray-100 last:border-0">
                      <div>
                        <div className="text-sm font-medium text-gray-800">{m.email}</div>
                        <div className="text-xs text-gray-400 mt-0.5">
                          {m.accepted_at ? "Active" : "Pending"} · {m.role}
                        </div>
                      </div>
                      <button onClick={() => removeMember(m.id)} className="text-xs text-red-400 hover:text-red-600 transition-colors px-2 py-1 rounded-lg hover:bg-red-50">
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Security ── */}
            {section === "security" && (
              <div className="space-y-5">
                <div>
                  <h3 className="text-sm font-bold text-sage-950 mb-0.5">Security</h3>
                  <p className="text-xs text-gray-400">Update your password.</p>
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs font-semibold text-sage-700 block mb-1">Current password</label>
                    <div className="relative">
                      <input type={showCurPw?"text":"password"} value={currentPw} onChange={e=>setCurrentPw(e.target.value)}
                        className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-sage-500 focus:ring-2 focus:ring-sage-200 pr-10"
                        placeholder="Enter current password"/>
                      <EyeBtn show={showCurPw} onToggle={() => setShowCurPw(v=>!v)}/>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-sage-700 block mb-1">New password</label>
                    <div className="relative">
                      <input type={showNewPw?"text":"password"} value={newPw} onChange={e=>setNewPw(e.target.value)}
                        className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-sage-500 focus:ring-2 focus:ring-sage-200 pr-10"
                        placeholder="Min. 8 characters"/>
                      <EyeBtn show={showNewPw} onToggle={() => setShowNewPw(v=>!v)}/>
                    </div>
                    {pwStrength && (
                      <div className="mt-1.5">
                        <div className="h-1 w-full bg-gray-100 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full transition-all ${pwStrength.color} ${pwStrength.w}`}/>
                        </div>
                        <p className="text-xs text-gray-400 mt-0.5">{pwStrength.label}</p>
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-sage-700 block mb-1">Confirm new password</label>
                    <input type="password" value={confirmPw} onChange={e=>setConfirmPw(e.target.value)}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-sage-500 focus:ring-2 focus:ring-sage-200"
                      placeholder="Re-enter new password"/>
                    {confirmPw && newPw !== confirmPw && <p className="text-xs text-red-500 mt-1">Passwords don't match</p>}
                  </div>
                </div>
                {pwMsg && <p className={`text-xs ${pwMsg.ok ? "text-green-600" : "text-red-500"}`}>{pwMsg.text}</p>}
                <button onClick={submitChangePassword} disabled={!canChangePw}
                  className="px-5 py-2.5 bg-sage-700 hover:bg-sage-800 text-white text-xs font-semibold rounded-xl transition-all disabled:opacity-40 shadow-sm">
                  {pwLoading ? "Updating…" : "Update password"}
                </button>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// RESET PASSWORD SCREEN
// ════════════════════════════════════════════════════════════════════════════
function ResetPasswordScreen({ token, onSuccess }) {
  const [password, setPassword]     = useState("");
  const [confirm, setConfirm]       = useState("");
  const [showPw, setShowPw]         = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [err, setErr]               = useState("");
  const [loading, setLoading]       = useState(false);
  const [done, setDone]             = useState(false);

  // Password strength helpers (mirrors server-side validatePassword)
  const pwStrength = (() => {
    if (!password) return null;
    const checks = [password.length >= 8, /[A-Z]/.test(password), /[0-9]/.test(password), /[^A-Za-z0-9]/.test(password)];
    const score = checks.filter(Boolean).length;
    if (score <= 1) return { label: "Too weak", color: "bg-red-400", width: "w-1/4" };
    if (score === 2) return { label: "Weak",     color: "bg-orange-400", width: "w-2/4" };
    if (score === 3) return { label: "Good",     color: "bg-yellow-400", width: "w-3/4" };
    return               { label: "Strong",    color: "bg-green-500", width: "w-full" };
  })();

  const canSubmit = password.length >= 8 && password === confirm && !loading;

  const submit = async () => {
    setErr(""); setLoading(true);
    const result = await api.resetPassword(token, password);
    setLoading(false);
    if (result.error) { setErr(result.error); return; }
    setDone(true);
    // Brief delay so user sees the success state before being logged in
    setTimeout(() => onSuccess(result.sessionId, result.user), 1200);
  };

  const EyeIcon = ({ open }) => open
    ? <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
    : <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>;

  return (
    <div className="min-h-screen bg-gradient-to-br from-sage-50 via-white to-sage-50 flex flex-col items-center justify-center p-4">
      <div className="flex items-center gap-2 mb-8">
        <img src="/logo.png" alt="Virtuosoh" className="w-9 h-9 rounded-xl shadow-sm object-cover"/>
        <span className="font-display text-2xl font-semibold tracking-tight text-sage-950">Virtuosoh</span>
      </div>
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 w-full max-w-md p-8">
        {done ? (
          <div className="text-center py-4">
            <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-green-100 text-green-600 flex items-center justify-center"><svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg></div>
            <h2 className="font-display text-xl font-semibold text-sage-950 mb-2">Password updated!</h2>
            <p className="text-sm text-gray-500">Logging you in…</p>
          </div>
        ) : (
          <>
            <h2 className="font-display text-xl font-semibold text-sage-950 mb-1">Set a new password</h2>
            <p className="text-sm text-gray-500 mb-6">Choose a strong password for your Virtuosoh account.</p>

            {/* New password */}
            <div className="mb-4">
              <label className="text-xs font-semibold text-sage-700 block mb-1">New password</label>
              <div className="relative">
                <input
                  type={showPw ? "text" : "password"}
                  autoFocus
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-sage-500 focus:ring-2 focus:ring-sage-200 pr-10"
                  placeholder="Min. 8 characters"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && canSubmit && submit()}
                />
                <button
                  type="button"
                  onClick={() => setShowPw(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  tabIndex={-1}
                >
                  <EyeIcon open={showPw} />
                </button>
              </div>
              {/* Strength bar */}
              {pwStrength && (
                <div className="mt-2">
                  <div className="h-1 w-full bg-gray-100 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full transition-all ${pwStrength.color} ${pwStrength.width}`}/>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">{pwStrength.label}</p>
                </div>
              )}
            </div>

            {/* Confirm password */}
            <div className="mb-5">
              <label className="text-xs font-semibold text-sage-700 block mb-1">Confirm password</label>
              <div className="relative">
                <input
                  type={showConfirm ? "text" : "password"}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-sage-500 focus:ring-2 focus:ring-sage-200 pr-10"
                  placeholder="Re-enter your password"
                  value={confirm}
                  onChange={e => setConfirm(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && canSubmit && submit()}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  tabIndex={-1}
                >
                  <EyeIcon open={showConfirm} />
                </button>
              </div>
              {confirm && password !== confirm && (
                <p className="text-xs text-red-500 mt-1">Passwords don't match</p>
              )}
            </div>

            {err && <div className="bg-red-50 border border-red-200 text-red-600 text-xs rounded-lg px-3 py-2 mb-4">{err}</div>}

            <button
              onClick={submit}
              disabled={!canSubmit}
              className="w-full bg-sage-700 hover:bg-sage-800 text-white font-semibold py-3 rounded-xl transition-all disabled:opacity-50 text-sm shadow-md"
            >
              {loading ? "Updating…" : "Update password →"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// LEGAL PAGES (Privacy / Terms) — markdown rendered from /public/legal
// ════════════════════════════════════════════════════════════════════════════
function renderInline(text) {
  return text.split(/\*\*(.+?)\*\*/g).map((p, i) =>
    i % 2 === 1 ? <strong key={i} className="font-semibold text-sage-950">{p}</strong> : p
  );
}

function MarkdownDoc({ text }) {
  const lines = text.replace(/\r\n/g, "\n").split("\n");
  const blocks = [];
  let i = 0, key = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) { i++; continue; }
    if (line.startsWith("### ")) { blocks.push(<h3 key={key++} className="font-display text-lg font-semibold text-sage-950 mt-7 mb-2">{renderInline(line.slice(4))}</h3>); i++; continue; }
    if (line.startsWith("## "))  { blocks.push(<h2 key={key++} className="font-display text-2xl font-semibold text-sage-950 mt-9 mb-3">{renderInline(line.slice(3))}</h2>); i++; continue; }
    if (line.startsWith("# "))   { blocks.push(<h1 key={key++} className="font-display text-3xl md:text-4xl font-semibold text-sage-950 mb-4">{renderInline(line.slice(2))}</h1>); i++; continue; }
    if (line.startsWith(">")) {
      const quote = [];
      while (i < lines.length && lines[i].startsWith(">")) { quote.push(lines[i].replace(/^>\s?/, "")); i++; }
      blocks.push(<blockquote key={key++} className="border-l-4 border-sage-300 bg-sage-50 text-sage-700 text-sm rounded-r-lg px-4 py-3 my-5 space-y-1">{quote.filter(q => q.trim()).map((q, qi) => <p key={qi}>{renderInline(q)}</p>)}</blockquote>);
      continue;
    }
    if (/^\s*-\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*-\s+/.test(lines[i])) {
        const indented = /^\s\s+-\s+/.test(lines[i]);
        items.push(<li key={items.length} className={indented ? "ml-5 list-[circle]" : ""}>{renderInline(lines[i].replace(/^\s*-\s+/, ""))}</li>);
        i++;
      }
      blocks.push(<ul key={key++} className="list-disc pl-5 space-y-1.5 text-sage-700 my-3">{items}</ul>);
      continue;
    }
    const para = [];
    while (i < lines.length && lines[i].trim() && !/^(#{1,3}\s|>|\s*-\s+)/.test(lines[i])) { para.push(lines[i]); i++; }
    blocks.push(<p key={key++} className="text-sage-700 leading-relaxed my-3">{renderInline(para.join(" "))}</p>);
  }
  return <>{blocks}</>;
}

function LegalPage({ doc, onHome }) {
  const file = doc === "terms" ? "terms-of-service" : "privacy-policy";
  const [text, setText] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    let active = true;
    setText(null); setError(false);
    fetch(`/legal/${file}.md`)
      .then(r => { if (!r.ok) throw new Error("not found"); return r.text(); })
      .then(t => { if (active) setText(t); })
      .catch(() => { if (active) setError(true); });
    return () => { active = false; };
  }, [file]);
  return (
    <div className="min-h-screen bg-[#edf7ef] text-sage-950 font-sans">
      <header className="sticky top-0 z-40 bg-[#edf7ef]/85 backdrop-blur-xl border-b border-sage-100">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 h-16 flex items-center justify-between">
          <button onClick={onHome} className="flex items-center gap-2.5">
            <img src="/logo.png" alt="Virtuosoh" className="w-9 h-9 rounded-xl shadow-sm object-cover"/>
            <span className="font-display text-xl font-semibold tracking-tight text-sage-950">Virtuosoh</span>
          </button>
          <button onClick={onHome} className="text-sm font-semibold text-sage-700 hover:text-sage-950 transition-colors">&larr; Back to site</button>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-6 sm:px-8 py-12 md:py-16">
        {error ? (
          <p className="text-sage-700">Sorry, we couldn't load this document. <button onClick={onHome} className="text-sage-800 underline">Go back</button>.</p>
        ) : text === null ? (
          <p className="text-sage-500">Loading&hellip;</p>
        ) : (
          <article className="bg-white border border-sage-100 rounded-2xl shadow-sm px-6 md:px-10 py-8 md:py-10">
            <MarkdownDoc text={text}/>
          </article>
        )}
      </main>
    </div>
  );
}

function LandingPage({ onStart, onLogin, onLegal }) {
  // ── A/B-swappable hero copy (positioning is still being tested) ──
  // Headline candidates: ["Leads ready","when you are"] · ["Your sales funnel,","managed"] · ["Automated sales","support, handled"]
  const HERO = {
    headline: ["Leads ready", "when you are"], // [0] roman, [1] italic accent
  };
  const scrollToTools = () => document.getElementById("landing-tools")?.scrollIntoView({ behavior: "smooth", block: "start" });
  const FeatureIcon = ({ children }) => (
    <span className="w-10 h-10 rounded-xl bg-sage-100 text-sage-700 flex items-center justify-center flex-shrink-0">
      {children}
    </span>
  );

  return (
    <div className="min-h-screen bg-[#edf7ef] text-gray-950 font-sans overflow-x-clip">
      <header className="sticky top-0 z-40 bg-[#edf7ef]/85 backdrop-blur-xl border-b border-sage-100">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Virtuosoh" className="w-9 h-9 rounded-xl shadow-sm object-cover"/>
            <span className="font-display text-xl font-semibold tracking-tight text-sage-950">Virtuosoh</span>
          </div>
          <nav className="hidden md:flex items-center gap-7 text-sm font-medium text-sage-700">
            <a href="#landing-tools" className="hover:text-sage-950 transition-colors">Outbound</a>
            <a href="#landing-inbound" className="hover:text-sage-950 transition-colors">Inbound</a>
            <a href="#landing-ready" className="hover:text-sage-950 transition-colors">What you get</a>
            <a href="#landing-pricing" className="hover:text-sage-950 transition-colors">Get started</a>
          </nav>
          <div className="flex items-center gap-2">
            <button onClick={onLogin} className="hidden sm:inline-flex text-sm font-semibold text-sage-700 hover:text-sage-950 px-3 py-2 transition-colors">Log in</button>
            <button onClick={onStart} className="bg-sage-700 hover:bg-sage-800 text-white text-sm font-semibold px-4 py-2 rounded-xl shadow-sm hover:shadow-md transition-all">Get started for free</button>
          </div>
        </div>
      </header>

      <main>
        <section className="relative overflow-hidden border-b border-sage-100">
          {/* Calm, single base color + one subtle accent — no stacked pastel gradients */}
          <div className="absolute inset-0 bg-[#edf7ef]"/>
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-sage-300/70 to-transparent"/>
          <div className="absolute -top-24 right-[-6rem] h-[460px] w-[460px] rounded-full bg-sage-200/30 blur-3xl"/>
          <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-10 pt-12 md:pt-16 pb-16 md:pb-24">
            <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-12 lg:gap-16 items-center">
              {/* ── Copy column ── */}
              <div>
                {/* Positioning eyebrow */}
                <div className="inline-flex items-center gap-2 rounded-full border border-sage-200 bg-white/70 px-3.5 py-1.5 mb-7">
                  <span className="w-1.5 h-1.5 rounded-full bg-sage-600"/>
                  <span className="text-sm font-medium text-sage-800">Simplify your sales funnel</span>
                </div>

                <h1 className="font-display text-[2.9rem] sm:text-6xl lg:text-[4.4rem] font-semibold tracking-[-0.02em] text-sage-950 leading-[0.98] mb-6">
                  {HERO.headline[0]}{" "}
                  <span className="italic font-normal text-sage-700">{HERO.headline[1]}</span>
                </h1>

                {/* Single subheader: assistance, not replacement */}
                <p className="text-lg md:text-xl text-sage-900/70 leading-relaxed max-w-xl mb-8">
                  We do the prospecting and the prep. <span className="font-semibold text-sage-900">You make the call, close the deal, and take the credit.</span>
                </p>

                <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-8">
                  <button onClick={onStart} className="group inline-flex items-center justify-center gap-2 bg-sage-700 hover:bg-sage-800 text-white font-semibold px-7 py-3.5 rounded-xl shadow-lg shadow-sage-900/15 hover:shadow-xl hover:-translate-y-0.5 transition-all">
                    Get started for free
                    <svg className="w-4 h-4 transition-transform group-hover:translate-x-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14m0 0l-6-6m6 6l-6 6"/></svg>
                  </button>
                  <button onClick={scrollToTools} className="group inline-flex items-center gap-1.5 text-sage-800 font-semibold px-2 py-2 hover:text-sage-950 transition-colors">
                    See how it works
                    <span className="text-sage-400 transition-transform group-hover:translate-x-0.5">&rarr;</span>
                  </button>
                </div>

                {/* Dual audience, plainly stated */}
                <div className="flex items-center gap-2 text-sm text-sage-800/80">
                  <svg className="w-4 h-4 text-sage-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.7}><path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-1a4 4 0 00-3-3.87M9 20H4v-1a4 4 0 013-3.87m6-1.13a4 4 0 10-4-4 4 4 0 004 4z"/></svg>
                  <span><span className="font-semibold text-sage-900">Works solo.</span> Scales to your team.</span>
                </div>
              </div>

              {/* ── Product fragment: two intakes converging into one board ── */}
              {/* Illustrative, not interactive — deliberately no hover lift/scale (it isn't a button) */}
              <div className="relative">
                <div className="rounded-2xl bg-white border border-sage-100 shadow-[0_24px_60px_-24px_rgba(29,62,58,0.4)]">
                  <div className="flex items-center justify-between px-5 py-3.5 border-b border-sage-100">
                    <div className="flex items-center gap-2">
                      <span className="w-6 h-6 rounded-md bg-sage-700 text-white flex items-center justify-center text-[11px] font-bold">V</span>
                      <span className="text-sm font-semibold text-sage-950">Your board</span>
                    </div>
                    <span className="text-xs font-medium text-sage-500">12 ready for follow-up</span>
                  </div>

                  {/* Two intakes */}
                  <div className="flex items-stretch gap-3 px-5 pt-4">
                    <div className="flex-1 rounded-lg border border-teal-200 bg-teal-50/70 px-3 py-2">
                      <div className="text-[10px] font-bold uppercase tracking-wide text-teal-700">Inbound</div>
                      <div className="text-xs text-sage-700 mt-0.5">Captured from your site</div>
                    </div>
                    <div className="flex-1 rounded-lg border border-emerald-200 bg-emerald-50/70 px-3 py-2">
                      <div className="text-[10px] font-bold uppercase tracking-wide text-emerald-700">Outbound</div>
                      <div className="text-xs text-sage-700 mt-0.5">Prospected for you</div>
                    </div>
                  </div>
                  <div className="flex justify-center py-2 text-sage-300">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 5v14m0 0l-5-5m5 5l5-5"/></svg>
                  </div>

                  {/* Converged rows */}
                  <div className="px-5 pb-5 space-y-2.5">
                    {[
                      ["Website visitor", "Asked about Saturday slots", "Inbound", "bg-teal-50 text-teal-700 border-teal-200"],
                      ["Local business owner", "Matches your ideal customer", "Outbound", "bg-emerald-50 text-emerald-700 border-emerald-200"],
                      ["Returning customer", "Needs pricing for two services", "Hot", "bg-amber-50 text-amber-700 border-amber-200"],
                    ].map(([name, detail, tag, tagcls]) => (
                      <div key={name} className="flex items-center justify-between gap-3 rounded-lg border border-sage-100 bg-white px-3.5 py-3">
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-sage-950 truncate">{name}</div>
                          <div className="text-xs text-sage-500 truncate">{detail}</div>
                        </div>
                        <span className={`text-[10px] font-semibold rounded-full border px-2 py-0.5 whitespace-nowrap ${tagcls}`}>{tag}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── OUTBOUND — primary value prop ── */}
        <section id="landing-tools" className="bg-white pt-16 md:pt-24 pb-16 md:pb-20">
          <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-10">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <span className="text-xs font-semibold uppercase tracking-[0.14em] text-emerald-700">Outbound prospecting</span>
                <h2 className="font-display text-3xl md:text-5xl font-semibold tracking-[-0.02em] text-sage-950 mt-3 mb-5 leading-[1.05]">Automated prospecting so you can focus on closing.</h2>
                <p className="text-lg text-sage-900/70 leading-relaxed mb-6 max-w-xl">Virtuosoh finds the right people and businesses, fills in their contact details, and drops a ready-to-work list on your board &mdash; so your hours go to conversations, not list-building.</p>
                <div className="space-y-3 mb-8">
                  {[
                    "Start from a plain-English description, not a blank spreadsheet",
                    "Get a focused list with names, roles, and contact details filled in",
                    "Everything lands on your board with status, notes, and next steps",
                  ].map((point) => (
                    <div key={point} className="flex items-start gap-2.5 text-sage-900/80">
                      <svg className="w-5 h-5 mt-0.5 flex-shrink-0 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>
                      <span>{point}</span>
                    </div>
                  ))}
                </div>
                <button onClick={onStart} className="group inline-flex items-center gap-1.5 text-emerald-700 font-semibold hover:text-emerald-900 transition-colors">
                  Start prospecting
                  <span className="transition-transform group-hover:translate-x-0.5">&rarr;</span>
                </button>
              </div>
              <div className="relative">
                <div className="rounded-2xl bg-white border border-sage-100 shadow-[0_24px_60px_-24px_rgba(29,62,58,0.4)] overflow-hidden">
                  <span className="block h-1 bg-emerald-400"/>
                  <div className="p-5 md:p-6">
                    <div className="text-[10px] font-bold uppercase tracking-wide text-emerald-700 mb-2">Describe your ideal customer</div>
                    <div className="rounded-xl border border-sage-200 bg-sage-50/60 px-4 py-3 text-sm text-sage-800 mb-4">"Owners of dental clinics in Seattle, 11&ndash;50 staff"</div>
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs font-semibold text-sage-500 uppercase tracking-wide">18 prospects found</span>
                      <span className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 rounded-full px-2 py-0.5">Contacts filled in</span>
                    </div>
                    <div className="space-y-2.5">
                      {[
                        ["Dr. Lena Ortiz", "Bright Smile Dental"],
                        ["Marcus Webb", "Cascade Family Dentistry"],
                        ["Priya Nair", "Emerald City Orthodontics"],
                      ].map(([name, company]) => (
                        <div key={name} className="flex items-center justify-between gap-3 rounded-lg border border-sage-100 bg-white px-3.5 py-2.5">
                          <div className="min-w-0">
                            <div className="text-sm font-semibold text-sage-950 truncate">{name}</div>
                            <div className="text-xs text-sage-500 truncate">{company}</div>
                          </div>
                          <span className="text-[10px] font-semibold text-emerald-700 border border-emerald-200 rounded-full px-2 py-0.5 whitespace-nowrap">Email ready</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── INBOUND / customer service — secondary ── */}
        <section id="landing-inbound" className="bg-sage-50 pt-16 md:pt-20 pb-16 md:pb-20">
          <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-10">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div className="order-2 lg:order-1">
                <div className="rounded-2xl bg-white border border-sage-100 shadow-[0_24px_60px_-24px_rgba(29,62,58,0.4)] overflow-hidden">
                  <span className="block h-1 bg-teal-400"/>
                  <div className="px-5 py-3.5 border-b border-sage-100 flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-teal-600 text-white flex items-center justify-center text-[11px] font-bold">S</span>
                    <span className="text-sm font-semibold text-sage-950">Sarah from Virtuosoh</span>
                    <span className="ml-auto inline-flex items-center gap-1 text-[11px] text-teal-700"><span className="w-1.5 h-1.5 rounded-full bg-teal-500"/>Online</span>
                  </div>
                  <div className="p-5 space-y-3">
                    <div className="max-w-[80%] rounded-2xl rounded-tl-sm bg-sage-100 text-sage-800 text-sm px-3.5 py-2.5">Do you have any Saturday appointments?</div>
                    <div className="max-w-[85%] ml-auto rounded-2xl rounded-tr-sm bg-teal-600 text-white text-sm px-3.5 py-2.5">We do! Can you send your email so the team can lock one in?</div>
                    <div className="max-w-[80%] rounded-2xl rounded-tl-sm bg-sage-100 text-sage-800 text-sm px-3.5 py-2.5">Sure &mdash; jane@brightsmile.com</div>
                    <div className="flex items-center gap-2 pt-1 text-xs font-medium text-teal-700">
                      <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>
                      Lead captured and saved to your board
                    </div>
                  </div>
                </div>
              </div>
              <div className="order-1 lg:order-2">
                <span className="text-xs font-semibold uppercase tracking-[0.14em] text-teal-700">Inbound &amp; customer service</span>
                <h2 className="font-display text-3xl md:text-5xl font-semibold tracking-[-0.02em] text-sage-950 mt-3 mb-5 leading-[1.05]">A chat assistant that answers visitors and captures leads.</h2>
                <p className="text-lg text-sage-900/70 leading-relaxed mb-6 max-w-xl">Add a chat assistant to your site that answers questions around the clock, helps existing customers, and collects details from anyone worth a follow-up. Every conversation is summarized and saved to your board.</p>
                <div className="space-y-3 mb-8">
                  {[
                    "Answers common questions so visitors aren't left waiting",
                    "Asks a few qualifying questions before a lead reaches you",
                    "Saves the full conversation so follow-up feels personal",
                  ].map((point) => (
                    <div key={point} className="flex items-start gap-2.5 text-sage-900/80">
                      <svg className="w-5 h-5 mt-0.5 flex-shrink-0 text-teal-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>
                      <span>{point}</span>
                    </div>
                  ))}
                </div>
                <button onClick={onStart} className="group inline-flex items-center gap-1.5 text-teal-700 font-semibold hover:text-teal-900 transition-colors">
                  Add it to your site
                  <span className="transition-transform group-hover:translate-x-0.5">&rarr;</span>
                </button>
              </div>
            </div>
          </div>
        </section>

        <section id="landing-ready" className="bg-white pt-14 md:pt-20 pb-16 md:pb-24">
          <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-10">
            <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-10 lg:gap-14 items-start">
              <div className="lg:sticky lg:top-24">
                <span className="text-xs font-semibold uppercase tracking-[0.14em] text-sage-600">What you'll come back to</span>
                <h2 className="font-display text-3xl md:text-5xl font-semibold tracking-[-0.02em] text-sage-950 mt-3 mb-5 leading-[1.05]">Not just names. A useful place to start.</h2>
                <p className="text-lg text-sage-900/70 leading-relaxed mb-7">Every lead tells you why the person matters, what happened before, and what comes next. Use the board on your own, or share the same view across your team.</p>
                <button onClick={onStart} className="group inline-flex items-center gap-2 bg-sage-700 hover:bg-sage-800 text-white font-semibold px-6 py-3.5 rounded-xl shadow-lg shadow-sage-900/15 hover:shadow-xl hover:-translate-y-0.5 transition-all">
                  Try Virtuosoh free
                  <svg className="w-4 h-4 transition-transform group-hover:translate-x-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14m0 0l-6-6m6 6l-6 6"/></svg>
                </button>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  ["Conversation context", "See what someone asked about before you call or email them.", <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.7}><path strokeLinecap="round" strokeLinejoin="round" d="M8 10h8M8 14h4m-6 6l-2 1V6a2 2 0 012-2h12a2 2 0 012 2v8a2 2 0 01-2 2H8l-2 2z"/></svg>],
                  ["Simple next steps", "Know who needs a reply, a quote, a call, or a little more time.", <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.7}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7l-2 2-1-1"/></svg>],
                  ["One organized board", "Inbound leads, outbound prospects, and team follow-up in one workspace.", <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.7}><path strokeLinecap="round" strokeLinejoin="round" d="M4 5a1 1 0 011-1h4a1 1 0 011 1v14a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v6a1 1 0 01-1 1h-4a1 1 0 01-1-1V5z"/></svg>],
                  ["Clear ownership", "Work solo, or make it obvious who's handling the next step.", <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.7}><path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/></svg>]
                ].map(([title, copy, icon]) => (
                  <div key={title} className="rounded-2xl bg-white border border-sage-100 p-5 shadow-sm">
                    <FeatureIcon>{icon}</FeatureIcon>
                    <h3 className="font-semibold text-sage-950 mt-4 mb-1.5">{title}</h3>
                    <p className="text-sm text-sage-900/60 leading-relaxed">{copy}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="landing-pricing" className="bg-sage-950 text-white py-16 md:py-20">
          <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-10 text-center">
            <h2 className="font-display text-3xl md:text-5xl font-semibold tracking-[-0.02em] mb-5 leading-[1.05]">Keep your funnel moving &mdash; without adding more busywork.</h2>
            <p className="text-sage-100/70 text-lg leading-relaxed max-w-2xl mx-auto mb-9">Start on your own or bring in your team. Inbound leads, outbound prospects, and the next follow-up stay together in one practical workspace.</p>
            <div className="flex flex-col sm:flex-row justify-center gap-3">
              <button onClick={onStart} className="inline-flex items-center justify-center gap-2 bg-white text-sage-950 hover:bg-sage-100 font-semibold px-7 py-3.5 rounded-xl transition-all">
                Get started for free
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14m0 0l-6-6m6 6l-6 6"/></svg>
              </button>
              <button onClick={onLogin} className="border border-white/25 hover:border-white/60 hover:bg-white/5 text-white font-semibold px-7 py-3.5 rounded-xl transition-all">Log in</button>
            </div>
          </div>
        </section>
      </main>

      <footer className="bg-sage-950 text-white border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-10 py-12 md:py-14">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-10">
            <div className="max-w-sm">
              <div className="flex items-center gap-2.5 mb-3">
                <img src="/logo.png" alt="Virtuosoh" className="w-8 h-8 rounded-lg object-cover"/>
                <span className="font-display text-lg font-semibold tracking-tight">Virtuosoh</span>
              </div>
              <p className="text-sm text-sage-100/60 leading-relaxed">Simplify your sales funnel so you can focus on closing.</p>
            </div>
            <div className="flex gap-12 sm:gap-16">
              <div className="flex flex-col gap-3 text-sm">
                <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-sage-100/40">Product</span>
                <a href="#landing-tools" className="text-sage-100/70 hover:text-white transition-colors">Outbound</a>
                <a href="#landing-inbound" className="text-sage-100/70 hover:text-white transition-colors">Inbound</a>
                <a href="#landing-ready" className="text-sage-100/70 hover:text-white transition-colors">What you get</a>
              </div>
              <div className="flex flex-col items-start gap-3 text-sm">
                <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-sage-100/40">Account</span>
                <button onClick={onStart} className="text-sage-100/70 hover:text-white transition-colors">Get started for free</button>
                <button onClick={onLogin} className="text-sage-100/70 hover:text-white transition-colors">Log in</button>
              </div>
            </div>
          </div>
          <div className="mt-10 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-sage-100/50">
            <span>&copy; {new Date().getFullYear()} Virtuosoh. All rights reserved.</span>
            <div className="flex items-center gap-5">
              <button onClick={() => onLegal("privacy")} className="hover:text-white transition-colors">Privacy</button>
              <button onClick={() => onLegal("terms")} className="hover:text-white transition-colors">Terms</button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// APP
// ════════════════════════════════════════════════════════════════════════════
export default function App() {
  const [sessionId, setSessionId] = useState(null);
  const [user, setUser]           = useState(null);
  const [leads, setLeads]         = useState([]);
  const [tab, setTab]             = useState(0);
  const [showBonus, setShowBonus] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [guestUsed, setGuestUsed] = useState(() => parseInt(localStorage.getItem("lw_guest_used")||"0",10));
  const [userInfo, setUserInfo] = useState(null); // {plan, remaining, ...} from /api/usage/check
  const [showPaywall, setShowPaywall] = useState(false);
  const [showBriefing, setShowBriefing] = useState(false);
  const [checkoutMsg, setCheckoutMsg] = useState(null);
  const [appLoading, setAppLoading] = useState(!!localStorage.getItem("lw_sid"));
  const [resetToken, setResetToken] = useState(() => new URLSearchParams(window.location.search).get("reset") || null);
  const [legalDoc, setLegalDoc] = useState(() => { const d = new URLSearchParams(window.location.search).get("legal"); return d === "privacy" || d === "terms" ? d : null; });
  useEffect(() => {
    const onPop = () => { const d = new URLSearchParams(window.location.search).get("legal"); setLegalDoc(d === "privacy" || d === "terms" ? d : null); };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  const [searchPhase, setSearchPhase] = useState("input"); // lifted so nav can show live indicator
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showDevModal, setShowDevModal]           = useState(false);
  const [showHelpModal, setShowHelpModal]         = useState(false);
  const [showBuyPacks, setShowBuyPacks]           = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  useEffect(() => {
    // Handle Stripe checkout redirect
    const params = new URLSearchParams(window.location.search);
    const checkoutStatus = params.get("checkout");
    const packSuccess = params.get("pack_success");
    if (packSuccess === "true") {
      const contacts = params.get("contacts") || "?";
      setCheckoutMsg({ type: "success", text: `${contacts} bonus contacts added to your account!` });
      window.history.replaceState({}, "", window.location.pathname);
    } else if (checkoutStatus === "success") {
      setCheckoutMsg({ type: "success", text: "Payment successful! Your plan is now active." });
      window.history.replaceState({}, "", window.location.pathname);
    } else if (checkoutStatus === "cancel") {
      setCheckoutMsg({ type: "cancel", text: "Checkout cancelled. You can try again anytime." });
      window.history.replaceState({}, "", window.location.pathname);
    }

    // Handle password reset link — keep the token in state, clear it from URL
    if (params.get("reset")) {
      window.history.replaceState({}, "", window.location.pathname);
    }

    const savedSid = localStorage.getItem("lw_sid");
    if (savedSid) {
      (async () => {
        try {
          const u = await api.getUser();
          if (u) {
            setSessionId(savedSid); setUser(u);
            const [ld, usage] = await Promise.all([api.getLeads(), api.checkUsage()]);
            setLeads(ld || []);
            setUserInfo(usage);
            // Chatbot-only users go straight to their leads tab
            if (usage?.stage === "chatbot") setTab(1);
            // Show daily briefing once per day
            const today = new Date().toISOString().split("T")[0];
            if (localStorage.getItem("lw_last_briefing") !== today) {
              localStorage.setItem("lw_last_briefing", today);
              setShowBriefing(true);
            }
          }
        } finally {
          setAppLoading(false);
        }
      })();
    }
  }, []);

  const refreshUsage = async () => {
    const u = await api.getUser();
    if (u) setUser(u);
    const usage = await api.checkUsage();
    setUserInfo(usage);
    const ld = await api.getLeads();
    setLeads(ld || []);
  };

  const handleAuth = async (sid, u, isSignup) => {
    localStorage.setItem("lw_sid", sid);
    setSessionId(sid); setUser(u);
    // Upload any guest leads to the new account
    if (leads.length > 0) {
      await api.addLeads(leads);
    }
    const ld = await api.getLeads();
    setLeads(ld || []);
    const usage = await api.checkUsage();
    setUserInfo(usage);
    if (usage?.stage === "chatbot") setTab(1);
    localStorage.removeItem("lw_guest_used");
    setGuestUsed(0);
    setShowAuthModal(false);
    if (isSignup) setShowBonus(true);
  };

  const handleLogout = async () => {
    await api.logout();
    localStorage.removeItem("lw_sid");
    setSessionId(null); setUser(null); setLeads([]); setGuestUsed(0); setUserInfo(null);
  };

  const handleBillingPortal = async () => {
    setShowUserMenu(false);
    const r = await api.billingPortal();
    if (r?.url) window.location.href = r.url;
    else alert("Billing portal unavailable. Please try again.");
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== "DELETE MY ACCOUNT") return;
    setDeleteLoading(true); setDeleteError(null);
    const r = await api.deleteAccount();
    if (r?.ok) {
      localStorage.removeItem("lw_sid");
      localStorage.removeItem("lw_last_search");
      localStorage.removeItem("lw_saved_searches");
      setSessionId(null); setUser(null); setLeads([]); setGuestUsed(0); setUserInfo(null);
      setShowDeleteModal(false);
    } else {
      setDeleteError(r?.error || "Something went wrong. Please try again.");
    }
    setDeleteLoading(false);
  };

  const handleGuestUse = (n) => {
    setGuestUsed(prev => { const next = prev + n; localStorage.setItem("lw_guest_used", next); return next; });
  };

  const addLead = async l => {
    if (user) { await api.addLeads([l]); const ld = await api.getLeads(); setLeads(ld || []); }
    else { setLeads(prev => [l, ...prev]); }
  };
  const addLeads = async ls => {
    if (user) { await api.addLeads(ls); const ld = await api.getLeads(); setLeads(ld || []); }
    else { setLeads(prev => [...ls, ...prev]); }
  };
  const updateLead = async (id, f, v) => {
    if (user) { await api.updateLead(id, f, v); const ld = await api.getLeads(); setLeads(ld || []); }
    else { setLeads(prev => prev.map(l => l.id===id ? {...l,[f]:v} : l)); }
  };

  const u   = user || {};
  const lim = userInfo?.remaining || 0;
  const guestLeft = Math.max(0, FREE_LIMIT - guestUsed);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const isChatbotPlan = userInfo?.stage === "chatbot";

  const isAdmin = u.plan === "admin";

  const NavIconProspector = () => <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="3"/><path strokeLinecap="round" d="M12 3v3M12 18v3M3 12h3M18 12h3"/></svg>;
  const NavIconLeads = () => <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path strokeLinecap="round" strokeLinejoin="round" d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg>;
  const NavIconAutopilot = () => <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><circle cx="12" cy="12" r="9"/><path strokeLinecap="round" strokeLinejoin="round" d="M12 7v5l3 3"/></svg>;
  const NavIconAdmin = () => <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><circle cx="12" cy="12" r="3"/><path strokeLinecap="round" strokeLinejoin="round" d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/></svg>;

  const NAV_ITEMS = isChatbotPlan
    ? [{ id: 1, icon: <NavIconLeads/>, label: "Leads", desc: `${leads.length} total`, badge: leads.length > 0 ? leads.length : null }]
    : [
        { id: 0, icon: <NavIconProspector/>, label: "Prospector", desc: "Find leads" },
        { id: 1, icon: <NavIconLeads/>,      label: "Leads",       desc: `${leads.length} total`, badge: leads.length > 0 ? leads.length : null },
        { id: 2, icon: <NavIconAutopilot/>,  label: "Autopilot",   desc: "Scheduled searches" },
        ...(isAdmin ? [{ id: 3, icon: <NavIconAdmin/>, label: "Admin", desc: "Manage clients" }] : []),
      ];

  // Keyboard shortcut: Cmd+K or Ctrl+K to focus search / switch to prospector
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") { e.preventDefault(); setTab(0); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  // ── Legal pages (Privacy / Terms) — accessible regardless of auth ──
  const showLegal = (d) => { window.history.pushState({}, "", `?legal=${d}`); setLegalDoc(d); window.scrollTo(0, 0); };
  const exitLegal = () => { window.history.pushState({}, "", window.location.pathname); setLegalDoc(null); };
  if (legalDoc) return <LegalPage doc={legalDoc} onHome={exitLegal}/>;

  // ── Password reset screen — shown when user clicks the link from the email ──
  if (resetToken) return (
    <ResetPasswordScreen
      token={resetToken}
      onSuccess={(sid, u) => {
        setResetToken(null);
        handleAuth(sid, u, false);
      }}
    />
  );

  // Show a minimal loader while restoring the session — prevents login/0-leads flicker
  if (appLoading) return (
    <div className="flex h-screen items-center justify-center bg-gray-50">
      <img src="/logo.png" alt="Virtuosoh" className="w-12 h-12 rounded-2xl shadow-md object-cover animate-pulse"/>
    </div>
  );

  if (!user) return (
    <>
      <LandingPage onStart={() => setShowAuthModal(true)} onLogin={() => setShowAuthModal(true)} onLegal={showLegal}/>
      {showAuthModal && (
        <div className="fixed inset-0 z-[70] bg-black/40 backdrop-blur-sm overflow-y-auto" onClick={() => setShowAuthModal(false)}>
          <div className="min-h-full flex items-start sm:items-center justify-center p-3 sm:p-4" onClick={e => e.stopPropagation()}>
            <AuthScreen onAuth={handleAuth} onDismiss={() => setShowAuthModal(false)}/>
          </div>
        </div>
      )}
      {checkoutMsg && (
        <div className={`fixed top-4 left-1/2 -translate-x-1/2 z-50 px-6 py-3 rounded-xl shadow-lg text-sm font-medium ${checkoutMsg.type==="success"?"bg-green-600 text-white":"bg-gray-700 text-white"}`}>
          {checkoutMsg.text}
          <button onClick={()=>setCheckoutMsg(null)} className="ml-4 opacity-70 hover:opacity-100">✕</button>
        </div>
      )}
    </>
  );

  return (
    <div className="flex h-screen bg-gray-50 font-sans">
      {/* ── Daily Briefing ── */}
      {showBriefing && user && leads.length > 0 && (
        <DailyBriefing leads={leads} user={user} onClose={()=>setShowBriefing(false)} onGoToLeads={()=>setTab(1)}/>
      )}

      {/* ── Delete Account Modal ── */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="bg-red-50 border-b border-red-100 px-6 py-5 flex items-start gap-3">
              <svg className="w-6 h-6 text-red-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/></svg>
              <div>
                <h2 className="text-base font-bold text-red-700">Delete your account</h2>
                <p className="text-xs text-red-500 mt-0.5">This cannot be undone.</p>
              </div>
            </div>
            <div className="px-6 py-5 space-y-4">
              <p className="text-sm text-gray-600 leading-relaxed">
                This will permanently delete <strong>all your leads, saved searches, scheduled searches, API keys,</strong> and your account. Your subscription will be cancelled immediately.
              </p>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-2">
                  Type <span className="text-red-600 font-bold">DELETE MY ACCOUNT</span> to confirm
                </label>
                <input
                  value={deleteConfirmText}
                  onChange={e => { setDeleteConfirmText(e.target.value); setDeleteError(null); }}
                  placeholder="DELETE MY ACCOUNT"
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm font-mono outline-none focus:border-red-400 focus:ring-2 focus:ring-red-100"
                  autoFocus
                />
              </div>
              {deleteError && <p className="text-xs text-red-500">{deleteError}</p>}
            </div>
            <div className="px-6 pb-6 flex gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 border border-gray-200 text-gray-600 py-2.5 rounded-xl text-sm font-semibold hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={deleteConfirmText !== "DELETE MY ACCOUNT" || deleteLoading}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2.5 rounded-xl text-sm font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {deleteLoading ? "Deleting…" : "Delete my account"}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ── Auth modal overlay ── */}
      {showAuthModal && (
        <div className="fixed inset-0 z-[70] bg-black/40 backdrop-blur-sm overflow-y-auto" onClick={() => setShowAuthModal(false)}>
          <div className="min-h-full flex items-start sm:items-center justify-center p-3 sm:p-4" onClick={e => e.stopPropagation()}>
            <AuthScreen onAuth={handleAuth} onDismiss={() => setShowAuthModal(false)}/>
          </div>
        </div>
      )}

      {/* ── Settings modal ── */}
      {showSettingsModal && user && (
        <SettingsModal
          user={user}
          userInfo={userInfo}
          onClose={() => setShowSettingsModal(false)}
          onLogout={handleLogout}
          onDeleteAccount={() => { setDeleteConfirmText(""); setDeleteError(null); setShowDeleteModal(true); }}
          onUpdateUser={u => setUser(u)}
          onShowPaywall={() => setShowPaywall(true)}
          onBillingPortal={handleBillingPortal}
        />
      )}

      {/* ── Developer modal ── */}
      {showDevModal && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={e=>e.target===e.currentTarget&&setShowDevModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="font-display text-sm font-semibold text-sage-950">Developer</h2>
              <button onClick={() => setShowDevModal(false)} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors text-lg leading-none">✕</button>
            </div>
            <div className="flex-1 overflow-y-auto">
              <DeveloperPanel user={user} onNeedsAuth={() => { setShowDevModal(false); setShowAuthModal(true); }}/>
            </div>
          </div>
        </div>
      )}

      {/* ── Help modal ── */}
      {showHelpModal && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={e=>e.target===e.currentTarget&&setShowHelpModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="font-display text-sm font-semibold text-sage-950">Help Center</h2>
              <button onClick={() => setShowHelpModal(false)} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors text-lg leading-none">✕</button>
            </div>
            <div className="flex-1 overflow-y-auto">
              <HelpCenter/>
            </div>
          </div>
        </div>
      )}

      {showBonus && user && <BonusToast name={user.name} onDone={()=>setShowBonus(false)}/>}
      {showPaywall && user && <Paywall userEmail={user.email} onPaid={()=>{setShowPaywall(false);refreshUsage();}} onBuyPacks={()=>{setShowPaywall(false);setShowBuyPacks(true);}} onClose={()=>setShowPaywall(false)}/>}
      {showBuyPacks && <BuyContactsModal onClose={()=>{setShowBuyPacks(false);refreshUsage();}}/>}

      {/* ── Checkout result banner ── */}
      {checkoutMsg && (
        <div className={`fixed top-4 left-1/2 -translate-x-1/2 z-50 px-6 py-3 rounded-xl shadow-lg text-sm font-medium ${checkoutMsg.type==="success"?"bg-green-600 text-white":"bg-gray-700 text-white"}`}>
          {checkoutMsg.text}
          <button onClick={()=>setCheckoutMsg(null)} className="ml-4 opacity-70 hover:opacity-100">✕</button>
        </div>
      )}

      {/* ═══════════ MOBILE NAV BUTTON (floating corner) ═══════════ */}
      {/* Small floating button — content takes the full screen, nav is an overlay */}
      <button
        onClick={() => setSidebarOpen(p => !p)}
        className="md:hidden fixed top-3 left-3 z-50 w-9 h-9 flex items-center justify-center rounded-xl bg-white shadow-md border border-sage-200 hover:bg-sage-50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-sage-300"
        aria-label="Open menu"
      >
        {sidebarOpen
          ? <svg className="w-4 h-4 text-sage-700" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12"/></svg>
          : <svg className="w-4 h-4 text-sage-700" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 6h16M4 12h16M4 18h16"/></svg>
        }
      </button>

      {/* ═══════════ SIDEBAR OVERLAY (mobile) ═══════════ */}
      {sidebarOpen && <div className="fixed inset-0 bg-black/40 z-40 md:hidden" onClick={() => setSidebarOpen(false)}/>}

      {/* ═══════════ LEFT SIDEBAR ═══════════ */}
      {/* Mobile: fixed overlay drawer. Desktop: permanent sidebar in layout flow. */}
      <div className={`${sidebarOpen ? "translate-x-0" : "-translate-x-full"} md:translate-x-0 fixed md:relative z-50 md:z-auto w-64 md:w-56 bg-white border-r border-sage-100 flex flex-col flex-shrink-0 h-full transition-transform duration-200 ease-in-out`}>
        {/* Logo */}
        <div className="px-4 py-4 border-b border-sage-100 bg-sage-50/60">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Virtuosoh" className="w-9 h-9 rounded-xl shadow-sm object-cover"/>
            <div>
              <div className="font-display font-semibold tracking-tight text-sage-950">Virtuosoh</div>
              <div className="text-[11px] text-sage-500">Prospecting, enrichment, and inbound</div>
            </div>
          </div>
        </div>

        {/* Nav items */}
        <nav className="flex-1 px-3 py-3 space-y-1">
          {NAV_ITEMS.map(item => (
            <button key={item.id} onClick={() => {setTab(item.id); setShowUserMenu(false); setSidebarOpen(false);}}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all group focus:outline-none focus-visible:ring-2 focus-visible:ring-sage-300 ${
                tab === item.id
                  ? "bg-sage-700 text-white shadow-sm border border-sage-700"
                  : "text-sage-700 hover:bg-sage-50 hover:text-sage-900 border border-transparent"
              }`}>
              <span className="flex-shrink-0 flex items-center">{item.icon}</span>
              <div className="flex-1 min-w-0">
                <div className={`text-sm font-semibold leading-tight ${tab === item.id ? "text-white" : ""}`}>{item.label}</div>
                <div className={`text-xs leading-tight mt-0.5 ${tab === item.id ? "text-white/70" : "text-sage-500"}`}>{item.desc}</div>
              </div>
              {item.id === 0 && (searchPhase === "targeting" || searchPhase === "enriching")
                ? <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse flex-shrink-0"/>
                : item.badge
                  ? <span className={`text-[10px] font-bold min-w-[20px] h-5 rounded-full px-1 flex items-center justify-center flex-shrink-0 ${tab === item.id ? "bg-white text-sage-800" : "bg-sage-700 text-white"}`}>{item.badge > 99 ? "99+" : item.badge}</span>
                  : item.id === 0
                    ? <kbd className="hidden md:block text-[10px] text-sage-500 bg-sage-50 border border-sage-200 rounded px-1 py-0.5 font-mono flex-shrink-0">⌘K</kbd>
                    : null
              }
            </button>
          ))}
        </nav>

        {/* Sidebar footer — usage + account */}
        <div className="border-t border-sage-100 p-3 space-y-3">
          {/* Usage meter */}
          {!user && (
            <div className="px-1">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs text-gray-500 font-medium">Free credits</span>
                <span className="text-xs font-semibold text-gray-700">{guestLeft}/{FREE_LIMIT}</span>
              </div>
              <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full bg-sage-500 rounded-full transition-all" style={{width:`${Math.min(100,(guestUsed/FREE_LIMIT)*100)}%`}}/>
              </div>
            </div>
          )}
          {user && u.plan !== "admin" && (
            <div className="px-1 space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="text-xs text-sage-600 font-medium">Contacts this month</span>
                <span className="text-xs font-semibold text-sage-800">
                  {u.plan === "paid"
                    ? `${userInfo?.contactsUsed ?? 0} / ${userInfo?.monthlyLimit ?? "∞"}`
                    : `${lim === Infinity ? "∞" : lim} left`}
                </span>
              </div>
              {u.plan === "paid" && userInfo?.monthlyLimit > 0 && (
                <div className="w-full h-1.5 bg-sage-100 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full transition-all ${((userInfo?.contactsUsed??0)/userInfo.monthlyLimit) > 0.8 ? "bg-orange-400" : "bg-sage-500"}`}
                    style={{width:`${Math.min(100,((userInfo?.contactsUsed??0)/userInfo.monthlyLimit)*100)}%`}}/>
                </div>
              )}
              {(userInfo?.bonusContacts > 0) && (
                <div className="flex items-center justify-between text-xs">
                  <span className="text-emerald-600 font-medium">+ {userInfo.bonusContacts} bonus contacts</span>
                  <span className="text-gray-400">never expire</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                {u.plan === "paid" && userInfo?.plan !== "scale" && (
                  <button onClick={() => setShowPaywall(true)} className="flex-1 text-xs text-sage-600 hover:text-sage-700 font-semibold text-left transition-colors">
                    Upgrade plan →
                  </button>
                )}
                {u.plan !== "paid" && (
                  <button onClick={() => setShowPaywall(true)} className="flex-1 text-xs text-sage-600 hover:text-sage-700 font-semibold text-left transition-colors">
                    Upgrade →
                  </button>
                )}
                <button onClick={() => setShowBuyPacks(true)} className="text-xs text-sage-500 hover:text-sage-700 font-medium transition-colors">
                  Buy more
                </button>
              </div>
            </div>
          )}

          {/* Account button */}
          {!user ? (
            <button onClick={() => { setShowAuthModal(true); setSidebarOpen(false); }} className="w-full bg-sage-700 hover:bg-sage-800 text-white text-sm font-semibold py-2.5 rounded-lg transition-colors">
              Log in / Sign up
            </button>
          ) : (
            <div className="relative">
              <button onClick={() => setShowUserMenu(p => !p)} className="w-full flex items-center gap-2.5 px-2 py-2 rounded-lg hover:bg-sage-50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-sage-300">
                <div className="w-7 h-7 rounded-full bg-sage-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">{user.name?.[0]?.toUpperCase() || "U"}</div>
                <div className="flex-1 min-w-0 text-left">
                  <div className="text-xs font-semibold text-sage-900 truncate">{user.name}</div>
                  <div className="text-xs text-sage-500 truncate">{u.plan === "admin" ? "Admin" : u.plan === "paid" ? "Pro" : "Free plan"}</div>
                </div>
                <svg className="w-3.5 h-3.5 text-sage-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l4-4 4 4m0 6l-4 4-4-4"/></svg>
              </button>
              {showUserMenu && (
                <div className="absolute bottom-full left-0 right-0 mb-1 bg-white border border-sage-100 rounded-xl shadow-xl z-50 py-1 overflow-hidden">
                  <div className="px-3 py-2 border-b border-sage-100">
                    <div className="text-xs font-semibold text-sage-800 truncate">{user.name}</div>
                    <div className="text-xs text-sage-500 truncate">{user.email}</div>
                  </div>
                  <button onClick={() => { setShowUserMenu(false); setShowSettingsModal(true); }} className="w-full text-left px-3 py-2 text-xs text-sage-700 hover:bg-sage-50 transition-colors">
                    Settings
                  </button>
                  <button onClick={() => { setShowUserMenu(false); setShowDevModal(true); }} className="w-full text-left px-3 py-2 text-xs text-sage-700 hover:bg-sage-50 transition-colors">
                    Developer
                  </button>
                  <button onClick={() => { setShowUserMenu(false); setShowHelpModal(true); }} className="w-full text-left px-3 py-2 text-xs text-sage-700 hover:bg-sage-50 transition-colors">
                    Help
                  </button>
                  {u.plan === "paid" && (
                    <div className="border-t border-sage-100 mt-1">
                      <button onClick={() => { setShowUserMenu(false); handleBillingPortal(); }} className="w-full text-left px-3 py-2 text-xs text-sage-700 hover:bg-sage-50 transition-colors">
                        Billing
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ═══════════ MAIN CONTENT ═══════════ */}
      {/* All three panels stay mounted so in-progress searches survive tab switches */}
      <div className="flex-1 flex flex-col overflow-hidden" onClick={() => setShowUserMenu(false)}>
        <div className={tab === 0 ? "flex-1 flex flex-col overflow-hidden" : "hidden"}>
          <OutboundSDR addLeads={addLeads} addLead={addLead} user={user} userInfo={userInfo} guestUsed={guestUsed} onGuestUse={handleGuestUse} onGuestHitLimit={() => setShowAuthModal(true)} onUsageChange={refreshUsage} onSwitchToLeads={() => setTab(1)} onSchedule={()=>setTab(2)} onPhaseChange={setSearchPhase} onShowBuyPacks={() => setShowBuyPacks(true)}/>
        </div>
        <div className={tab === 1 ? "flex-1 flex flex-col overflow-hidden" : "hidden"}>
          <LeadBoard leads={leads} onUpdate={updateLead} userInfo={userInfo} onShowPaywall={()=>user?setShowPaywall(true):setShowAuthModal(true)}
            onBulkUpdate={async (ids,field,value)=>{await api.bulkUpdateLeads(ids,field,value);setLeads(prev=>prev.map(l=>ids.includes(l.id)?{...l,[field]:value}:l));}}
            onDelete={async (ids)=>{await api.bulkDeleteLeads(ids);setLeads(prev=>prev.filter(l=>!ids.includes(l.id)));}}
            onReEnrich={(id,data)=>{setLeads(prev=>prev.map(l=>l.id===id?{...l,email:data.email||l.email,phone:data.phone||l.phone,linkedinUrl:data.linkedinUrl||l.linkedinUrl,confidenceEmail:data.confidenceEmail||l.confidenceEmail,confidencePhone:data.confidencePhone||l.confidencePhone,confidenceLinkedin:data.confidenceLinkedin||l.confidenceLinkedin,dataSources:data.dataSources||l.dataSources,enrichedAt:data.enrichedAt||l.enrichedAt,score:data.score||l.score}:l));}}
            onDone={async()=>{ const ld = await api.getLeads(); setLeads(ld||[]); }}
            onOpenProspector={()=>setTab(0)}
          />
        </div>
        <div className={tab === 2 ? "flex-1 flex flex-col overflow-hidden" : "hidden"}>
          <ScheduleManager user={user} userInfo={userInfo} onAddLeads={addLeads} onSwitchToLeads={()=>setTab(1)} onNeedsAuth={()=>setShowAuthModal(true)}/>
        </div>
        {isAdmin && (
          <div className={tab === 3 ? "flex-1 flex flex-col overflow-hidden" : "hidden"}>
            <AdminPanel user={user}/>
          </div>
        )}
      </div>

      {/* ── Floating inbound chat bubble (always visible) ── */}
      <InboundChatBubble addLead={addLead} companyContext=""/>
    </div>
  );
}
